import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { PlayerSpawner } from './game/PlayerSpawner';
import { LootSpawner } from './game/LootSpawner';
import { GameManager } from './GameManager';
import { useToast } from './contexts/ToastContext';
import { PreloadScene } from './scenes/PreloadScene';
import { InstructionsScene } from './scenes/InstructionsScene';
import { JoinScene } from './scenes/JoinScene';
import { PreBattleScene } from './scenes/PreBattleScene';
import { VictoryScene } from './scenes/VictoryScene';
import { FirstTimeScene } from './scenes/FirstTimeScene';
import { WrapUpScene } from './scenes/WrapUpScene';
import { RewardsScene } from './scenes/RewardsScene';
import { BattlefieldLootScene } from './scenes/BattlefieldLootScene';
import { ItemSpotlightScene } from './scenes/ItemSpotlightScene';
import { PredictionResultsScene } from './scenes/PredictionResultsScene';
import BattleStateManager from './game/BattleStateManager.js';
import CameraController from './camera/CameraController';
import CloudManager from './camera/CloudManager';
import FogManager from './camera/FogManager';
import { loadBattlefieldsData, generateCampyMatchTitle, loadIntermissionsData, loadItemsData, buildItemsById, loadMusicManifest, loadSfxManifest, loadWeaponsManifest, loadFoodData, loadSpellsSoundsManifest, loadShieldsData, setSelectedThemeId, loadTheme, clearThemeCache, getCurrentTheme, showMarqueeMessage } from './Utils';
import { IntermissionManager } from './intermission/IntermissionManager';
import { LeaderboardOverlay } from './intermission/LeaderboardOverlay';
import { resolveEmoteToken } from './chat/Emotes';
import { handleChatMessage } from './chat/ChatCommands';
import { CommandDurations } from './chat/CommandConfig';
import './chat/GlobalChatHandler.js'; // Initialize global chat handler
import { SfxManager } from './audio/SfxManager';
import { SpellManager } from './spells/SpellManager';
import { ArrowManager } from './combat/ArrowManager';
import { GameModeManager } from './modes/GameModeManager.js';
import { useEntitlements } from './EntitlementsContext';
import { useJWTAuth } from './auth/JWTAuthContext';
import VoteManager from './services/VoteManager.js';
import OperatorChatbox from './components/OperatorChatbox';
import { socketService } from './services/SocketService';
import sessionStateManager from './game/SessionStateManager';
// Account linking moved to home page


//const REACT_APP_SOULWARS_API_KEY = 'soulwarz';
const prizeAmount = 0.0001;
const doPrizes = false;

// Default number of holders to fetch
let holdersToFetch = 350;

// Debug flag for chat/AI logs (enable with ?debugChat=1 or REACT_APP_DEBUG_CHAT=true)
let DEBUG_CHAT = true;
try {
  const qs = new URLSearchParams(window.location.search);
  DEBUG_CHAT = (process.env.REACT_APP_DEBUG_CHAT === 'true') || (qs.get('debugChat') === '1') || (localStorage.getItem('debugChat') === '1');
} catch (_) {}

const chatDbg = (...args) => { try { if (DEBUG_CHAT) console.log(...args); } catch (_) {} };

// Persist last played battle music across scene restarts
let lastBattleMusicKeyGlobal = null;

const SIDEBAR_MANA_COLOR = 0x60A5FA;      // bright blue
const SIDEBAR_STAMINA_COLOR = 0xF97316;   // vivid orange
const SIDEBAR_ULTIMATE_COLOR = 0x9D4EDD;
const SIDEBAR_ULTIMATE_FULL_COLOR = 0xFFD700;
const SIDEBAR_BAR_BG_COLOR = 0x333333;
const MAX_SIDEBAR_PLAYERS = 12; // Maximum number of players to display in the sidebar
// Session-persistent audio preferences (survive scene restarts)
// Stored in-module for simplicity; can be extended to localStorage if needed
const audioPrefs = {
  musicUserVol: null,   // last non-zero music volume user set
  musicMuted: null,     // whether user muted music
  sfxUserVol: null,     // last SFX base volume user set
  sfxMuted: null        // whether user muted SFX
};

// Persist auto-join across scene restarts: wallet -> remaining matches to auto-join
const autoJoinRemaining = new Map();

// Parse URL parameters for holder count
const urlParams = new URLSearchParams(window.location.search);
const holdersParam = urlParams.get('holders');
console.log("holders to fetch: ", holdersParam);
if (holdersParam && !isNaN(parseInt(holdersParam))) {
  const parsed = parseInt(holdersParam);
  // Limit to reasonable range (10-200)
  holdersToFetch = Math.min(1000, parsed);
  console.log("final holders to fetch: ", holdersToFetch);
}

// Helper function to truncate wallet addresses
function truncateWallet(walletAddress) {
  if (!walletAddress || walletAddress.length < 7) {
    return walletAddress || 'Unknown';
  }
  const firstThree = walletAddress.substring(0, 3);
  const lastFour = walletAddress.substring(walletAddress.length - 4);
  return `${firstThree}...${lastFour}`;
}

// Fallback display name when no chat username is present

// Helper to truncate long usernames (12+ chars) with ellipsis
function truncateUsername(name, max = 20) {
  try {
    if (!name || typeof name !== 'string') return name || '';
    return (name.length >= max) ? (name.slice(0, max) + '...') : name;
  } catch (_) { return name; }
}

class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.topHolders = [];
    this.holdersOverlayTimer = 0;
    this.holdersDataLoaded = false;
    this.victoryDelay = 3000;
    this.lootDelay = 10000; // Default loot delay (can be overridden by hero lootDelay property)

    // Army loading/UI state (managed in Phaser)
    this.armyLoading = false;
    this.nftLoadingProgress = 0;
    this.armyFetchUI = null; // { barFill, loadingText, barX, barY, barW, barH }
    this._armyPanelCoords = null; // { overlayX, overlayY, overlayWidth }

    // Battle music state
    this.battleMusic = null;
    this.battleMusicKeys = [
      'music/battle/PixelatedShowdown1.mp3'
    ];
    this.battleMusicTitleText = null;
    this.battleMusicQueue = [];
    this.lastBattleMusicKey = null;
    // Seed music prefs from session-persistent store
    if (typeof audioPrefs.musicUserVol === 'number') this.battleMusicUserVolume = audioPrefs.musicUserVol;
    this._initialMusicMuted = !!audioPrefs.musicMuted;

    // Next battlefield preload state
    this.nextBattlefield = null;
    this.nextBattlefieldReady = false;
    this.boundaryInitialized = false;
    this.extraLootTriggered = false;

    // Action focus state (for command-driven camera zoom)
    this.actionFocusActive = false;
    this.actionFocusPlayer = null;
    this.actionFocusEndAt = 0;
    this.actionFocusReleaseTimer = null;
    this.actionFocusReturnZoom = null;

    // Texture cache tracking
    this.loadedBattlefieldKeys = new Set();
    this.currentBattlefieldKey = null;

    // Gameplay tuning
    this.baseMeleeRange = 63; // unscaled melee distance baseline (roughly sprite overlap)
    this.baseRangedRange = 420;
    this.meleeRange = this.baseMeleeRange; // will be scaled by objectScale in startGame()
    this.rangedRange = this.baseRangedRange;

    // Seed SFX prefs from session-persistent store
    if (typeof audioPrefs.sfxUserVol === 'number') this.sfxManagerUserBaseVolume = audioPrefs.sfxUserVol;
    if (typeof audioPrefs.sfxMuted === 'boolean') this.sfxMuted = audioPrefs.sfxMuted;

    // Audience chat overlay (disabled by default)
    this.showAudienceChatOverlay = false;
    this.chatLog = [];
    this.maxChatLines = 50;
    this.chatText = null;
    this.chatBg = null;
    this._onChatEvent = (ev) => this.onExternalChat(ev);

    // Lobby joiners collected from chat (wallet -> username)
    this.lobbyJoiners = new Map();
    this.joinersText = null;
    this.joinGridMap = new Map(); // wallet -> { x, y, lastEmoteAt }

    // Wallet → preferred display name from chat (nicknames)
    this.walletDisplayNames = new Map();
    // Global emote rate limiting times
    this._emoteGlobalTimes = [];

    // Apply pending auto-join counters this session
    this.pendingAutoJoinApplied = false;

    // Game mode system
    this.gameModeManager = new GameModeManager();
    this.selectedGameMode = null; // Will be set from config

    // Voting gate state
    this.votingOpen = false;
    this.votingClosed = false;
    this.finalTenVoteStarted = false;
    this._lastSidebarAliveCount = null;

    // Chat "show" command focus tracking
    this._showCommandFocusPlayer = null;
    this._showCommandFocusExpiresAt = 0;
    this._showCommandFocusTimer = null;

    this.sessionConfig = {
      initialTokenDropCount: 50,
      lateGameLootCount: 20,
      lateGameTokenCount: 60,
      joinedDeathDropRate: 0.25
    };

    this.handleServerSessionConfig = (ev) => {
      try {
        const detail = ev.detail || {};
        this.updateSessionConfig(detail);
      } catch (error) {
        console.warn('[GameScene] Failed to handle session config event', error);
      }
    };
  }

  updateSessionConfig(config) {
    if (!config || typeof config !== 'object') return;
    this.sessionConfig = {
      initialTokenDropCount: Number.isFinite(config.initialTokenDropCount) ? config.initialTokenDropCount : this.sessionConfig.initialTokenDropCount,
      lateGameLootCount: Number.isFinite(config.lateGameLootCount) ? config.lateGameLootCount : this.sessionConfig.lateGameLootCount,
      lateGameTokenCount: Number.isFinite(config.lateGameTokenCount) ? config.lateGameTokenCount : this.sessionConfig.lateGameTokenCount,
      joinedDeathDropRate: Number.isFinite(config.joinedDeathDropRate) ? config.joinedDeathDropRate : this.sessionConfig.joinedDeathDropRate
    };
    console.log('[GameScene] Session config updated:', this.sessionConfig);
  }

  getSidebarContainerOrder() {
    if (!this._sidebarContainerOrder) {
      this._sidebarContainerOrder = [
        'arrow_container',
        'battle_container',
        'healing_container',
        'magic_container',
      ];
    }
    return this._sidebarContainerOrder;
  }

  resolveSidebarItemDefinition(itemId) {
    if (!itemId || !this.itemsById) return null;
    const key = String(itemId);
    if (typeof this.itemsById.get === 'function') {
      const direct = this.itemsById.get(key);
      if (direct) return direct;
      const numericKey = Number(key);
      if (!Number.isNaN(numericKey)) {
        const numericDef = this.itemsById.get(numericKey);
        if (numericDef) return numericDef;
      }
      return null;
    }
    return this.itemsById[key] ?? null;
  }

  parseSidebarEffect(effect) {
    if (!effect) return null;
    if (typeof effect === 'object') return effect;
    if (typeof effect === 'string') {
      try {
        return JSON.parse(effect);
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  inferSidebarContainer(item) {
    if (!item) return null;
    const explicitSlot = item.equipmentSlot ?? item.equipment_slot ?? null;
    if (explicitSlot && typeof explicitSlot === 'string' && explicitSlot.toLowerCase().includes('container')) {
      return explicitSlot;
    }

    const rawId = item.itemId ?? item.item_id ?? item.themeItemId ?? item.theme_item_id ?? null;
    const definition = this.resolveSidebarItemDefinition(rawId);

    const type = (
      item.itemType ??
      item.item_type ??
      item.type ??
      definition?.type ??
      ''
    ).toString().toLowerCase();
    const effect = this.parseSidebarEffect(item.effect ?? item.effect_json ?? definition?.effect ?? null);

    const matchesHealing =
      type.includes('heal') ||
      type.includes('health') ||
      (effect && (effect.heal || effect.healing || effect.restoreHp || effect.health));
    if (matchesHealing) return 'healing_container';

    const matchesMagic =
      type.includes('mana') ||
      type.includes('magic') ||
      type.includes('focus') ||
      (effect && (effect.mana || effect.restoreMana || effect.energy || effect.magic));
    if (matchesMagic) return 'magic_container';

    const matchesArrow =
      type.includes('ammo') ||
      type.includes('arrow') ||
      (effect && effect.arrows);
    if (matchesArrow) return 'arrow_container';

    const matchesBattle =
      type.includes('strength') ||
      type.includes('battle') ||
      type.includes('rage') ||
      (effect &&
        (effect.attackBoost ||
          effect.damageBoost ||
          effect.strength ||
          effect.strengthBoost ||
          effect.battle));
    if (matchesBattle) return 'battle_container';

    return explicitSlot ?? null;
  }

  resolveSidebarIconKey(item) {
    if (!item) return null;
    const direct =
      item.iconKey ??
      item.icon_key ??
      item.icon ??
      null;
    if (direct) return direct;
    const rawId = item.itemId ?? item.item_id ?? item.themeItemId ?? item.theme_item_id ?? null;
    const definition = this.resolveSidebarItemDefinition(rawId);
    return definition?.iconKey ?? definition?.icon_key ?? null;
  }

  extractSidebarQuantity(item) {
    if (!item) return 0;
    const value =
      item.quantity ??
      item.qty ??
      item.amount ??
      item.lootQty ??
      item.lootqty ??
      null;
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return Math.floor(num);
    return 1;
  }

  buildSidebarIconData(player) {
    const containerOrder = this.getSidebarContainerOrder();
    const inventorySource =
      (player && player.hero && Array.isArray(player.hero.inventory) && player.hero.inventory) ||
      (player && Array.isArray(player.inventory) && player.inventory) ||
      [];
    const lootSource = Array.isArray(player?.loot) ? player.loot : [];

    // Combine inventory + loot by container and itemId
    // Map: containerKey -> Map<itemId, { totalQty, iconKey, itemType }>
    const containerItems = new Map();

    // Process inventory items
    inventorySource.forEach((item) => {
      if (!item) return;
      const inferredSlot = this.inferSidebarContainer(item);
      const slot =
        inferredSlot ??
        item.equipmentSlot ??
        item.equipment_slot ??
        'general';
      
      // Only process container items (not equipment)
      if (!containerOrder.includes(slot)) return;
      
      const itemId = item.itemId || item.item_id || null;
      if (!itemId) return;
      
      const qty = this.extractSidebarQuantity(item);
      if (qty <= 0) return; // Skip zero quantity items
      
      const key = `${slot}:${itemId}`;
      if (!containerItems.has(key)) {
        containerItems.set(key, {
          slot,
          itemId,
          totalQty: 0,
          iconKey: this.resolveSidebarIconKey(item),
          itemType: item.itemType || item.item_type || null
        });
      }
      const entry = containerItems.get(key);
      entry.totalQty += qty;
    });

    // Process loot items and combine with inventory
    lootSource.forEach((lootItem) => {
      if (!lootItem) return;
      const lootType = (lootItem.lootType ?? lootItem.loot_type ?? 'item').toLowerCase();
      if (lootType !== 'item') return; // Skip tokens, etc.
      
      const inferredSlot = this.inferSidebarContainer(lootItem);
      const slot =
        inferredSlot ??
        'general';
      
      // Only process container items (not equipment)
      if (!containerOrder.includes(slot)) return;
      
      const itemId = lootItem.itemId || null;
      if (!itemId) return;
      
      const qty = this.extractSidebarQuantity(lootItem);
      if (qty <= 0) return; // Skip zero quantity items
      
      const key = `${slot}:${itemId}`;
      if (!containerItems.has(key)) {
        containerItems.set(key, {
          slot,
          itemId,
          totalQty: 0,
          iconKey: this.resolveSidebarIconKey(lootItem),
          itemType: lootItem.itemType || null
        });
      }
      const entry = containerItems.get(key);
      entry.totalQty += qty;
    });

    // Convert to slot entries, aggregating by container (one entry per container)
    // Group by container and find the item with highest quantity for icon
    const containerGroups = new Map(); // slot -> { items: [], totalQty: 0 }
    
    containerItems.forEach((entry) => {
      if (entry.totalQty <= 0) return; // Filter out zero quantities
      
      const slot = entry.slot;
      if (!containerGroups.has(slot)) {
        containerGroups.set(slot, {
          items: [],
          totalQty: 0
        });
      }
      const group = containerGroups.get(slot);
      group.items.push(entry);
      group.totalQty += entry.totalQty;
    });

    // Create slot entries with icon from most common item in container
    const slotEntries = [];
    containerGroups.forEach((group, slot) => {
      if (group.totalQty <= 0) return; // Filter out zero quantities
      
      // Find item with highest quantity for icon (or first if tied)
      const representative = group.items.reduce((max, item) => 
        item.totalQty > (max?.totalQty || 0) ? item : max, 
        group.items[0]
      );
      
      slotEntries.push({
        slot,
        displayQty: group.totalQty,
        iconKey: representative?.iconKey || null
      });
    });


    // Filter to only container entries (exclude equipment)
    const containerEntries = slotEntries
      .filter((entry) => entry.slot && containerOrder.includes(entry.slot) && entry.displayQty > 0)
      .sort((a, b) => a.slot.localeCompare(b.slot));

    // Equipment entries (non-container items)
    const equipmentEntries = slotEntries
      .filter((entry) => !entry.slot || !containerOrder.includes(entry.slot))
      .filter((entry) => entry.displayQty > 0)
      .sort((a, b) => {
        const aKey = entrySlotKey(a.slot);
        const bKey = entrySlotKey(b.slot);
        return aKey.localeCompare(bKey);
      })
      .slice(0, 4);

    function entrySlotKey(slot) {
      if (!slot) return 'zz';
      return String(slot);
    }

    return { containerEntries, equipmentEntries, containerOrder };
  }

  renderSidebarIcons(cardObj, containerEntries, equipmentEntries) {
    if (!cardObj || !cardObj.card) return;
    if (!Array.isArray(cardObj.invIcons)) cardObj.invIcons = [];
    cardObj.invIcons.forEach((node) => {
      if (node && typeof node.destroy === 'function') {
        try { node.destroy(); } catch (_) {}
      }
    });
    cardObj.invIcons.length = 0;

    const card = cardObj.card;
    const cardWidth = cardObj.cardWidth ?? 175;
    const cardHeight = cardObj.cardHeight ?? 68;
    const containerOrder = cardObj.containerOrder ?? this.getSidebarContainerOrder();

    const createIconDisplay = (entry, posX, posY) => {
      let iconNode;
      if (entry.iconKey && this.textures && this.textures.exists && this.textures.exists(entry.iconKey)) {
        iconNode = this.add.image(posX, posY, entry.iconKey);
        iconNode.setOrigin(0.5);
        iconNode.setScale(0.22);
      } else {
        iconNode = this.add.circle(posX, posY, 14, 0x4b5563, 0.85);
      }
      card.add(iconNode);
      cardObj.invIcons.push(iconNode);

      // Always show count for arrows/ammo, show for others if > 1
      const isArrow = entry.slot === 'arrow_container';
      if (isArrow || entry.displayQty > 1) {
        const qtyText = this.add.text(posX + 12, posY + 10, String(entry.displayQty), {
          fontSize: '11px',
          fontFamily: 'Arial, sans-serif',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(1, 1);
        card.add(qtyText);
        cardObj.invIcons.push(qtyText);
      }
    };

    const orderedContainerEntries = containerOrder
      .map((slotKey) => containerEntries.find((entry) => entry.slot === slotKey))
      .filter(Boolean);

    if (orderedContainerEntries.length) {
      const containerSpacing = 25;
      const rowY = cardHeight / 2 - 14;
      const totalWidth = containerSpacing * (orderedContainerEntries.length - 1);
      const startX = orderedContainerEntries.length > 1 ? -totalWidth / 2 + 10 : 0; // Moved left: 20 -> 10
      orderedContainerEntries.forEach((entry, idx) => {
        const x = startX + idx * containerSpacing;
        createIconDisplay(entry, x, rowY);
      });
    }

    if (equipmentEntries.length) {
      const equipmentX = cardWidth / 2 + 10; // Moved left: 20 -> 10
      const equipmentStartY = -cardHeight / 2 + 18;
      const equipmentSpacing = 20;
      equipmentEntries.forEach((entry, idx) => {
        const y = equipmentStartY + idx * equipmentSpacing;
        createIconDisplay(entry, equipmentX, y);
      });
    }
  }

  refreshSidebarForPlayer(player) {
    if (!player || !Array.isArray(this.sidebarCardObjects)) return;
    const heroId = player.hero?.id;
    const target = this.sidebarCardObjects.find((obj) => {
      if (!obj) return false;
      if (obj.player === player) return true;
      const objHeroId = obj.player?.hero?.id;
      return heroId && objHeroId && objHeroId === heroId;
    });
    if (!target) return;

    const { containerEntries, equipmentEntries, containerOrder } = this.buildSidebarIconData(target.player);
    if (containerOrder) target.containerOrder = containerOrder;
    this.renderSidebarIcons(target, containerEntries, equipmentEntries);

    const resourceBars = target.resourceBars || {};
    const updateBarFill = (key, colorFn, isVertical = false) => {
      const entry = resourceBars[key];
      if (!entry?.fill) return;
      const ratio = this.getSidebarResourceRatio(player, key);
      // Scale vertically for ultimate bar, horizontally for others
      entry.fill.setScale(isVertical ? 1 : ratio, isVertical ? ratio : 1);
      const color = colorFn ? colorFn(ratio) : entry.fill.fillColor;
      entry.fill.setFillStyle(color);
    };

    updateBarFill('health', (ratio) => this.getSidebarHealthColor(ratio));
    updateBarFill('stamina', () => SIDEBAR_STAMINA_COLOR);
    updateBarFill('mana', () => SIDEBAR_MANA_COLOR);
    updateBarFill('arrows', () => 0xFDE68A);
    
    // Update ultimate background alpha based on charge
    if (resourceBars.ultimate && resourceBars.ultimate.bg) {
      const ultimateRatio = this.getSidebarResourceRatio(player, 'ultimate');
      resourceBars.ultimate.bg.setAlpha(ultimateRatio);
      // Update border alpha when fully charged
      if (ultimateRatio >= 1) {
        resourceBars.ultimate.bg.setStrokeStyle(1, 0xFFD700, 0.8);
      } else {
        resourceBars.ultimate.bg.setStrokeStyle(1, 0xFFD700, 0.3);
      }
    }
    
    // Update main card border to golden when ultimate is fully charged
    if (target.bg) {
      const ultimateRatio = this.getSidebarResourceRatio(player, 'ultimate');
      if (ultimateRatio >= 1) {
        target.bg.setStrokeStyle(2, 0xFFD700, 1.0); // Golden outline when fully charged
      } else {
        target.bg.setStrokeStyle(1, 0x4B5563, 1.0); // Default gray outline
      }
    }

    if (target.ultimateIcon) {
      const iconMap = {
        damage_burst: '⚔️',
        area_burst: '💥'
      };
      const icon = iconMap[player.ultimateType] || '⭐';
      target.ultimateIcon.setText(icon);
    }
  }

  getRandomInt(max) {
    const n = Math.max(1, Math.floor(max));
    if (window && window.crypto && window.crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      window.crypto.getRandomValues(buf);
      return buf[0] % n;
    }
    return Math.floor(Math.random() * n);
  }

  shuffleKeys(keys) {
    const arr = [...(keys || [])];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.getRandomInt(i + 1);
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  async preload() {
    // Track weapons loading status
    this.weaponsLoaded = false;
        // Load battlefield data
        try {
          this.battlefieldsData = await loadBattlefieldsData();
          console.log(`Loaded ${this.battlefieldsData.length} battlefields`);
        } catch (error) {
          console.error('Failed to load battlefields:', error);
          this.battlefieldsData = [];
        }

        // Load intermission and items data
        try {
          this.intermissionsData = await loadIntermissionsData();
          this.itemsData = await loadItemsData();
          this.itemsById = buildItemsById(this.itemsData);
        } catch (e) {
          console.error('Failed to load intermission/items data', e);
          this.intermissionsData = [];
          this.itemsData = [];
          this.itemsById = new Map();
        }

        // Load music manifest for battle tracks
        try {
          const musicManifest = await loadMusicManifest();
          const manifestEntries = (musicManifest && Array.isArray(musicManifest.battle)) ? musicManifest.battle : [];
          this.battleMusicManifest = manifestEntries;
          let keys = manifestEntries.map(e => e.key);
          
          // Check if this is a custom theme (non-default)
          const theme = getCurrentTheme();
          const isCustomTheme = theme && theme.id && theme.id !== 'soulwars-default';
          
          // For custom themes: ONLY use the theme's specified battle music (no fallback)
          // For default theme: apply fallback if needed to ensure variety
          if (!isCustomTheme && (!keys || keys.length < 2)) {
            const fallback = [
              'music/College_DrumCadence03.mp3',
              'music/College_DrumCadence07.mp3',
              'music/College_DrumCadence11.mp3',
              'music/College_DrumCadence25.mp3'
            ];
            keys = Array.from(new Set([...(keys || []), ...fallback]));
            console.log('Default theme: applied fallback music for variety');
          }
          
          if (keys && keys.length) this.battleMusicKeys = keys;
          console.log('Battle music keys loaded:', this.battleMusicKeys);
          console.log('Custom theme:', isCustomTheme, 'Theme ID:', theme?.id);
        } catch (e) {
          console.warn('Music manifest not available, using defaults.');
        }

        // Load SFX manifest
        try {
          this.sfxManifest = await loadSfxManifest();
        } catch (e) {
          console.warn('SFX manifest not available.');
          this.sfxManifest = {};
        }

        // Load weapons manifest and merge with SFX manifest
        try {
          const weaponsManifest = await loadWeaponsManifest();
          Object.assign(this.sfxManifest, weaponsManifest);
          this.weaponsLoaded = true;
          console.log('Weapons manifest loaded:', Object.keys(weaponsManifest));
        } catch (e) {
          console.warn('Weapons manifest not available:', e);
          this.weaponsLoaded = true; // Still mark as loaded to proceed
        }

        // Load spells sounds manifest and merge with SFX manifest
        try {
          const spellsSoundsManifest = await loadSpellsSoundsManifest();
          Object.assign(this.sfxManifest, spellsSoundsManifest);
          console.log('Spells sounds manifest loaded:', Object.keys(spellsSoundsManifest));
        } catch (e) {
          console.warn('Spells sounds manifest not available:', e);
        }

        // Load food data
        try {
          this.foodData = await loadFoodData();
          console.log('Food data loaded:', this.foodData.length, 'items');
        } catch (e) {
          console.warn('Food data not available:', e);
          this.foodData = [];
        }

        // Load shields data
        try {
          this.shieldsData = await loadShieldsData();
          console.log('Shields data loaded:', this.shieldsData.length, 'shields');
        } catch (e) {
          console.warn('Shields data not available:', e);
          this.shieldsData = [];
        }

        // Token holders fetching disabled - using new battle flow with TeamBuilder
        console.log('Skipping token holders fetch - using new battle flow');
        this.topHolders = [];
        this.holdersDataLoaded = true;
  }

  create() {
    // Initialize basic game state - no GameManager yet
    this.sound.pauseOnBlur = false;
    
    // Add focus/blur event listeners to prevent music pausing
    this.setupFocusHandling();

    // Set game mode and team count from config
    this.selectedGameMode = this.game.config.gameMode || 'free-for-all';
    this.teamCount = this.game.config.teamCount || 2;
    this.gameModeManager.setSelectedMode(this.selectedGameMode);
    
    // Get max players from BattleStateManager configuration
    const battleConfig = BattleStateManager.getBattleConfig();
    this.maxHeroes = battleConfig?.maxPlayers || 20;

    //this.game.events.on(Phaser.Core.Events.BLUR, () => this.battleMusic?.setVolume(0.1));
    //this.game.events.on(Phaser.Core.Events.FOCUS, () => this.battleMusic?.setVolume(0.5));

    this.abortedMatchHandled = false;
    this.noPlayersTimeoutTriggered = false;

    this.players = [];
    this.food = [];
    this.deathMarkers = [];
    this.playerGroup = this.add.group(); // Group for z-indexing
    this.gameState = 'playing'; // Skip holders logic - now using new battle flow
    console.log('GameState changed to:', this.gameState);
    this.matchEndTimer = 0;
    this.finalTenVoteStarted = false;
    this._lastSidebarAliveCount = null;

    // Initialize dual camera system (corrected approach)
    // Reset any previous camera state to prevent corruption
    this.cameraController = null;
    this.worldCamera = null;
    this.uiCamera = null;
    
    // World camera (main) for game objects with panning/zooming
    this.worldCamera = this.cameras.main;
    this.worldCamera.setName('world');
    
    // Ensure camera is in a clean state
    console.log('📷 GameScene: Initializing fresh camera state');
    // Initial bounds will be expanded/overridden by the CameraController
    // Set a permissive square default to avoid clamping
    try {
      this.worldCamera.setBounds(0, 0, 4096, 4096);
    } catch (error) {
      console.warn('GameScene: Error setting initial camera bounds:', error);
    }

    // Check if we should preserve camera position from previous match
    if (this.preserveCameraPosition) {
      // Restore camera position from previous match
      this.worldCamera.setZoom(this.preserveCameraPosition.zoom);
      // Set appropriate bounds for the restored zoom level
      const viewWorldW = this.worldCamera.width / this.preserveCameraPosition.zoom;
      const viewWorldH = this.worldCamera.height / this.preserveCameraPosition.zoom;
      const extraX = Math.max(0, (viewWorldW - 2048) / 2);
      const extraY = Math.max(0, (viewWorldH - 2048) / 2);
      try {
        this.worldCamera.setBounds(-extraX, -extraY, 2048 + extraX * 2, 2048 + extraY * 2);
      } catch (error) {
        console.warn('GameScene: Error setting preserved camera bounds:', error);
      }
      this.worldCamera.centerOn(this.preserveCameraPosition.x, this.preserveCameraPosition.y);
      // Clear the flag
      this.preserveCameraPosition = null;
    } else {
      // Normal camera initialization
      // Fit full battlefield (2048x2048) to current viewport and expand bounds so centering isn't clamped
      const initialFitZoom = Math.min(this.worldCamera.width / 2048, this.worldCamera.height / 2048);
      this.worldCamera.setZoom(initialFitZoom);
      const viewWorldW0 = this.worldCamera.width / initialFitZoom;
      const viewWorldH0 = this.worldCamera.height / initialFitZoom;
      const extraX0 = Math.max(0, (viewWorldW0 - 2048) / 2);
      const extraY0 = Math.max(0, (viewWorldH0 - 2048) / 2);
      try {
        this.worldCamera.setBounds(-extraX0, -extraY0, 2048 + extraX0 * 2, 2048 + extraY0 * 2);
      } catch (error) {
        console.warn('GameScene: Error setting default camera bounds:', error);
      }
      // Center on battlefield (will be overridden by camera controller later)
    const isLargeForInit = (this.currentBattlefield?.size === 'large' || this.currentBattlefield?.size === 'largew');
      const cx = isLargeForInit ? 2048 : 1024;
      const cy = isLargeForInit ? 1536 : 768;
      this.worldCamera.centerOn(cx, cy);
    }

    // UI camera (fixed) for HUD elements - no scrolling or zooming
    // Use current scale size to avoid stale 1440x900 when canvas is wider
    const initialUiW = this.scale?.width || 1440;
    const initialUiH = this.scale?.height || 900;
    this.uiCamera = this.cameras.add(0, 0, initialUiW, initialUiH);
    this.uiCamera.setName('ui');
    this.uiCamera.setScroll(0, 0); // Fixed to top-left
    this.uiCamera.setZoom(1); // No zoom
    // Make UI camera transparent so it doesn't clear over world camera
    this.uiCamera.setBackgroundColor('rgba(0,0,0,0)');
    // Ensure viewport matches canvas
    try { this.uiCamera.setViewport(0, 0, initialUiW, initialUiH); } catch (_) {}

    // Player info panel properties
    this.playerInfoPanel = null;
    this.playerInfoBg = null;
    this.playerInfoName = null;
    this.playerInfoLevel = null;
    this.playerInfoHealth = null;
    this.playerInfoInvIcons = [];
    this.playerInfoPanelRect = null;
    this.selectedPlayer = null;

    console.log('Dual camera system initialized: world camera and UI camera');

    // Initialize player info panel
    this.createPlayerInfoPanel();

    // Initialize player sidebar (right column)
    this.createPlayerSidebar();

    // Initialize in-game chat debug overlay and hook window events
    if (this.showAudienceChatOverlay) this.initChatOverlay();
    try {
      window.addEventListener('soulwars-chat', this._onChatEvent);
      window.addEventListener('server-session-config', this.handleServerSessionConfig);
      this.events.once('shutdown', () => {
        try { window.removeEventListener('soulwars-chat', this._onChatEvent); } catch (e) {}
        try { window.removeEventListener('server-session-config', this.handleServerSessionConfig); } catch (_) {}
      });
    } catch (e) {
      console.warn('Chat event wiring failed:', e);
    }

    // Log available player textures
    try {
      const keys = this.textures ? this.textures.getTextureKeys() : [];
      //console.log('🧩 GameScene: all texture keys:', keys);
    } catch (e) {
      console.warn('GameScene: failed to list texture keys', e);
    }

    // Ensure audio can play on platforms requiring user interaction
    if (this.sound && this.sound.locked) {
      this.input.once('pointerdown', () => {
        if (this.sound && this.sound.locked) this.sound.unlock();
      });
    }

    // Initialize LeaderboardOverlay early so it's available for join screen
    this.leaderboardOverlay = new LeaderboardOverlay(this);
    console.log('LeaderboardOverlay initialized in create()');
    
    // Set up socket listener for hero image updates
    this.setupHeroImageUpdateListener();
    
    // Start the game immediately since we're using the new battle flow
    this.startGame();

    // Schedule a one-time battlefield camera tour 10s after match start
    //try { this.runBattlefieldTourOnce(10000); } catch (_) {}
  }

  initChatOverlay() {
    const padding = 8;
    const width = 520;
    const height = 200;

    // Background panel
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.55);
    bg.fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, 0x334155, 1);
    bg.strokeRoundedRect(0.5, 0.5, width - 1, height - 1, 6);
    bg.setDepth(10000);
    this.worldCamera.ignore(bg);
    this.chatBg = bg;

    // Text log
    const text = this.add.text(padding, padding, '', {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: '14px',
      color: '#E5E7EB',
      wordWrap: { width: width - padding * 2, useAdvancedWrap: true },
      lineSpacing: 2
    });
    text.setDepth(10001);
    this.worldCamera.ignore(text);
    this.chatText = text;

    // Position top-left with margin, on UI layer
    const margin = 10;
    this.chatBg.setPosition(margin, margin);
    this.chatText.setPosition(margin + padding, margin + padding);
  }

  onExternalChat(ev) {
    console.log('onExternalChat 1', ev);
    if (!ev || !ev.detail) return;
    // Scene might be shutting down or not ready
    if (!this.scene || !this.add || !this.game || !this.sys || this.sys.isDestroyed) {
      return;
    }
    // If scene lifecycle isn't at least created, ignore
    if (this.sys && typeof this.sys.isActive === 'function' && !this.sys.isActive()) {
      return;
    }
    const m = ev.detail;
    const time = new Date(m.timestamp || Date.now()).toLocaleTimeString();
    const user = m.username || 'unknown';
    const msg = (m.message == null ? '' : String(m.message));
    const wallet = (m && (m.userAddress || m.wallet || m.address)) || '';
    chatDbg('[ChatCmd] event received', { user, wallet, message: msg });
    if (this.showAudienceChatOverlay) this.appendChatLine(`[${time}] ${user}: ${msg}`);
    console.log('onExternalChat 2', m);
    handleChatMessage(this, m, { time, user, raw: msg });
  }

  appendChatLine(line) {
    if (typeof line !== 'string') return;
    this.chatLog.push(line);
    if (this.chatLog.length > this.maxChatLines) this.chatLog.splice(0, this.chatLog.length - this.maxChatLines);
    if (this.chatText) this.chatText.setText(this.chatLog.join('\n'));
  }

  // handleAudienceChat moved to chat/ChatCommands.js

  highlightPlayer(player, color = 0xffff00, durationMs = 1000, options = {}) {
    if (!player || !player.sprite) return;
    const opts = options || {};
    if (opts.fromShowCommand && typeof this.focusOnPlayerFromSidebar === 'function') {
      try {
        this.focusOnPlayerFromSidebar(player, {
          fromShowCommand: true,
          releaseAfterMs: (typeof opts.releaseAfterMs === 'number' && opts.releaseAfterMs > 0)
            ? opts.releaseAfterMs
            : durationMs
        });
      } catch (_) {}
    }
    if (!this.add || !this.time) return; // scene not ready
    try {
      const oldTintFill = (player.sprite.tintFill === true);
      const oldTint = player.sprite.tintTopLeft;
      player.sprite.setTintFill(color);
      chatDbg('[ChatCmd] highlight applied', { player: player.name, color, durationMs });
      this.time.delayedCall(durationMs, () => {
        try {
          if (oldTintFill) player.sprite.setTint(oldTint || 0xffffff);
          else player.sprite.clearTint();
          chatDbg('[ChatCmd] highlight cleared', { player: player.name });
        } catch (_) {}
      });
    } catch (_) {}
  }

  createPlayerInfoPanel() {
    // Panel dimensions and positioning
    const panelWidth = 220;
    const panelHeight = 146;
    const panelX = this.uiCamera.width - panelWidth - 10; // Right margin (10px from right edge)
    const panelY = this.uiCamera.height - panelHeight - 40; // 40px from bottom edge
    this.playerInfoPanelRect = { x: panelX, y: panelY, w: panelWidth, h: panelHeight };

    // Background
    this.playerInfoBg = this.add.graphics();
    this.playerInfoBg.fillStyle(0x000000, 0.8);
    this.playerInfoBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 8);
    this.playerInfoBg.lineStyle(2, 0xffffff, 0.5);
    this.playerInfoBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 8);

    // Text elements
    this.playerInfoName = this.add.text(panelX + 10, panelY + 10, '', {
      fontSize: '16px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2
    });

    this.playerInfoLevel = this.add.text(panelX + 10, panelY + 35, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffff00',
      stroke: '#000000',
      strokeThickness: 1
    });

    this.playerInfoHealth = this.add.text(panelX + 10, panelY + 55, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#00ff00',
      stroke: '#000000',
      strokeThickness: 1
    });

    // Strategy positioning factor (debug)
    this.playerInfoPositioning = this.add.text(panelX + 10, panelY + 75, '', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#00e5ff',
      stroke: '#000000',
      strokeThickness: 1
    });

    // Initially hide the panel
    this.hidePlayerInfoPanel();

    // Ensure world camera ignores these UI elements (they should only show on UI camera)
    if (this.worldCamera) {
      this.worldCamera.ignore([this.playerInfoBg, this.playerInfoName, this.playerInfoLevel, this.playerInfoHealth, this.playerInfoPositioning]);
    }
  }

  // --- Player Sidebar (left) ---
  createPlayerSidebar() {
    try {
      const sidebarWidth = Math.floor(this.uiCamera.width * 0.08); // Reduced from 0.1 to 0.08
      const sidebarX = sidebarWidth / 2;
      const topMargin = 78;
      const sidebarY = topMargin;

      // Container for sidebar
      this.playerSidebarContainer = this.add.container(sidebarX, sidebarY);
      this.playerSidebarContainer.setDepth(2002);

      // Background
      const bgHeight = Math.max(0, this.uiCamera.height - topMargin);
      const bg = this.add.rectangle(0, bgHeight / 2, sidebarWidth, bgHeight, 0x1F2937, 0);
      this.playerSidebarContainer.add(bg);

      // Title
      /*const title = this.add.text(0, 14, 'Who Will Win?', {
        fontFamily: 'Arial Black, Arial, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#F3F4F6'
      }).setOrigin(0.5, 0);
      this.playerSidebarContainer.add(title);*/

      // Cards container
      this.sidebarCardsContainer = this.add.container(0, 0);
      this.playerSidebarContainer.add(this.sidebarCardsContainer);

      // Track items
      this.sidebarCardObjects = [];

      // Ensure world camera ignores the sidebar (UI-only)
      if (this.worldCamera && this.worldCamera.ignore) {
        this.worldCamera.ignore([this.playerSidebarContainer]);
      }

      // Populate now if players already exist
      try { this.populatePlayerSidebar(); } catch (_) {}

      // Listen for vote standings updates
      this._sidebarVoteHandler = (event) => {
        try { this.updateSidebarVotes(event?.detail); } catch (_) {}
      };
      window.addEventListener('vote-standings-update', this._sidebarVoteHandler);
      this.events.once('shutdown', () => {
        try { window.removeEventListener('vote-standings-update', this._sidebarVoteHandler); } catch (_) {}
      });
    } catch (e) {
      console.warn('Failed to create player sidebar:', e);
    }
  }

  populatePlayerSidebar() {
    if (!this.sidebarCardsContainer || !Array.isArray(this.players)) return;
    // Clear existing
    if (Array.isArray(this.sidebarCardObjects)) {
      this.sidebarCardObjects.forEach(obj => {
        try { obj.card?.destroy(); } catch (_) {}
      });
    }
    this.sidebarCardObjects = [];

    const list = this.getSidebarPlayers();
    const cardHeight = 70;
    list.forEach((player, idx) => {
      //console.log('[SideBar] player', player);
      const yOffset = idx * (cardHeight);
      const cardObj = this.createPlayerSidebarCard(player, yOffset);
      this.sidebarCardsContainer.add(cardObj.card);
      this.sidebarCardObjects.push(cardObj);
    });
  }

  // Determine which players to show in the sidebar (max MAX_SIDEBAR_PLAYERS)
  getSidebarPlayers() {
    const all = Array.isArray(this.players) ? [...this.players] : [];
    const alive = all.filter(p => p && p.isAlive);
    const aliveCount = alive.length;

    // Specials always considered
    const specials = all.filter(p => p && p.isSpecial);

    let chosen = [];
    if (aliveCount > MAX_SIDEBAR_PLAYERS) {
      // Before MAX_SIDEBAR_PLAYERS left: first MAX_SIDEBAR_PLAYERS joined players, ensuring specials included
      const joinedFirst = all
        .filter(p => p)
        .sort((a, b) => this.playerSequence(a) - this.playerSequence(b));
      chosen = joinedFirst.slice(0, MAX_SIDEBAR_PLAYERS);
      // Ensure specials are present by replacing items from the end if needed
      specials.forEach(sp => {
        if (!chosen.includes(sp)) {
          if (chosen.length < MAX_SIDEBAR_PLAYERS) {
            chosen.push(sp);
          } else {
            chosen[chosen.length - 1] = sp;
          }
        }
      });
      // Re-sort by join order for display
      chosen = chosen.sort((a, b) => this.playerSequence(a) - this.playerSequence(b)).slice(0, MAX_SIDEBAR_PLAYERS);
    } else {
      // MAX_SIDEBAR_PLAYERS or fewer alive: show remaining players (alive) in join order
      chosen = alive.sort((a, b) => this.playerSequence(a) - this.playerSequence(b)).slice(0, MAX_SIDEBAR_PLAYERS);
    }

    return chosen;
  }

  playerSequence(player) {
    if (!player) return Number.MAX_SAFE_INTEGER;
    if (typeof player.joinOrder === 'number') return player.joinOrder;
    if (typeof player.playerNumber === 'number') return player.playerNumber;
    if (player.isAI) return 100000 + (player.id ? 1 : 0);
    return 50000;
  }

  buildVotePlayersList(players) {
    return players.map((player, index) => ({
      heroId: player.hero?.id || `player_${index}`,
      name: player.hero?.name || player.name,
      level: player.hero?.level || 1,
      isAI: !!player.isAI,
      isSpecial: !!player.isSpecial,
      playerNumber: player.playerNumber || index + 1
    }));
  }

  getSidebarHealthColor(percent) {
    if (percent <= 0.33) {
      return 0xff3333; // red
    }
    if (percent <= 0.66) {
      return 0xffff33; // yellow
    }
    return 0x33ff33; // green
  }

  getSidebarResourceRatio(player, resource) {
    if (!player) return 0;
    const clamp = (value) => Math.max(0, Math.min(1, value));
    switch (resource) {
      case 'health': {
        const max = player.maxHealth || player.hero?.stats?.health || player.health || 1;
        const current = Math.max(0, Math.min(max, player.health ?? max));
        return clamp(max > 0 ? current / max : 0);
      }
      case 'mana': {
        const max = player.maxMana || player.hero?.stats?.mana || 0;
        if (max <= 0) return 0;
        const current = Math.max(0, Math.min(max, player.mana ?? max));
        return clamp(current / max);
      }
      case 'stamina': {
        const max = player.maxStamina || player.hero?.stats?.stamina || 0;
        if (max <= 0) return 0;
        const current = Math.max(0, Math.min(max, player.stamina ?? max));
        return clamp(current / max);
      }
      case 'ultimate': {
        return clamp((player.ultimateCharge || 0) / 100);
      }
      case 'arrows': {
        const max = Math.max(1, player.maxArrows || 99);
        const count = player.arrowCount || 0;
        if (count > 0) {
          return clamp(count / max);
        }
        if (typeof player.hasArrowItem === 'function' && player.hasArrowItem()) {
          return 1;
        }
        return 0;
      }
      default:
        return 0;
    }
  }

  getPlatformIconKey(platform) {
    const normalized = (platform || '').toString().trim().toLowerCase();
    switch (normalized) {
      case 'pumpfun':
      case 'pump':
        return 'platform-pumpfun';
      case 'tiktok':
      case 'tt':
        return 'platform-tiktok';
      case 'x':
      case 'twitter':
        return 'platform-x';
      case 'youtube':
        return 'platform-youtube';
      case 'twitch':
        return 'platform-twitch';
      case 'facebook':
      case 'meta':
        return 'platform-facebook';
      default:
        return null;
    }
  }

  createPlayerSidebarCard(player, yOffset) {
    const cardWidth = 140; // Reduced from 175 to 140
    const cardHeight = 68;
    const card = this.add.container(0, yOffset);
    // Enable click to focus
    try { card.setSize(cardWidth, cardHeight); } catch (_) {}
    try { card.setInteractive({ useHandCursor: true }); } catch (_) {}
    try { card.on('pointerdown', () => this.focusOnPlayerFromSidebar(player)); } catch (_) {}

    // Golden ultimate background (behind main background, alpha controlled by ultimate charge)
    const ultimateRatio = this.getSidebarResourceRatio(player, 'ultimate');
    const ultimateBgAlpha = ultimateRatio; // Alpha from 0 to 1 based on ultimate charge
    const ultimateBg = this.add.rectangle(0, 0, cardWidth, cardHeight, SIDEBAR_ULTIMATE_FULL_COLOR, ultimateBgAlpha);
    ultimateBg.setStrokeStyle(1, 0xFFD700, 0.5); // Subtle golden border when charged
    card.add(ultimateBg);

    // Background
    const bg = this.add.rectangle(0, 0, cardWidth, cardHeight, 0x000000, 0.8);
    bg.setStrokeStyle(1, 0x4B5563);
    card.add(bg);

    // Player sprite (mini) if available
    let miniSprite = null;
    try {
      if (player.sprite && player.sprite.texture) {
        miniSprite = this.add.image(-cardWidth/2 + 18, 5 , player.sprite.texture.key); // Moved left: 24 -> 18
        miniSprite.setScale(0.08); // Reduced by 20%: 0.1 * 0.8 = 0.08
        card.add(miniSprite);
      }
    } catch (_) {}

    // Health bar behind name (created first so it's behind the text)
    const nameY = -25;
    const nameX = -cardWidth / 2 + 2;
    const healthBarWidth = 115; // Width of health bar behind name
    const healthBarHeight = 14; // Height matching name text (15px font + padding)
    const healthRatio = this.getSidebarResourceRatio(player, 'health');
    const healthColor = this.getSidebarHealthColor(healthRatio);
    
    const healthBarBg = this.add.rectangle(nameX, nameY, healthBarWidth, healthBarHeight, SIDEBAR_BAR_BG_COLOR, 1);
    const healthBarFill = this.add.rectangle(nameX, nameY, healthBarWidth, healthBarHeight, 0xffffff, 1);
    healthBarBg.setOrigin(0, 0.5);
    healthBarFill.setOrigin(0, 0.5);
    healthBarFill.setScale(healthRatio, 1);
    healthBarFill.setFillStyle(healthColor);
    card.add(healthBarBg);
    card.add(healthBarFill);

    // Name (truncated) - positioned over health bar
    const displayName = truncateUsername(player.name || '', 14);
    const nameText = this.add.text(nameX, nameY, displayName, {
      fontSize: '15px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0.5);
    card.add(nameText);

    // Level (upper right corner)
    const iconLevelY = -cardHeight / 2;
    const levelText = this.add.text(cardWidth - 75, iconLevelY, `${player.hero?.level || 1}`, {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#F3F300'
    }).setOrigin(1, 0);
    card.add(levelText);

    // Other resource bars (stamina, mana, arrows) - excluding health and ultimate
    const barWidth = 46;
    const barHeight = 6; // Increased from 4 to 6 (taller bars)
    const barGap = 1;
    const barX = -cardWidth / 2 + 40;
    let barY = -11;
    const resourceDefs = [
      { key: 'stamina', color: () => SIDEBAR_STAMINA_COLOR },
      { key: 'mana', color: () => SIDEBAR_MANA_COLOR }
    ];
    const resourceBars = {
      health: { fill: healthBarFill, bg: healthBarBg } // Store health bar reference
    };
    let ultimateIcon = null;

    resourceDefs.forEach((def, idx) => {
      const bg = this.add.rectangle(barX, barY, barWidth, barHeight, SIDEBAR_BAR_BG_COLOR, 1);
      const fill = this.add.rectangle(barX, barY, barWidth, barHeight, 0xffffff, 1);
      bg.setOrigin(0, 0.5);
      fill.setOrigin(0, 0.5);
      card.add(bg);
      card.add(fill);

      const ratio = this.getSidebarResourceRatio(player, def.key);
      const color = def.color(ratio);
      fill.setScale(ratio, 1);
      fill.setFillStyle(color);

      resourceBars[def.key] = { fill, bg };
      barY += barHeight + barGap;
    });

  
    
    // Store ultimate background for alpha updates
    resourceBars.ultimate = { bg: ultimateBg };

    // Vote percentage (lower )
    const voteText = this.add.text(cardWidth / 2 - 4, cardHeight / 2 - 6, '', { // Moved left: -6 -> -4
      fontSize: '12px',
      color: '#10B981',
      fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif',
      align: 'right'
    }).setOrigin(1, 1);
    card.add(voteText);

    // Platform icon (bottom left)
    let platformIcon = null;
    let platformFallbackText = null;
    const iconX = -cardWidth / 2 + 4; // Moved left: 8 -> 4
    const iconY = cardHeight / 2 - 6;
    const platformIconKey = this.getPlatformIconKey(player.platform);

    if (platformIconKey && this.textures && this.textures.exists && this.textures.exists(platformIconKey)) {
      platformIcon = this.add.image(iconX, iconY, platformIconKey);
      platformIcon.setOrigin(0, 1);
      //Nudge to the left
      platformIcon.setPosition(iconX - 4, iconY + 3); // Moved left: -8 -> -4
      platformIcon.setScale(0.22);
      card.add(platformIcon);
    } else {
      const label = player.isAI ? 'AI' : (player.platform ? player.platform.slice(0, 3).toUpperCase() : '???');
      platformFallbackText = this.add.text(iconX - 4, iconY + 3, label, { // Moved left: -8 -> -4
        fontSize: '10px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0, 1);
      card.add(platformFallbackText);
    }

    // Inventory icons (containers + next items)
    const invIcons = [];
    const cardObj = {
      card,
      player,
      bg,
      miniSprite,
      nameText,
      levelText,
      resourceBars,
      ultimateIcon,
      voteText,
      invIcons,
      platformIcon,
      platformFallbackText,
      cardWidth,
      cardHeight,
    };

    try {
      const { containerEntries, equipmentEntries, containerOrder } = this.buildSidebarIconData(player);
      cardObj.containerOrder = containerOrder;
      this.renderSidebarIcons(cardObj, containerEntries, equipmentEntries);
    } catch (error) {
      console.warn('[GameScene] Failed to render sidebar inventory icons', error);
    }

    return cardObj;
  }

  updateSidebarVotes(standings) {
    return;
    try {
      if (!standings || !standings.options || !Array.isArray(this.sidebarCardObjects)) return;
      const voteMap = {};
      standings.options.forEach(opt => { voteMap[opt.optionKey] = opt.percentage; });
      this.sidebarCardObjects.forEach(obj => {
        if (obj && obj.voteText && obj.player && obj.player.playerNumber) {
          const optionKey = `player_${obj.player.playerNumber}`;
          const percentage = voteMap[optionKey] || 0;
          obj.voteText.setText(`${percentage.toFixed(1)}%`);
        }
      });
    } catch (e) {
      console.error('[GameScene] Error updating sidebar votes:', e);
    }
  }

  // Focus camera on a player from sidebar click
  focusOnPlayerFromSidebar(player, options = {}) {
    try {
      if (!player || !player.sprite || !this.cameraController) return;
      const opts = options || {};
      const fromShowCommand = !!opts.fromShowCommand;
      const releaseAfterMsRaw = (typeof opts.releaseAfterMs === 'number' && opts.releaseAfterMs >= 0) ? opts.releaseAfterMs : 0;
      const now = this.time?.now || Date.now();

      if (this._showCommandFocusPlayer && (this._showCommandFocusExpiresAt || 0) <= now) {
        if (this._showCommandFocusTimer) {
          try { this._showCommandFocusTimer.remove(); } catch (_) {}
        }
        this._showCommandFocusTimer = null;
        this._showCommandFocusPlayer = null;
        this._showCommandFocusExpiresAt = 0;
      }

      if (fromShowCommand) {
        const activePlayer = this._showCommandFocusPlayer;
        const expiresAt = this._showCommandFocusExpiresAt || 0;
        if (activePlayer && activePlayer !== player) {
          if (expiresAt > now) {
            return;
          }
          if (this._showCommandFocusTimer) {
            try { this._showCommandFocusTimer.remove(); } catch (_) {}
          }
          this._showCommandFocusTimer = null;
          this._showCommandFocusPlayer = null;
          this._showCommandFocusExpiresAt = 0;
        }
      } else if (this._showCommandFocusPlayer) {
        if (this._showCommandFocusTimer) {
          try { this._showCommandFocusTimer.remove(); } catch (_) {}
        }
        this._showCommandFocusTimer = null;
        this._showCommandFocusPlayer = null;
        this._showCommandFocusExpiresAt = 0;
      }

      const x = player.sprite.x;
      const y = player.sprite.y;
      // Tween focus to player's position
      try { this.cameraController._startFocusTweenTo(x, y, 1200); } catch (_) { this.cameraController.setPlayerTarget(player); }
      this.cameraController.manualFocusActive = true;
      this.cameraController.manualFocusLastTs = this.time?.now || Date.now();
      // Tween zoom to ~0.75 (clamped by controller's max)
      const desired = 0.75;
      let maxZ = 1.1;
      try { maxZ = this.cameraController._computeMaxZoom(); } catch (_) {}
      const targetZoom = Phaser.Math.Clamp(desired, this.cameraController.minZoom || 0.05, maxZ || 1.1);
      this.cameraController.manualZoomActive = true;
      this.cameraController.manualZoomLastTs = this.time?.now || Date.now();
      if (typeof this.cameraController.manualZoomBaseline !== 'number') {
        this.cameraController.manualZoomBaseline = this.worldCamera?.zoom || desired;
      }
      if (this.tweens && this.worldCamera) {
        this.tweens.add({
          targets: this.worldCamera,
          zoom: targetZoom,
          duration: 900,
          ease: 'Sine.easeInOut'
        });
      } else if (this.worldCamera) {
        this.worldCamera.setZoom(targetZoom);
      }

      if (fromShowCommand) {
        const releaseAfterMs = releaseAfterMsRaw > 0 ? releaseAfterMsRaw : 1500;
        this._showCommandFocusPlayer = player;
        this._showCommandFocusExpiresAt = now + releaseAfterMs;
        if (this._showCommandFocusTimer) {
          try { this._showCommandFocusTimer.remove(); } catch (_) {}
        }
        this._showCommandFocusTimer = null;
        if (this.time && typeof this.time.delayedCall === 'function') {
          this._showCommandFocusTimer = this.time.delayedCall(releaseAfterMs, () => {
            try {
              if (this._showCommandFocusPlayer === player) {
                this._showCommandFocusPlayer = null;
                this._showCommandFocusExpiresAt = 0;
              }
            } catch (_) {}
            this._showCommandFocusTimer = null;
          });
        }
      }
    } catch (e) {
      console.warn('focusOnPlayerFromSidebar error', e);
    }
  }

  requestActionFocus(player, options = {}) {
    return;
    if (!player || !player.sprite || !this.cameraController) return;
    const now = this.time?.now || Date.now();
    const duration = Math.max(1000, options.durationMs || CommandDurations.actionFocusMs);
    const desiredZoom = typeof options.zoom === 'number' ? options.zoom : 0.85;

    const userManualFocusActive = !this.actionFocusActive && this.cameraController.manualFocusActive;
    if (userManualFocusActive) {
      return;
    }

    this.actionFocusActive = true;
    this.actionFocusPlayer = player;
    this.actionFocusEndAt = now + duration;
    this.actionFocusReturnZoom = this.worldCamera ? this.worldCamera.zoom : null;

    if (this.actionFocusReleaseTimer) {
      try { this.actionFocusReleaseTimer.remove(false); } catch (_) {}
      this.actionFocusReleaseTimer = null;
    }

    if (this.time && typeof this.time.delayedCall === 'function') {
      this.actionFocusReleaseTimer = this.time.delayedCall(duration, () => {
        this.releaseActionFocus();
      });
    } else {
      setTimeout(() => this.releaseActionFocus(), duration);
    }

    if (this.gameManager && typeof this.gameManager.pauseBoundaryShrinkFor === 'function') {
      this.gameManager.pauseBoundaryShrinkFor(duration + 1000);
    }

    try {
      if (typeof this.cameraController._startFocusTweenTo === 'function') {
        this.cameraController._startFocusTweenTo(player.sprite.x, player.sprite.y, 700);
      } else {
        this.cameraController.setPlayerTarget(player);
      }
    } catch (_) {
      this.cameraController.setPlayerTarget(player);
    }

    this.cameraController.manualFocusActive = true;
    this.cameraController.manualFocusLastTs = now;
    this.cameraController.manualZoomActive = true;
    this.cameraController.manualZoomLastTs = now;
    if (typeof this.cameraController.manualZoomBaseline !== 'number') {
      this.cameraController.manualZoomBaseline = this.worldCamera?.zoom || desiredZoom;
    }

    const maxZoom = typeof this.cameraController._computeMaxZoom === 'function'
      ? this.cameraController._computeMaxZoom()
      : (this.cameraController.maxZoom || 1.1);
    const minZoom = this.cameraController.minZoom || 0.05;
    const targetZoom = Phaser.Math.Clamp(desiredZoom, minZoom, maxZoom);

    if (this.worldCamera) {
      if (this.tweens) {
        if (this.cameraController.zoomRevertTween) {
          this.cameraController.zoomRevertTween.stop();
          this.cameraController.zoomRevertTween = null;
        }
        this.tweens.add({
          targets: this.worldCamera,
          zoom: targetZoom,
          duration: 650,
          ease: 'Sine.easeInOut'
        });
      } else {
        this.worldCamera.setZoom(targetZoom);
      }
    }
  }

  releaseActionFocus() {
    if (!this.actionFocusActive) return;
    this.actionFocusActive = false;
    this.actionFocusPlayer = null;
    this.actionFocusEndAt = 0;

    if (this.actionFocusReleaseTimer) {
      try { this.actionFocusReleaseTimer.remove(false); } catch (_) {}
      this.actionFocusReleaseTimer = null;
    }

    if (this.cameraController) {
      this.cameraController.manualFocusActive = false;
      this.cameraController.manualZoomActive = false;
      const returnZoom = this.actionFocusReturnZoom;
      this.actionFocusReturnZoom = null;
      const baseline = returnZoom != null
        ? returnZoom
        : (typeof this.cameraController.manualZoomBaseline === 'number'
            ? this.cameraController.manualZoomBaseline
            : null);
      if (baseline != null && this.worldCamera) {
        const maxZoom = typeof this.cameraController._computeMaxZoom === 'function'
          ? this.cameraController._computeMaxZoom()
          : (this.cameraController.maxZoom || 1.1);
        const minZoom = this.cameraController.minZoom || 0.05;
        const targetZoom = Phaser.Math.Clamp(baseline, minZoom, maxZoom);
        if (this.tweens) {
          if (this.cameraController.zoomRevertTween) {
            this.cameraController.zoomRevertTween.stop();
            this.cameraController.zoomRevertTween = null;
          }
          this.cameraController.zoomRevertTween = this.tweens.add({
            targets: this.worldCamera,
            zoom: targetZoom,
            duration: 1200,
            ease: 'Sine.easeInOut',
            onComplete: () => {
              this.cameraController.zoomRevertTween = null;
            }
          });
        } else {
          this.worldCamera.setZoom(targetZoom);
        }
        this.cameraController.manualZoomBaseline = targetZoom;
      }
    }
  }

  async startFinalTenVoting() {
    try {
      const alivePlayers = (this.players || []).filter(p => p && p.isAlive);
      if (!alivePlayers.length) return;

      const sorted = alivePlayers.slice().sort((a, b) => this.playerSequence(a) - this.playerSequence(b));
      const votePlayers = this.buildVotePlayersList(sorted);

      VoteManager.reset();

      let battleId = BattleStateManager.getBattleId();
      if (!battleId) {
        battleId = `battle_${Date.now()}`;
        BattleStateManager.setBattleId(battleId);
      }

      const roomId = (typeof window !== 'undefined' && window.currentRoomId) ? window.currentRoomId : null;
      const sessionId = await VoteManager.initSession(battleId, votePlayers, roomId);
      BattleStateManager.setVoteSessionId(sessionId);

      this.votingOpen = true;
      this.votingClosed = false;

      if (this.timerText && this.timerText.active) {
        this.timerText.setText('Voting: Final 10');
        this.timerText.setColor('#10B981');
      }

      //try { showMarqueeMessage('🔥 Final 10 voting is live! Place your bets!', { color: '#F97316' }, 'high'); } catch (_) {}
    } catch (error) {
      console.error('[GameScene] Failed to start final ten voting:', error);
      this.finalTenVoteStarted = false;
    }
  }

  showPlayerInfoPanel(player) {
    if (!this.playerInfoBg || !player) return;

    this.selectedPlayer = player;

    console.log('showPlayerInfoPanel', player);

    // Update text content
    const heroLevel = player.hero?.level || 1;
    const currentHealth = Math.max(0, Math.floor(player.health));
    const maxHealth = Math.floor(player.maxHealth);

    this.playerInfoName.setText(player.name);
    this.playerInfoLevel.setText(`Level ${heroLevel}`);
    this.playerInfoHealth.setText(`HP: ${currentHealth}/${maxHealth}`);

    // Positioning strategy (0..100) and computed factor (0..1)
    const pos = (typeof player.strategyPositioning === 'number')
      ? player.strategyPositioning
      : (player.strategies && typeof player.strategies.positioning === 'number')
        ? player.strategies.positioning
        : 50;
    const posFactor = (pos / 100).toFixed(2);
    this.playerInfoPositioning.setText(`Pos: ${pos} (factor ${posFactor})`);

    // Show all elements
    this.playerInfoBg.setVisible(true);
    this.playerInfoName.setVisible(true);
    this.playerInfoLevel.setVisible(true);
    this.playerInfoHealth.setVisible(true);
    this.playerInfoPositioning.setVisible(true);

    // Render inventory row
    try { this.renderPlayerInventoryRow(player); } catch (_) {}
  }

  hidePlayerInfoPanel() {
    if (!this.playerInfoBg) return;

    this.selectedPlayer = null;

    // Hide all elements
    this.playerInfoBg.setVisible(false);
    this.playerInfoName.setVisible(false);
    this.playerInfoLevel.setVisible(false);
    this.playerInfoHealth.setVisible(false);
    if (this.playerInfoPositioning) this.playerInfoPositioning.setVisible(false);
    try { this.clearPlayerInventoryRow(); } catch (_) {}
  }

  // Player lookup methods for chat commands
  findPlayerByUserId(userId) {
    try { 
      // First try local players array
      let player = this.players?.find(p => p && p.userId === userId);
      if (player) return player;
      
      // Fallback to BattleStateManager
      return BattleStateManager.findPlayerByUserId(userId);
    } catch (_) { 
      return null; 
    }
  }

  findPlayerByWallet(wallet) {
    try { 
      // First try local players array
      let player = this.players?.find(p => p && (p.wallet === wallet || p.walletAddress === wallet));
      if (player) return player;
      
      // Fallback to BattleStateManager
      return BattleStateManager.findPlayerByWallet(wallet);
    } catch (_) { 
      return null; 
    }
  }

  updatePlayerInfoPanel() {
    if (!this.selectedPlayer || !this.playerInfoHealth || !this.playerInfoHealth.visible) {
      return;
    }

    // Check if selected player is still alive
    if (!this.selectedPlayer.isAlive) {
      // Player died, hide the panel
      this.hidePlayerInfoPanel();
      return;
    }

    // Update health information
    const currentHealth = Math.max(0, Math.floor(this.selectedPlayer.health));
    const maxHealth = Math.floor(this.selectedPlayer.maxHealth);
    this.playerInfoHealth.setText(`HP: ${currentHealth}/${maxHealth}`);

    // Refresh inventory UI if it changed
    try {
      const inv = Array.isArray(this.selectedPlayer.inventory) ? this.selectedPlayer.inventory : [];
      const sig = inv.map(e => `${e.itemId}:${e.qty||0}`).join('|');
      if (sig !== this.selectedPlayer._lastInfoInvSig) {
        this.selectedPlayer._lastInfoInvSig = sig;
        this.renderPlayerInventoryRow(this.selectedPlayer);
      }
    } catch (_) {}

    // Keep positioning debug updated in case strategies change at runtime
    const pos = (typeof this.selectedPlayer.strategyPositioning === 'number')
      ? this.selectedPlayer.strategyPositioning
      : (this.selectedPlayer.strategies && typeof this.selectedPlayer.strategies.positioning === 'number')
        ? this.selectedPlayer.strategies.positioning
        : 50;
    if (this.playerInfoPositioning) {
      this.playerInfoPositioning.setText(`Pos: ${pos} (factor ${(pos / 100).toFixed(2)})`);
    }

    // Could add other dynamic updates here (position, status, etc.)
    // For now, health is the main thing that changes during gameplay
  }

  clearPlayerInventoryRow() {
    if (!Array.isArray(this.playerInfoInvIcons)) this.playerInfoInvIcons = [];
    this.playerInfoInvIcons.forEach(el => { try { el.destroy(); } catch (_) {} });
    this.playerInfoInvIcons.length = 0;
  }

  renderPlayerInventoryRow(player) {
    if (!player || !this.playerInfoPanelRect) return;
    const inv = Array.isArray(player.inventory) ? player.inventory : [];
    this.clearPlayerInventoryRow();
    if (!inv.length) return;

    const { x, y, h } = this.playerInfoPanelRect;
    const startX = x + 10;
    const rowY = y + h - 24;
    const maxSlots = 7;
    const step = 28;

    for (let i = 0; i < Math.min(inv.length, maxSlots); i++) {
      const entry = inv[i];
      const def = this.itemsById && this.itemsById.get ? this.itemsById.get(entry.itemId) : null;
      const iconKey = def?.iconKey;
      if (!iconKey) continue;
      const ix = startX + i * step;
      const img = this.add.image(ix, rowY, iconKey);
      img.setOrigin(0, 0.5);
      //rotate the item 45 degrees counterclockwise
      img.rotation = -Math.PI / 4;
      img.setScale(0.2);
      img.setDepth(10005);
      this.playerInfoInvIcons.push(img);
      if (this.worldCamera && this.worldCamera.ignore) this.worldCamera.ignore(img);

      const qty = Math.max(1, entry.qty || 1);
      const qt = this.add.text(ix + 16, rowY + 8, String(qty), {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2
      });
      qt.setOrigin(0.5, 0.5);
      qt.setDepth(10006);
      this.playerInfoInvIcons.push(qt);
      if (this.worldCamera && this.worldCamera.ignore) this.worldCamera.ignore(qt);
    }
  }

  showEmote(player, token) {
    if (!player || !player.sprite) return;
    if (!this.add || this.sys?.isDestroyed) return; // scene not ready
    const s = (typeof this.objectScale === 'number') ? this.objectScale : 1.0;
    const x = player.sprite.x;
    const y = player.sprite.y - 70 * s;

    const resolved = resolveEmoteToken(token, this.textures);
    let node;
    if (resolved.kind === 'sprite' && resolved.key) {
      node = this.add.image(x, y, resolved.key);
      node.setScale(1.6 * s);
    } else {
      node = this.add.text(x, y, String(resolved.text || '🙂'), {
        fontFamily: 'Arial',
        fontSize: `${Math.round(44 * s)}px`,
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);
    }
    chatDbg('[ChatCmd] emote rendered', { player: player.name, resolved });
    if (!node || !node.setDepth) return;
    node.setDepth(12000);
    // Render emotes in the battle (world) camera; ensure UI camera ignores them
    if (this.uiCamera && this.uiCamera.ignore) this.uiCamera.ignore(node);

    // Move slightly and fade after hold
    try {
      if (this.tweens) {
        this.tweens.add({ targets: node, y: y - 24 * s, duration: 1400, ease: 'Quad.easeOut' });
        this.tweens.add({ targets: node, alpha: { from: 1, to: 0 }, delay: 500, duration: 1000, ease: 'Quad.easeOut', onComplete: () => node.destroy() });
      } else {
        setTimeout(() => { try { node.destroy(); } catch (_) {} }, 1600);
      }
    } catch (_) {
      if (this.time) this.time.delayedCall(1600, () => { try { node.destroy(); } catch (_) {} });
      else setTimeout(() => { try { node.destroy(); } catch (_) {} }, 1600);
    }
  }

  showEmoteForWallet(wallet, token) {
    // If in lobby (before game starts), try to emote over the lobby grid slot
    if (this.gameState === 'waitingForHolders' || this.gameState === 'showingHolders') {
      if (this.joinGridMap && this.joinGridMap.has(wallet)) {
        // Global emote throttle: at most 2 per second across all wallets
        const nowGlobal = this.time?.now || Date.now();
        this._emoteGlobalTimes = (this._emoteGlobalTimes || []).filter(ts => nowGlobal - ts < 1000);
        if (this._emoteGlobalTimes.length >= 2) return;
        const slot = this.joinGridMap.get(wallet);
        const now = this.time?.now || Date.now();
        slot.lastEmoteAt = slot.lastEmoteAt || 0;
        if (now - slot.lastEmoteAt >= 2000) { // 2s cooldown per slot
          slot.lastEmoteAt = now;
          this._emoteGlobalTimes.push(nowGlobal);
          const s = (typeof this.objectScale === 'number') ? this.objectScale : 1.0;
          const x = slot.x;
          const y = slot.y - 70 * s;
          const resolved = resolveEmoteToken(token, this.textures);
          let node;
          if (resolved.kind === 'sprite' && resolved.key) {
            node = this.add.image(x, y, resolved.key);
            node.setScale(1.6 * s);
          } else {
            node = this.add.text(x, y, String(resolved.text || '🙂'), {
              fontFamily: 'Arial',
              fontSize: `${Math.round(44 * s)}px`,
              color: '#ffffff',
              stroke: '#000000',
              strokeThickness: 3
            }).setOrigin(0.5);
          }
          if (node) {
            node.setDepth(2002);
            // During lobby, join grid is rendered on the UI camera, so emotes must also be on UI camera
            if (this.worldCamera && this.worldCamera.ignore) this.worldCamera.ignore(node);
            try {
              if (this.tweens) {
                this.tweens.add({ targets: node, y: y - 24 * s, duration: 1400, ease: 'Quad.easeOut' });
                this.tweens.add({ targets: node, alpha: { from: 1, to: 0 }, delay: 500, duration: 1000, ease: 'Quad.easeOut', onComplete: () => node.destroy() });
              } else setTimeout(() => { try { node.destroy(); } catch (_) {} }, 1600);
            } catch (_) {
              if (this.time) this.time.delayedCall(1600, () => { try { node.destroy(); } catch (_) {} });
              else setTimeout(() => { try { node.destroy(); } catch (_) {} }, 1600);
            }
          }
          return;
        }
        return; // on cooldown during lobby
      }
    }

    // Otherwise, emote over the in-game player sprite
    const nowGlobal2 = this.time?.now || Date.now();
    this._emoteGlobalTimes = (this._emoteGlobalTimes || []).filter(ts => nowGlobal2 - ts < 1000);
    if (this._emoteGlobalTimes.length >= 2) return;
    const p = (this.players || []).find(pp => pp && pp.walletAddress === wallet);
    if (!p) return;
    // Don't allow emotes if player is dead
    if (!p.isAlive) return;
    const now = this.time?.now || Date.now();
    p._emoteCooldownUntil = p._emoteCooldownUntil || 0;
    if (now < p._emoteCooldownUntil) return;
    p._emoteCooldownUntil = now + 2000;
    this._emoteGlobalTimes.push(nowGlobal2);
    this.showEmote(p, token);
  }

  // Compute per-player melee attack cooldown; rage halves the interval
  getAttackCooldownMs(player) {
    try {
      let base = 1000; // 1 second base
      if (!player) return base;
      
      // Apply weapon attack speed modifier
      if (player.equippedWeapon && player.equippedWeapon.attackSpeed) {
        base = base / player.equippedWeapon.attackSpeed;
      }
      
      // Quickness reduces cooldown: 0.5% per point above 10
      const quicknessReduction = player.getEffectiveQuickness ? 
        (player.getEffectiveQuickness() - 10) * 0.005 : 0;
      const itemSpeedBonus = player.statModifiers?.attackSpeed || 0;
      const totalReduction = Math.min(0.5, quicknessReduction + itemSpeedBonus); // Cap at 50%
      
      let cooldown = base * (1 - totalReduction);
      
      // Rage command still halves the remaining cooldown
      const now = this.time?.now || Date.now();
      const rageUntil = player.commandState?.rageUntil || 0;
      if (now < rageUntil) cooldown = Math.max(200, cooldown / 2);

      const defendUntil = player.commandState?.defendUntil || 0;
      if (now < defendUntil) {
        cooldown *= 1.4;
      }
      
      return cooldown;
    } catch (_) {
      return 1000;
    }
  }

  tryShootArrow(shooter, target, distanceOverride = null) {
    if (!this.arrowManager || !shooter?.isAlive || !target?.isAlive) return false;
    if (!shooter.commandState?.shootModeActive) return false;
    // Cannot shoot while stunned
    if (typeof shooter.isStunned === 'function' && shooter.isStunned()) return false;
    if (typeof shooter.canAttack === 'function' && !shooter.canAttack(target)) return false;
    if (!shooter.sprite || !target.sprite) return false;

    const arrowStats = typeof shooter.getArrowStats === 'function' ? shooter.getArrowStats() : null;
    if (!arrowStats && (shooter.arrowCount || 0) <= 0) {
      shooter.stopShooting?.('no_arrows');
      return false;
    }

    // Check stamina cost (5 stamina per arrow)
    const arrowStaminaCost = 5;
    if (typeof shooter.stamina === 'number' && shooter.stamina < arrowStaminaCost) {
      return false; // Not enough stamina to fire
    }

    // Prevent firing arrows when too close to enemy (must be outside melee range)
    // Calculate distance to target
    const distance = (typeof distanceOverride === 'number')
      ? distanceOverride
      : Phaser.Math.Distance.Between(shooter.sprite.x, shooter.sprite.y, target.sprite.x, target.sprite.y);

    // Prevent firing if player is in melee combat with ANY enemy (not just the target)
    if (shooter.isBattling) {
      return false; // Too close - in melee combat, use melee attacks instead
    }

    // Also check if there are any enemies within melee range (defense against being surrounded)
    const meleeRange = this.meleeRange || 52;
    const allPlayers = this.players || [];
    for (let i = 0; i < allPlayers.length; i++) {
      const enemy = allPlayers[i];
      if (!enemy || enemy === shooter || !enemy.isAlive) continue;
      if (typeof shooter.canAttack === 'function' && !shooter.canAttack(enemy)) continue;
      
      const enemyDist = Phaser.Math.Distance.Between(
        shooter.sprite.x, shooter.sprite.y,
        enemy.sprite.x, enemy.sprite.y
      );
      
      if (enemyDist < meleeRange) {
        return false; // There's an enemy in melee range - can't fire arrows
      }
    }

    // Prevent firing arrows when too close to the target (must be well outside melee range)
    // Use a larger minimum distance to ensure arrows can't be used in close combat
    const minDistance = Math.max(meleeRange * 1.5, 100); // At least 1.5x melee range, minimum 100 pixels
    if (distance <= minDistance) {
      return false; // Too close to target - use melee instead
    }

    const maxDistance = Math.max(
      minDistance + 25,
      arrowStats?.range || this.rangedRange || this.baseRangedRange || 600
    );

    if (distance >= maxDistance) {
      return false; // Too far away
    }

    const now = this.time?.now || Date.now();
    const lastShot = shooter.lastRangedAttackTime || 0;
    const cooldown = (this.getAttackCooldownMs(shooter) * 1.35) + 250;
    if (now < lastShot + cooldown) {
      return false;
    }

    shooter.lastRangedAttackTime = now;
    if (!shooter.consumeArrow || !shooter.consumeArrow()) {
      return false;
    }

    const baseDamage = shooter.calculateMeleeDamage ? shooter.calculateMeleeDamage() : 8;
    const damage = Math.max(2, arrowStats?.damage ?? baseDamage);
    const projectileSpeed = Math.max(120, arrowStats?.speed ?? 720);
    const arrowSpriteKey = shooter.getArrowItemIconKey ? shooter.getArrowItemIconKey() : null;
    const fired = this.arrowManager.shoot({
      shooter,
      target,
      damage,
      speed: projectileSpeed,
      maxDistance,
      spriteKey: arrowSpriteKey,
    });
    if (!fired) {
      return false;
    }

    // Deduct stamina cost (5 stamina per arrow) after arrow is successfully fired
    if (typeof shooter.spendStamina === 'function') {
      shooter.spendStamina(arrowStaminaCost);
    }

    if (typeof shooter.markCombatEngaged === 'function') {
      shooter.markCombatEngaged('ranged_attack');
    }

    if (this.tauntManager) {
      this.tauntManager.triggerTaunt(shooter, 'attack');
    }

    return true;
  }

  createArmyConfigPanel(overlayX, overlayY, overlayWidth) {
    if (!this.armyConfigPanel) {
      this.armyConfigPanel = [];
    }

    const panelX = overlayX + 20;
    const panelY = overlayY + 90;
    const panelWidth = 420;
    
    // Remember layout so we can update progress UI during loads
    this._armyPanelCoords = { overlayX, overlayY, overlayWidth };

    // Get current army and settings from React state via window
    const armies = window.__SW_ARMIES__ || [];
    const selectedArmyId = window.__SW_SELECTED_ARMY_ID__ || '';
    const selectedArmy = armies.find(a => a.id === selectedArmyId);
    const armyPlayerCount = window.__SW_ARMY_PLAYER_COUNT__ || 100;
    const armySelectionMode = window.__SW_ARMY_SELECTION_MODE__ || 'random';
    const isExpanded = window.__SW_ARMY_CONFIG_EXPANDED__ || false;
    const isPaused = window.__SW_COUNTDOWN_PAUSED__ || false;
    
    // Initialize selected battlefield from localStorage if available
    if (!window.__SW_SELECTED_BATTLEFIELD_ID__) {
      try {
        const saved = localStorage.getItem('sw_selected_battlefield_id');
        if (saved) window.__SW_SELECTED_BATTLEFIELD_ID__ = saved;
      } catch (_) {}
    }
    
    // Load battlefields if not already loaded
    if (!window.__SW_BATTLEFIELDS__) {
      this.fetchBattlefields();
    }

    // Header background
    const headerBg = this.add.graphics();
    headerBg.fillStyle(0x1a1a2e, 0.95);
    headerBg.fillRoundedRect(panelX, panelY, panelWidth, 40, 8);
    headerBg.lineStyle(2, 0x00ff88, 1);
    headerBg.strokeRoundedRect(panelX, panelY, panelWidth, 40, 8);
    headerBg.setDepth(2010);
    this.worldCamera.ignore(headerBg);
    this.overlayElements.push(headerBg);
    this.armyConfigPanel.push(headerBg);

    // Header text (clickable to expand/collapse)
    const headerText = this.add.text(panelX + 20, panelY + 20, `⚔️ Army Configuration ${isExpanded ? '▼' : '▶'}`, {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#00FF88',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5).setDepth(2011);
    headerText.setInteractive({ useHandCursor: true });
    headerText.on('pointerover', () => { headerText.setColor('#FFFF00'); });
    headerText.on('pointerout', () => { headerText.setColor('#00FF88'); });
    headerText.on('pointerdown', () => {
      window.__SW_ARMY_CONFIG_EXPANDED__ = !isExpanded;
      this.destroyArmyConfigPanel();
      this.createArmyConfigPanel(overlayX, overlayY, overlayWidth);
    });
    this.worldCamera.ignore(headerText);
    this.overlayElements.push(headerText);
    this.armyConfigPanel.push(headerText);

    // Pause/Resume button in header
    const pauseBtnX = panelX + panelWidth - 110;
    const pauseBtnY = panelY + 8;
    const pauseBtnBg = this.add.graphics();
    pauseBtnBg.fillStyle(isPaused ? 0x00aa00 : 0xaa5500, 0.9);
    pauseBtnBg.fillRoundedRect(pauseBtnX, pauseBtnY, 100, 24, 4);
    pauseBtnBg.setDepth(2011);
    this.worldCamera.ignore(pauseBtnBg);
    this.overlayElements.push(pauseBtnBg);
    this.armyConfigPanel.push(pauseBtnBg);

    const pauseBtnText = this.add.text(pauseBtnX + 50, pauseBtnY + 12, isPaused ? '▶ Resume' : '⏸ Pause', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(2012);
    pauseBtnText.setInteractive({ useHandCursor: true });
    pauseBtnText.on('pointerover', () => { pauseBtnText.setColor('#FFFF00'); });
    pauseBtnText.on('pointerout', () => { pauseBtnText.setColor('#FFFFFF'); });
    pauseBtnText.on('pointerdown', () => {
      window.__SW_COUNTDOWN_PAUSED__ = !isPaused;
      this.destroyArmyConfigPanel();
      this.createArmyConfigPanel(overlayX, overlayY, overlayWidth);
    });
    this.worldCamera.ignore(pauseBtnText);
    this.overlayElements.push(pauseBtnText);
    this.armyConfigPanel.push(pauseBtnText);

    // If expanded, show configuration options
    if (isExpanded) {
      let currentY = panelY + 50;

      // Panel content background
      const contentBg = this.add.graphics();
      contentBg.fillStyle(0x0f0f1e, 0.95);
      contentBg.fillRoundedRect(panelX, currentY, panelWidth, 290, 8);
      contentBg.lineStyle(1, 0x00ff88, 0.5);
      contentBg.strokeRoundedRect(panelX, currentY, panelWidth, 240, 8);
      contentBg.setDepth(2010);
      this.worldCamera.ignore(contentBg);
      this.overlayElements.push(contentBg);
      this.armyConfigPanel.push(contentBg);

      currentY += 15;

      // Army Info
      const armyName = selectedArmy ? selectedArmy.name : 'None';
      const armyType = selectedArmy ? (selectedArmy.army_type === 'nft' ? 'NFT' : 'Token') : '';
      const armyChain = selectedArmy ? (selectedArmy.chain === 'solana' ? 'SOL' : 'SUI') : '';
      
      const armyInfoText = this.add.text(panelX + 20, currentY, 
        `Selected: ${armyName}\n` +
        `Type: ${armyType} • Chain: ${armyChain}\n` +
        `Players: ${armyPlayerCount} • Mode: ${armySelectionMode === 'top' ? 'Top Holders' : 'Random'}`, {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: '#FFFFFF',
        lineSpacing: 4
      }).setDepth(2011);
      this.worldCamera.ignore(armyInfoText);
      this.overlayElements.push(armyInfoText);
      this.armyConfigPanel.push(armyInfoText);

      currentY += 75;

      // Note about changing settings
      const noteText = this.add.text(panelX + 20, currentY,
        '💡 To change army settings, use the sidebar before launching the match', {
        fontFamily: 'Arial',
        fontSize: '12px',
        color: '#AAAAAA',
        wordWrap: { width: panelWidth - 40 }
      }).setDepth(2011);
      this.worldCamera.ignore(noteText);
      this.overlayElements.push(noteText);
      this.armyConfigPanel.push(noteText);

      currentY += 50;

      // Team selection (Team A vs Team B)
      const allArmies = armies || [];
      // Initialize defaults if not set
      if (!window.__SW_TEAM_A_ARMY_ID__ && allArmies.length > 0) window.__SW_TEAM_A_ARMY_ID__ = allArmies[0].id;
      if (!window.__SW_TEAM_B_ARMY_ID__ && allArmies.length > 1) window.__SW_TEAM_B_ARMY_ID__ = allArmies[1].id;
      const teamAId = window.__SW_TEAM_A_ARMY_ID__ || '';
      const teamBId = window.__SW_TEAM_B_ARMY_ID__ || '';
      const teamA = allArmies.find(a => a.id === teamAId) || null;
      const teamB = allArmies.find(a => a.id === teamBId) || null;

      const teamLabel = this.add.text(panelX + 20, currentY, 'Team Armies:', {
        fontFamily: 'Arial',
        fontSize: '13px',
        color: '#DDDDDD'
      }).setDepth(2011);
      this.worldCamera.ignore(teamLabel);
      this.overlayElements.push(teamLabel);
      this.armyConfigPanel.push(teamLabel);

      const rowY = currentY + 22;

      // Helper to draw team row
      const drawTeamRow = (x, y, label, armyObj, side) => {
        const name = armyObj ? armyObj.name : 'None';
        const bg = this.add.graphics();
        bg.fillStyle(0x182032, 0.9);
        bg.fillRoundedRect(x, y, panelWidth - 40, 26, 6);
        bg.lineStyle(1, 0x00aaff, 0.5);
        bg.strokeRoundedRect(x, y, panelWidth - 40, 26, 6);
        bg.setDepth(2011);
        this.worldCamera.ignore(bg);
        this.overlayElements.push(bg);
        this.armyConfigPanel.push(bg);

        const text = this.add.text(x + 10, y + 13, `${label}: ${name}`, {
          fontFamily: 'Arial',
          fontSize: '14px',
          color: '#FFFFFF'
        }).setOrigin(0, 0.5).setDepth(2012);
        this.worldCamera.ignore(text);
        this.overlayElements.push(text);
        this.armyConfigPanel.push(text);

        // Left/right selectors
        const left = this.add.text(x + panelWidth - 40 - 40, y + 13, '◀', {
          fontFamily: 'Arial', fontSize: '16px', color: '#00FF88', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(2012);
        left.setInteractive({ useHandCursor: true });
        left.on('pointerdown', () => { this.cycleTeamArmy(side, -1); });
        this.worldCamera.ignore(left);
        this.overlayElements.push(left);
        this.armyConfigPanel.push(left);

        const right = this.add.text(x + panelWidth - 40 - 16, y + 13, '▶', {
          fontFamily: 'Arial', fontSize: '16px', color: '#00FF88', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(2012);
        right.setInteractive({ useHandCursor: true });
        right.on('pointerdown', () => { this.cycleTeamArmy(side, +1); });
        this.worldCamera.ignore(right);
        this.overlayElements.push(right);
        this.armyConfigPanel.push(right);
      };

      drawTeamRow(panelX + 20, rowY, 'Team A', teamA, 'A');
      drawTeamRow(panelX + 20, rowY + 32, 'Team B', teamB, 'B');

      currentY += 72;

      // Battlefield Selection (operator use)
      const battlefieldLabel = this.add.text(panelX + 20, currentY, 'Battlefield:', {
        fontFamily: 'Arial',
        fontSize: '13px',
        color: '#DDDDDD'
      }).setDepth(2011);
      this.worldCamera.ignore(battlefieldLabel);
      this.overlayElements.push(battlefieldLabel);
      this.armyConfigPanel.push(battlefieldLabel);

      const battlefieldRowY = currentY + 22;
      
      const allBattlefields = window.__SW_BATTLEFIELDS__ || [];
      const selectedBattlefieldId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
      const selectedBattlefield = allBattlefields.find(bf => bf.id === selectedBattlefieldId) || null;
      
      // Display battlefield info
      let battlefieldDisplayName = 'None';
      if (selectedBattlefield) {
        const customName = selectedBattlefield.customName;
        const themeName = selectedBattlefield.themeBattlefield?.name || 'Unknown';
        const ownerName = selectedBattlefield.ownerName || 'Unowned';
        battlefieldDisplayName = customName ? `${customName} (${ownerName})` : `${themeName} (${ownerName})`;
      }

      const battlefieldBg = this.add.graphics();
      battlefieldBg.fillStyle(0x182032, 0.9);
      battlefieldBg.fillRoundedRect(panelX + 20, battlefieldRowY, panelWidth - 40, 26, 6);
      battlefieldBg.lineStyle(1, 0x00aaff, 0.5);
      battlefieldBg.strokeRoundedRect(panelX + 20, battlefieldRowY, panelWidth - 40, 26, 6);
      battlefieldBg.setDepth(2011);
      this.worldCamera.ignore(battlefieldBg);
      this.overlayElements.push(battlefieldBg);
      this.armyConfigPanel.push(battlefieldBg);

      const battlefieldText = this.add.text(panelX + 30, battlefieldRowY + 13, battlefieldDisplayName, {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: '#FFFFFF'
      }).setOrigin(0, 0.5).setDepth(2012);
      this.worldCamera.ignore(battlefieldText);
      this.overlayElements.push(battlefieldText);
      this.armyConfigPanel.push(battlefieldText);

      // Left/right selectors for battlefield
      const battlefieldLeft = this.add.text(panelX + panelWidth - 40 - 40, battlefieldRowY + 13, '◀', {
        fontFamily: 'Arial', fontSize: '16px', color: '#00FF88', fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2012);
      battlefieldLeft.setInteractive({ useHandCursor: true });
      battlefieldLeft.on('pointerdown', () => { this.cycleBattlefield(-1); });
      this.worldCamera.ignore(battlefieldLeft);
      this.overlayElements.push(battlefieldLeft);
      this.armyConfigPanel.push(battlefieldLeft);

      const battlefieldRight = this.add.text(panelX + panelWidth - 40 - 16, battlefieldRowY + 13, '▶', {
        fontFamily: 'Arial', fontSize: '16px', color: '#00FF88', fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2012);
      battlefieldRight.setInteractive({ useHandCursor: true });
      battlefieldRight.on('pointerdown', () => { this.cycleBattlefield(+1); });
      this.worldCamera.ignore(battlefieldRight);
      this.overlayElements.push(battlefieldRight);
      this.armyConfigPanel.push(battlefieldRight);

      currentY += 50;

      // Player Count stepper (choose how many NFTs will spawn)
      const countLabel = this.add.text(panelX + 20, currentY, 'Players to spawn:', {
        fontFamily: 'Arial',
        fontSize: '13px',
        color: '#DDDDDD'
      }).setDepth(2011);
      this.worldCamera.ignore(countLabel);
      this.overlayElements.push(countLabel);
      this.armyConfigPanel.push(countLabel);

      const minCount = 10;
      const maxCount = 200;
      const step = 10;
      const displayX = panelX + 200;
      const displayY = currentY - 2;

      // Minus button
      const minusBg = this.add.graphics();
      minusBg.fillStyle(0x333333, 0.9);
      minusBg.fillRoundedRect(displayX, displayY, 28, 22, 4);
      minusBg.lineStyle(1, 0x888888, 1);
      minusBg.strokeRoundedRect(displayX, displayY, 28, 22, 4);
      minusBg.setDepth(2012);
      this.worldCamera.ignore(minusBg);
      this.overlayElements.push(minusBg);
      this.armyConfigPanel.push(minusBg);

      const minusText = this.add.text(displayX + 14, displayY + 11, '−', {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#FFFFFF',
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2013);
      minusText.setInteractive({ useHandCursor: true });
      minusText.on('pointerdown', () => {
        let newCount = Math.max(minCount, Math.min(maxCount, (window.__SW_ARMY_PLAYER_COUNT__ || 100) - step));
        window.__SW_ARMY_PLAYER_COUNT__ = newCount;
        try { localStorage.setItem('sw_army_player_count', String(newCount)); } catch (_) {}
        if (this._armyPanelCoords) {
          this.destroyArmyConfigPanel();
          this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
        }
      });
      this.worldCamera.ignore(minusText);
      this.overlayElements.push(minusText);
      this.armyConfigPanel.push(minusText);

      // Value display
      const valueBg = this.add.graphics();
      valueBg.fillStyle(0x202030, 0.95);
      valueBg.fillRoundedRect(displayX + 34, displayY, 56, 22, 4);
      valueBg.setDepth(2012);
      this.worldCamera.ignore(valueBg);
      this.overlayElements.push(valueBg);
      this.armyConfigPanel.push(valueBg);

      const valueText = this.add.text(displayX + 62, displayY + 11, String(armyPlayerCount), {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: '#00FF88',
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2013);
      this.worldCamera.ignore(valueText);
      this.overlayElements.push(valueText);
      this.armyConfigPanel.push(valueText);

      // Plus button
      const plusBg = this.add.graphics();
      plusBg.fillStyle(0x333333, 0.9);
      plusBg.fillRoundedRect(displayX + 96, displayY, 28, 22, 4);
      plusBg.lineStyle(1, 0x888888, 1);
      plusBg.strokeRoundedRect(displayX + 96, displayY, 28, 22, 4);
      plusBg.setDepth(2012);
      this.worldCamera.ignore(plusBg);
      this.overlayElements.push(plusBg);
      this.armyConfigPanel.push(plusBg);

      const plusText = this.add.text(displayX + 110, displayY + 11, '+', {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#FFFFFF',
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2013);
      plusText.setInteractive({ useHandCursor: true });
      plusText.on('pointerdown', () => {
        let newCount = Math.max(minCount, Math.min(maxCount, (window.__SW_ARMY_PLAYER_COUNT__ || 100) + step));
        window.__SW_ARMY_PLAYER_COUNT__ = newCount;
        try { localStorage.setItem('sw_army_player_count', String(newCount)); } catch (_) {}
        if (this._armyPanelCoords) {
          this.destroyArmyConfigPanel();
          this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
        }
      });
      this.worldCamera.ignore(plusText);
      this.overlayElements.push(plusText);
      this.armyConfigPanel.push(plusText);

      currentY += 32;

      // Fetch Players Button
      const fetchBtnX = panelX + 20;
      const fetchBtnY = currentY;
      const fetchBtnWidth = panelWidth - 40;
      const fetchBtnHeight = 40;
      
      const fetchBtnBg = this.add.graphics();
      fetchBtnBg.fillStyle(0x0066cc, 0.9);
      fetchBtnBg.fillRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
      fetchBtnBg.lineStyle(2, 0x00aaff, 1);
      fetchBtnBg.strokeRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
      fetchBtnBg.setDepth(2011);
      this.worldCamera.ignore(fetchBtnBg);
      this.overlayElements.push(fetchBtnBg);
      this.armyConfigPanel.push(fetchBtnBg);

      const fetchBtnText = this.add.text(fetchBtnX + fetchBtnWidth / 2, fetchBtnY + fetchBtnHeight / 2,
        '🔄 Fetch Players from Army', {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#FFFFFF',
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2012);
      fetchBtnText.setInteractive({ useHandCursor: true });
      fetchBtnText.on('pointerover', () => { 
        fetchBtnText.setColor('#FFFF00'); 
        fetchBtnBg.clear();
        fetchBtnBg.fillStyle(0x0088ff, 0.9);
        fetchBtnBg.fillRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
        fetchBtnBg.lineStyle(2, 0x00aaff, 1);
        fetchBtnBg.strokeRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
      });
      fetchBtnText.on('pointerout', () => { 
        fetchBtnText.setColor('#FFFFFF'); 
        fetchBtnBg.clear();
        fetchBtnBg.fillStyle(0x0066cc, 0.9);
        fetchBtnBg.fillRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
        fetchBtnBg.lineStyle(2, 0x00aaff, 1);
        fetchBtnBg.strokeRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
      });
      fetchBtnText.on('pointerdown', () => {
        console.log('🔄 Fetch players button clicked (Phaser)');
        try { this.fetchArmyPlayersInPhaser(); } catch (e) { console.error('Fetch players (Phaser) failed', e); }
      });
      this.worldCamera.ignore(fetchBtnText);
      this.overlayElements.push(fetchBtnText);
      this.armyConfigPanel.push(fetchBtnText);

      // Loading overlay on top of the fetch button
      const isLoading = !!this.armyLoading;
      const progress = Math.max(0, Math.min(100, Number(this.nftLoadingProgress || 0)));
      if (isLoading) {
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.55);
        overlay.fillRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
        overlay.lineStyle(1, 0x00ff88, 0.6);
        overlay.strokeRoundedRect(fetchBtnX, fetchBtnY, fetchBtnWidth, fetchBtnHeight, 8);
        overlay.setDepth(2013);
        this.worldCamera.ignore(overlay);
        this.overlayElements.push(overlay);
        this.armyConfigPanel.push(overlay);

        // Progress bar background
        const barBg = this.add.graphics();
        barBg.fillStyle(0x0a0a1a, 0.9);
        const barX = fetchBtnX + 10;
        const barY = fetchBtnY + fetchBtnHeight - 14;
        const barW = fetchBtnWidth - 20;
        const barH = 8;
        barBg.fillRoundedRect(barX, barY, barW, barH, 4);
        barBg.setDepth(2014);
        this.worldCamera.ignore(barBg);
        this.overlayElements.push(barBg);
        this.armyConfigPanel.push(barBg);

        // Progress fill
        const barFill = this.add.graphics();
        const fillW = Math.max(0, Math.min(barW, Math.floor(barW * (progress / 100))));
        barFill.fillStyle(0x00ff88, 0.95);
        barFill.fillRoundedRect(barX, barY, fillW, barH, 4);
        barFill.setDepth(2015);
        this.worldCamera.ignore(barFill);
        this.overlayElements.push(barFill);
        this.armyConfigPanel.push(barFill);

        // Loading text
        const loadingText = this.add.text(fetchBtnX + fetchBtnWidth / 2, fetchBtnY + fetchBtnHeight / 2,
          progress > 0 ? `Loading NFTs… ${progress}%` : 'Fetching players…', {
          fontFamily: 'Arial',
          fontSize: '15px',
          color: '#00FF88',
          fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(2016);
        this.worldCamera.ignore(loadingText);
        this.overlayElements.push(loadingText);
        this.armyConfigPanel.push(loadingText);
        this.armyFetchUI = { barFill, loadingText, barX, barY, barW, barH };
      }

      currentY += 55;

      // Fetch Team Armies Button
      const fetchTeamsBg = this.add.graphics();
      fetchTeamsBg.fillStyle(0x5a2a2a, 0.9);
      fetchTeamsBg.fillRoundedRect(panelX + 20, currentY, panelWidth - 40, 36, 8);
      fetchTeamsBg.lineStyle(2, 0xff5555, 1);
      fetchTeamsBg.strokeRoundedRect(panelX + 20, currentY, panelWidth - 40, 36, 8);
      fetchTeamsBg.setDepth(2011);
      this.worldCamera.ignore(fetchTeamsBg);
      this.overlayElements.push(fetchTeamsBg);
      this.armyConfigPanel.push(fetchTeamsBg);

      const fetchTeamsText = this.add.text(panelX + 20 + (panelWidth - 40)/2, currentY + 18, '🏳️‍⚔️ Fetch Team Armies', {
        fontFamily: 'Arial', fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2012);
      fetchTeamsText.setInteractive({ useHandCursor: true });
      fetchTeamsText.on('pointerdown', () => {
        console.log('🔄 Fetch team armies clicked (Phaser)');
        try { this.fetchTeamArmiesInPhaser(); } catch (e) { console.error('Fetch team armies failed', e); }
      });
      this.worldCamera.ignore(fetchTeamsText);
      this.overlayElements.push(fetchTeamsText);
      this.armyConfigPanel.push(fetchTeamsText);

      currentY += 46;

      // Status text
      const armyPlayersCount = (window.__SW_ARMY_PLAYERS__ || []).length;
      const statusText = this.add.text(panelX + 20, currentY,
        armyPlayersCount > 0 ? `✅ ${armyPlayersCount} players loaded` : '⏳ No players loaded yet', {
        fontFamily: 'Arial',
        fontSize: '12px',
        color: armyPlayersCount > 0 ? '#00FF88' : '#FFAA00'
      }).setDepth(2011);
      this.worldCamera.ignore(statusText);
      this.overlayElements.push(statusText);
      this.armyConfigPanel.push(statusText);
    }
  }

  destroyArmyConfigPanel() {
    if (this.armyConfigPanel && this.armyConfigPanel.length > 0) {
      this.armyConfigPanel.forEach(el => {
        if (el && el.destroy) {
          try { el.destroy(); } catch (_) {}
        }
      });
      this.armyConfigPanel = [];
    }
    this.armyFetchUI = null;
  }

  async fetchArmyPlayersInPhaser() {
    const armies = window.__SW_ARMIES__ || [];
    const selectedArmyId = window.__SW_SELECTED_ARMY_ID__ || '';
    const playerCount = window.__SW_ARMY_PLAYER_COUNT__ || 100;
    const selectionMode = window.__SW_ARMY_SELECTION_MODE__ || 'random';
    const army = armies.find(a => a && a.id === selectedArmyId);
    if (!army) {
      console.warn('[Phaser] No army selected');
      return;
    }

    this.armyLoading = true;
    this.nftLoadingProgress = 0;
    try {
      if (this._armyPanelCoords) {
        this.destroyArmyConfigPanel();
        this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
      }
    } catch (_) {}

    try {
      const response = await fetch(`/api/armies/${encodeURIComponent(selectedArmyId)}/fetch-players`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SOULWARS_API_KEY) ? process.env.REACT_APP_SOULWARS_API_KEY : 'dev-key'
        },
        body: JSON.stringify({ count: playerCount, mode: selectionMode })
      });
      if (!response.ok) {
        console.error('[Phaser] Failed to fetch army players', response.status);
        this.armyLoading = false;
        return;
      }
      const data = await response.json();
      const players = data?.data?.players || [];
      this.armyPlayers = players;
      window.__SW_ARMY_PLAYERS__ = players;
      console.log(`[Phaser] Army players fetched: ${players.length}`);

      if (army.army_type === 'nft') {
        const imageUrls = players.map(p => p && p.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) {
          const nftKeys = [];
          this.load.setCORS('anonymous');

          const onProgress = (progress) => {
            this.nftLoadingProgress = Math.round(progress * 100);
            if (this.armyFetchUI) {
              const { barFill, loadingText, barX, barY, barW, barH } = this.armyFetchUI;
              try {
                const fillW = Math.max(0, Math.min(barW, Math.floor(barW * (this.nftLoadingProgress / 100))));
                barFill.clear();
                barFill.fillStyle(0x00ff88, 0.95);
                barFill.fillRoundedRect(barX, barY, fillW, barH, 4);
                loadingText.setText(`Loading NFTs… ${this.nftLoadingProgress}%`);
              } catch (_) {}
            }
          };

          this.load.on('progress', onProgress);

          imageUrls.forEach((url, index) => {
            const key = `nft_army_${index}_${Date.now()}`;
            nftKeys.push(key);
            this.load.image(key, url);
          });

          this.load.once('complete', () => {
            window.__SW_NFT_SPRITE_KEYS__ = nftKeys;
            this.nftLoadingProgress = 100;
            if (this.armyFetchUI) {
              const { barFill, loadingText, barX, barY, barW, barH } = this.armyFetchUI;
              try {
                barFill.clear();
                barFill.fillStyle(0x00ff88, 0.95);
                barFill.fillRoundedRect(barX, barY, barW, barH, 4);
                loadingText.setText('Loaded 100%');
              } catch (_) {}
            }
            this.load.off('progress', onProgress);
            this.armyLoading = false;
            try {
              if (this._armyPanelCoords) {
                this.destroyArmyConfigPanel();
                this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
              }
            } catch (_) {}
          });

          this.load.start();
          return;
        }
      }

      this.armyLoading = false;
      try {
        if (this._armyPanelCoords) {
          this.destroyArmyConfigPanel();
          this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
        }
      } catch (_) {}
    } catch (e) {
      console.error('[Phaser] Error during army fetch/preload', e);
      this.armyLoading = false;
    }
  }

  cycleTeamArmy(side, delta) {
    const armies = window.__SW_ARMIES__ || [];
    if (!armies.length) return;
    const key = side === 'B' ? '__SW_TEAM_B_ARMY_ID__' : '__SW_TEAM_A_ARMY_ID__';
    const currentId = window[key] || (armies[0] && armies[0].id) || '';
    const idx = Math.max(0, armies.findIndex(a => a.id === currentId));
    const next = (idx + (delta > 0 ? 1 : armies.length - 1)) % armies.length;
    window[key] = armies[next].id;
    try { localStorage.setItem(key, window[key]); } catch (_) {}
    if (this._armyPanelCoords) {
      this.destroyArmyConfigPanel();
      this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
    }
  }

  async fetchBattlefields() {
    try {
      const response = await fetch('/api/battlefields/all', {
        credentials: 'include',
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
          ...(window.__SW_AUTH_HEADERS__ || {})
        }
      });
      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data) {
          window.__SW_BATTLEFIELDS__ = json.data;
          console.log('[Game] Loaded battlefields:', json.data.length);
        }
      } else {
        console.warn('[Game] Failed to fetch battlefields:', response.status);
      }
    } catch (error) {
      console.error('[Game] Error fetching battlefields:', error);
    }
  }

  cycleBattlefield(delta) {
    const battlefields = window.__SW_BATTLEFIELDS__ || [];
    if (!battlefields.length) {
      this.fetchBattlefields().then(() => {
        // Retry after fetch
        setTimeout(() => this.cycleBattlefield(delta), 100);
      });
      return;
    }
    const currentId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
    const idx = currentId ? Math.max(0, battlefields.findIndex(bf => bf.id === currentId)) : 0;
    const next = (idx + (delta > 0 ? 1 : battlefields.length - 1)) % battlefields.length;
    window.__SW_SELECTED_BATTLEFIELD_ID__ = battlefields[next].id;
    try { localStorage.setItem('sw_selected_battlefield_id', window.__SW_SELECTED_BATTLEFIELD_ID__); } catch (_) {}
    if (this._armyPanelCoords) {
      this.destroyArmyConfigPanel();
      this.createArmyConfigPanel(this._armyPanelCoords.overlayX, this._armyPanelCoords.overlayY, this._armyPanelCoords.overlayWidth);
    }
  }

  async fetchTeamArmiesInPhaser() {
    const teamAId = window.__SW_TEAM_A_ARMY_ID__ || '';
    const teamBId = window.__SW_TEAM_B_ARMY_ID__ || '';
    const count = window.__SW_ARMY_PLAYER_COUNT__ || 100;
    const mode = window.__SW_ARMY_SELECTION_MODE__ || 'random';

    const fetchOne = async (armyId) => {
      const response = await fetch(`/api/armies/${encodeURIComponent(armyId)}/fetch-players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SOULWARS_API_KEY) ? process.env.REACT_APP_SOULWARS_API_KEY : 'dev-key' },
        body: JSON.stringify({ count, mode })
      });
      if (!response.ok) throw new Error(`fetch ${armyId} failed: ${response.status}`);
      const data = await response.json();
      return data?.data?.players || [];
    };

    try {
      // Fetch both in parallel
      const [playersA, playersB] = await Promise.all([
        fetchOne(teamAId),
        fetchOne(teamBId)
      ]);
      window.__SW_TEAM_A_PLAYERS__ = playersA;
      window.__SW_TEAM_B_PLAYERS__ = playersB;

      // Preload NFT images for each team (if any)
      const urlsA = (playersA || []).map(p => p && p.imageUrl).filter(Boolean);
      const urlsB = (playersB || []).map(p => p && p.imageUrl).filter(Boolean);
      const keysA = [];
      const keysB = [];
      this.load.setCORS('anonymous');
      if (urlsA.length) {
        urlsA.forEach((url, i) => { const key = `nft_teamA_${i}_${Date.now()}`; keysA.push(key); this.load.image(key, url); });
      }
      if (urlsB.length) {
        urlsB.forEach((url, i) => { const key = `nft_teamB_${i}_${Date.now()}`; keysB.push(key); this.load.image(key, url); });
      }

      if (urlsA.length || urlsB.length) {
        this.load.once('complete', () => {
          window.__SW_TEAM_A_SPRITE_KEYS__ = keysA;
          window.__SW_TEAM_B_SPRITE_KEYS__ = keysB;
          console.log(`[Teams] Preloaded A:${keysA.length} B:${keysB.length} NFT sprites`);
        });
        this.load.start();
      } else {
        console.log('[Teams] No NFT images to preload for teams');
      }
    } catch (e) {
      console.error('[Teams] Error fetching/preloading team armies', e);
    }
  }

  // createHoldersOverlay method removed - now using new battle flow with PreBattleScene

  getAvailablePlayerTextures() {
    // Get player textures from current theme
    const theme = getCurrentTheme();
    
    if (theme && theme.players && Array.isArray(theme.players) && theme.players.length > 0) {
      // Extract texture keys from theme players (handle both string filenames and objects)
      const themePlayerKeys = theme.players.map(p => {
        const filename = typeof p === 'string' ? p : (p && (p.image || p.asset || ''));
        return String(filename || '').replace(/\.[^/.]+$/, ''); // Remove extension
      }).filter(k => k && this.textures && this.textures.exists && this.textures.exists(k));
      
      //console.log('🎯 getAvailablePlayerTextures: found from theme:', themePlayerKeys);
      
      if (themePlayerKeys.length > 0) {
        return themePlayerKeys;
      }
    }
    
    // Fallback to default player textures
    const candidates = [
      'easy1','easy2','easy3','easy4','easy5','easy6',
      'elite1','elite2','elite3','elite4','elite5','elite6','elite7','elite8','elite9',
      'expert1','expert2','expert3',
      'hard1','hard2','hard3','hard4',
      'normal1','normal2','normal3','normal4'
    ];
    const available = candidates.filter(k => this.textures && this.textures.exists && this.textures.exists(k));
    //console.log('🎯 getAvailablePlayerTextures: using default fallback:', available);
    return available;
  }

  ensureJoinerRecord(wallet, displayName) {
    if (!this.lobbyJoiners) this.lobbyJoiners = new Map();
    if (this.lobbyJoiners.has(wallet)) return this.lobbyJoiners.get(wallet);
    const textures = this.getAvailablePlayerTextures();
    const idx = Math.abs(this.hashString(wallet)) % Math.max(1, textures.length);
    const textureKey = textures[idx] || 'easy1';
    // Prefer chat nickname if present
    let name = displayName || truncateWallet(wallet);
    try { if (this.walletDisplayNames && this.walletDisplayNames.has(wallet)) name = this.walletDisplayNames.get(wallet) || name; } catch (_) {}
    name = truncateUsername(name);
    const rec = { name, textureKey };
    this.lobbyJoiners.set(wallet, rec);
    return rec;
  }

  markAutoJoin(wallet, matches = 2) {
    try {
      const cur = autoJoinRemaining.get(wallet) || 0;
      // If user is getting a new grant (e.g., 5 credits from interaction), prefer the higher of existing or new
      // Otherwise allow additive behavior when small top-ups are granted
      const incoming = Number(matches) || 0;
      const next = (incoming >= cur) ? incoming : cur;
      autoJoinRemaining.set(wallet, next);
    } catch (_) {}
  }

  refreshJoinGrid() {
    if (!this.joinGridArea) return;
    // Clear previous grid
    if (Array.isArray(this.joinGridObjects)) {
      this.joinGridObjects.forEach(o => { try { o.destroy(); } catch (_) {} });
    }
    this.joinGridObjects = [];
    this.joinGridMap = new Map();

    // Clean up lobbyJoiners - remove wallets with 0 credits
    try {
      if (this.lobbyJoiners) {
        const walletsToRemove = [];
        this.lobbyJoiners.forEach((rec, wallet) => {
          const credits = autoJoinRemaining.get(wallet) || 0;
          if (credits <= 0) {
            walletsToRemove.push(wallet);
          }
        });
        walletsToRemove.forEach(wallet => this.lobbyJoiners.delete(wallet));
        if (walletsToRemove.length > 0) {
          console.log('🎫 Removed wallets with no credits from lobby:', walletsToRemove.length);
        }
      }
    } catch (e) {
      console.error('Error cleaning up lobbyJoiners:', e);
    }

    // Include auto-join wallets in the grid display even if they didn't re-type join this match
    try {
      autoJoinRemaining.forEach((remaining, wallet) => {
        if (remaining > 0 && !this.lobbyJoiners.has(wallet)) {
          const name = (this.walletDisplayNames && this.walletDisplayNames.get(wallet)) || truncateWallet(wallet);
          this.ensureJoinerRecord(wallet, name);
        }
      });
    } catch (_) {}

    // Get entries AFTER cleanup and auto-join additions
    const entries = Array.from((this.lobbyJoiners || new Map()).entries());
    if (!entries.length) return;

    const itemW = 110;
    const itemH = 120;
    const cols = Math.max(1, Math.floor(this.joinGridArea.w / itemW));
    const scale = 0.12 * (this.objectScale || 1.0);

    entries.forEach(([wallet, rec], i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = this.joinGridArea.x + col * itemW + itemW / 2;
      const cy = this.joinGridArea.y + row * itemH + itemH / 2;

      const img = this.add.image(cx, cy, rec.textureKey);
      img.setScale(scale);
      img.setDepth(2001);
      this.worldCamera.ignore(img);
      this.joinGridObjects.push(img);

      const label = this.add.text(cx, cy - 48 * (this.objectScale || 1.0), rec.name, {
        fontFamily: 'Arial Black, Arial, sans-serif',
        fontSize: '16px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5).setDepth(2001);
      this.worldCamera.ignore(label);
      this.joinGridObjects.push(label);

      // Credits remaining (auto-join) under the sprite
      try {
        const credits = (autoJoinRemaining.get(wallet) || 0);
        const creditsText = this.add.text(cx, cy + 40 * (this.objectScale || 1.0), `Credits: ${credits}`, {
          fontFamily: 'Arial',
          fontSize: '12px',
          color: '#00ff88',
          stroke: '#000000',
          strokeThickness: 2
        }).setOrigin(0.5).setDepth(2001);
        this.worldCamera.ignore(creditsText);
        this.joinGridObjects.push(creditsText);
      } catch (_) {}

      // Record center for emotes during lobby
      this.joinGridMap.set(wallet, { x: cx, y: cy, lastEmoteAt: 0 });
    });
  }

  hashString(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) - h) + s.charCodeAt(i);
      h |= 0;
    }
    return h;
  }

  handleSceneResize(gameSize) {
    // Update camera dimensions
    if (this.uiCamera && gameSize) {
      const { width, height } = gameSize;
      console.log('🎯 Resize detected:', width, 'x', height);
      
      // Force camera to update its dimensions
      this.uiCamera.setSize(width, height);
      try { this.uiCamera.setViewport(0, 0, width, height); } catch (_) {}
      
      // Update UI element positions
      this.repositionUIElements(width, height);
    }
    
    // Old holders overlay resize logic removed - now using new battle flow
  }

  repositionUIElements(width, height) {
    try {
      console.log('🔧 Repositioning UI elements...', {
        width,
        height,
        hasBg: !!this.battleMusicBg,
        hasTitle: !!this.battleMusicTitleText,
        hasPlus: !!this.battleMusicBtnPlus,
        hasMinus: !!this.battleMusicBtnMinus,
        hasLabel: !!this.battleMusicLabel,
        hasMute: !!this.battleMusicBtnMute
      });

      // Reposition music bar background
      if (this.battleMusicBg && this.battleMusicBg.active) {
        this.battleMusicBg.clear();
        const padding = 6;
        const barHeight = 16 + padding * 2;
        this.battleMusicBg.fillStyle(0x000000, 0.55);
        this.battleMusicBg.fillRect(0, height - barHeight, width, barHeight);
        
        // Update interactive area
        this.battleMusicBg.removeInteractive();
        this.battleMusicBg.setInteractive(
          new Phaser.Geom.Rectangle(0, height - barHeight, width, barHeight),
          Phaser.Geom.Rectangle.Contains
        );
        console.log('✓ Music bar background repositioned');
      }
      
      // Reposition music title text (centered)
      if (this.battleMusicTitleText && this.battleMusicTitleText.active) {
        this.battleMusicTitleText.x = width / 2;
        this.battleMusicTitleText.y = height - 6;
        console.log('✓ Music title repositioned to', width / 2, height - 6);
      }
      
      // Reposition music controls (right side)
      const right = width - 8;
      const baseY = height - 10;
      
      if (this.battleMusicBtnPlus && this.battleMusicBtnPlus.active) {
        this.battleMusicBtnPlus.x = right;
        this.battleMusicBtnPlus.y = baseY;
        console.log('✓ Music + button repositioned');
      }
      
      if (this.battleMusicBtnMinus && this.battleMusicBtnMinus.active) {
        this.battleMusicBtnMinus.x = right - 22;
        this.battleMusicBtnMinus.y = baseY;
        console.log('✓ Music - button repositioned');
      }
      
      if (this.battleMusicLabel && this.battleMusicLabel.active) {
        const labelRightX = right - 44;
        this.battleMusicLabel.x = labelRightX;
        this.battleMusicLabel.y = baseY;
        console.log('✓ Music label repositioned');
        
        // Reposition mute button based on label width
        if (this.battleMusicBtnMute && this.battleMusicBtnMute.active) {
          const muteX = labelRightX - (this.battleMusicLabel.width || 32) - 8;
          this.battleMusicBtnMute.x = muteX;
          this.battleMusicBtnMute.y = baseY;
          console.log('✓ Music mute button repositioned');
        }
      }
      
      // Reposition SFX controls (left side)
      const left = 8;
      const baseYLeft = height - 10;
      
      if (this.sfxMuteBtn && this.sfxMuteBtn.active) {
        this.sfxMuteBtn.x = left;
        this.sfxMuteBtn.y = baseYLeft;
      }
      
      if (this.sfxBtnMinus && this.sfxBtnMinus.active) {
        this.sfxBtnMinus.x = left + 56;
        this.sfxBtnMinus.y = baseYLeft;
      }
      
      if (this.sfxBtnPlus && this.sfxBtnPlus.active) {
        this.sfxBtnPlus.x = left + 74;
        this.sfxBtnPlus.y = baseYLeft;
      }
      
      // Reposition playersLeftText if it exists
      if (this.playersLeftText && this.playersLeftText.active) {
        this.playersLeftText.x = width - 45;
        this.playersLeftText.y = 11;
      }
      
      // Reposition match title if it exists
      if (this.matchTitleText && this.matchTitleText.active) {
        this.matchTitleText.x = width / 2;
        this.matchTitleText.y = 20;
      }

      // Reposition player info panel
      if (this.playerInfoBg && this.playerInfoBg.active) {
        const panelWidth = 200;
        const panelHeight = 80;
        const panelX = width - panelWidth - 10;
        const panelY = height - panelHeight - 10;

        // Recreate background graphics
        this.playerInfoBg.clear();
        this.playerInfoBg.fillStyle(0x000000, 0.8);
        this.playerInfoBg.fillRoundedRect(panelX, panelY, panelWidth, panelHeight, 8);
        this.playerInfoBg.lineStyle(2, 0xffffff, 0.5);
        this.playerInfoBg.strokeRoundedRect(panelX, panelY, panelWidth, panelHeight, 8);

        // Update text positions
        if (this.playerInfoName) {
          this.playerInfoName.setPosition(panelX + 10, panelY + 10);
        }
        if (this.playerInfoLevel) {
          this.playerInfoLevel.setPosition(panelX + 10, panelY + 35);
        }
        if (this.playerInfoHealth) {
          this.playerInfoHealth.setPosition(panelX + 10, panelY + 55);
        }
      }

      console.log('✅ UI elements repositioned for new size');
    } catch (error) {
      console.warn('Error repositioning UI elements:', error);
    }
  }

  startBattleMusicIfNeeded() {
    if (this.battleMusic || !this.sound) return;
    const keys = this.battleMusicKeys || [];
    if (!keys.length) return;
    // Initialize or refresh queue if empty
    if (!this.battleMusicQueue || this.battleMusicQueue.length === 0) {
      console.log('Rebuilding battle music queue from keys:', keys);
      let queue = this.shuffleKeys(keys);
      const offset = this.getRandomInt(queue.length || 1);
      if (offset && queue.length > 1) {
        queue = queue.slice(offset).concat(queue.slice(0, offset));
        console.log('Applied random rotation offset', offset, '→', queue);
      }
      this.battleMusicQueue = queue;
      console.log('Shuffled battle music queue:', this.battleMusicQueue);
      // Avoid immediate repeat across matches when 2+ tracks available
      const lastKey = lastBattleMusicKeyGlobal || this.lastBattleMusicKey;
      if (lastKey && this.battleMusicQueue.length > 1 && this.battleMusicQueue[0] === lastKey) {
        const first = this.battleMusicQueue.shift();
        this.battleMusicQueue.push(first);
        console.log('Adjusted queue to avoid immediate repeat:', this.battleMusicQueue);
      }
    }
    const key = this.battleMusicQueue.shift();
    console.log('Selected battle music key:', key);
    this.lastBattleMusicKey = key;
    lastBattleMusicKeyGlobal = key;

    const showTitle = () => {
      // Try to find metadata from manifest
      const meta = (this.battleMusicManifest || []).find(e => e.key === key);
      const title = meta?.title || (key.split('/').pop() || key).replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
      const artist = meta?.artist || 'DaveY';
      const vol = (this.battleMusic && typeof this.battleMusic.volume === 'number') ? this.battleMusic.volume : 0;
      const volLabel = vol > 0 ? ` (vol ${Math.round(vol * 100)}%)` : ' (muted)';
      const textStr = `\u266B ${title} — ${artist}${volLabel}`;
      // Create/update dark translucent bar behind text for readability
      if (!this.battleMusicBg) {
        const padding = 6;
        const barHeight = 16 + padding * 2;
        const g = this.add.graphics();
        g.fillStyle(0x000000, 0.55);
        g.fillRect(0, this.uiCamera.height - barHeight, this.uiCamera.width, barHeight);
        g.setDepth(3002);
        this.worldCamera.ignore(g);
        this.battleMusicBg = g;
        // Make clickable
        try {
          g.setInteractive(new Phaser.Geom.Rectangle(0, this.uiCamera.height - barHeight, this.uiCamera.width, barHeight), Phaser.Geom.Rectangle.Contains);
        } catch (_) {}
      }
      if (!this.battleMusicTitleText) {
        this.battleMusicTitleText = this.add.text(this.uiCamera.width / 2, this.uiCamera.height - 6, textStr, {
          fontFamily: 'Arial',
          fontSize: '14px',
          color: '#D1D5DB',
          stroke: '#000000',
          strokeThickness: 2,
          align: 'center'
        }).setOrigin(0.5, 1).setDepth(3003);
        this.worldCamera.ignore(this.battleMusicTitleText);
        // Controls UI: music [-] [🔊] [+] and SFX [label] [−] [+]
        if (!this._musicUiBound) {
          this._musicUiBound = true;
          // Tiny UI buttons on right
          const baseY = this.uiCamera.height - 10;
          const mkBtn = (label, x, handler) => {
            const t = this.add.text(x, baseY, label, {
              fontFamily: 'Arial',
              fontSize: '14px',
              color: '#E5E7EB',
              stroke: '#000000',
              strokeThickness: 2,
              align: 'center'
            }).setOrigin(1, 1).setDepth(3004);
            this.worldCamera.ignore(t);
            t.setInteractive({ useHandCursor: true });
            t.on('pointerdown', handler);
            return t;
          };
          const right = this.uiCamera.width - 8;
          // Desired order (left->right): [🔊] Music [−] [+]
          this.battleMusicBtnPlus = this.battleMusicBtnPlus || mkBtn('+', right, () => { this.changeBattleMusicVolume(+0.05); });
          // Minus sits just left of plus
          this.battleMusicBtnMinus = this.battleMusicBtnMinus || mkBtn('−', right - 22, () => { this.changeBattleMusicVolume(-0.05); });
          // Label sits just left of minus with small gap
          if (!this.battleMusicLabel) {
            this.battleMusicLabel = this.add.text(right - 44, baseY, 'Music', {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#E5E7EB',
            stroke: '#000000',
            strokeThickness: 2,
              align: 'right'
            }).setOrigin(1, 1).setDepth(3004);
            this.worldCamera.ignore(this.battleMusicLabel);
            // Make label clickable to toggle music mute
            this.battleMusicLabel.setInteractive({ useHandCursor: true });
            this.battleMusicLabel.on('pointerdown', () => {
              this.toggleBattleMusicMute();
              this.refreshBattleMusicTitle();
              this.updateMusicButtonsIcon();
            });
          }
          // Mute icon sits to the left of the label based on label width
          const labelRightX = right - 44;
          this.battleMusicLabel.setX(labelRightX);
          const muteX = labelRightX - (this.battleMusicLabel.width || 32) - 8;
          if (!this.battleMusicBtnMute) {
            this.battleMusicBtnMute = mkBtn('🔊', muteX, () => { this.toggleBattleMusicMute(); this.refreshBattleMusicTitle(); this.updateMusicButtonsIcon(); });
          } else {
            this.battleMusicBtnMute.setX(muteX);
          }
          // Ensure minus/plus are positioned each time (responsive)
          this.battleMusicBtnMinus.setX(right - 22);
          this.battleMusicBtnPlus.setX(right);
          // SFX controls on left side: [SFX] [−] [+]
          const left = 8;
          const baseYLeft = this.uiCamera.height - 10;
          if (!this.sfxMuteBtn) {
            const sfxBtn = this.add.text(left, baseYLeft, this.sfxMuted ? '🔇 SFX' : '🔊 SFX', {
              fontFamily: 'Arial',
              fontSize: '14px',
              color: '#E5E7EB',
              stroke: '#000000',
              strokeThickness: 2,
              align: 'left'
            }).setOrigin(0, 1).setDepth(3004);
            this.worldCamera.ignore(sfxBtn);
            sfxBtn.setInteractive({ useHandCursor: true });
            sfxBtn.on('pointerdown', () => {
              this.toggleSfxMute();
            });
            this.sfxMuteBtn = sfxBtn;
          }
          if (!this.sfxBtnMinus) {
            const minus = this.add.text(left + 56, baseYLeft, '−', {
              fontFamily: 'Arial',
              fontSize: '14px',
              color: '#E5E7EB',
              stroke: '#000000',
              strokeThickness: 2,
              align: 'center'
            }).setOrigin(0, 1).setDepth(3004);
            this.worldCamera.ignore(minus);
            minus.setInteractive({ useHandCursor: true });
            minus.on('pointerdown', () => this.changeSfxBaseVolume(-0.05));
            this.sfxBtnMinus = minus;
          }
          if (!this.sfxBtnPlus) {
            const plus = this.add.text(left + 74, baseYLeft, '+', {
              fontFamily: 'Arial',
              fontSize: '14px',
              color: '#E5E7EB',
              stroke: '#000000',
              strokeThickness: 2,
              align: 'center'
            }).setOrigin(0, 1).setDepth(3004);
            this.worldCamera.ignore(plus);
            plus.setInteractive({ useHandCursor: true });
            plus.on('pointerdown', () => this.changeSfxBaseVolume(+0.05));
            this.sfxBtnPlus = plus;
          }
          this.updateMusicButtonsIcon();
        }
      } else {
        this.battleMusicTitleText.setText(textStr);
      }
    };

    const play = () => {
      if (this.battleMusic || !this.sound) return;
      const s = this.sound.get(key) || this.sound.add(key);
      if (s) {
        s.setLoop(true);
        // Initialize user volume state (persisted)
        const defaultVol = 0.315;
        if (typeof this.battleMusicUserVolume !== 'number') {
          this.battleMusicUserVolume = (typeof audioPrefs.musicUserVol === 'number') ? audioPrefs.musicUserVol : defaultVol;
        }
        const startMuted = this._initialMusicMuted === true || audioPrefs.musicMuted === true;
        s.setVolume(startMuted ? 0 : this.battleMusicUserVolume);
        s.play();
        this.battleMusic = s;
        // Ensure UI created even if called repeatedly
        this.battleMusicTitleText = null;
        this.battleMusicBg = null;
        this.battleMusicBtnPlus = null;
        this.battleMusicBtnMinus = null;
        this.battleMusicBtnMute = null;
        this._musicUiBound = false;
        showTitle();
        this.updateMusicButtonsIcon?.();
      }
    };

    const loadAndPlay = () => {
      if (this.cache.audio && this.cache.audio.exists(key)) {
        play();
      } else {
        this.load.once('complete', play);
        
        // Determine S3 base path for any theme; fallback to local assets if unavailable
        const theme = getCurrentTheme();
        const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
        const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
        const useS3 = !!s3BasePath;

        const musicPath = useS3
          ? `${s3BasePath}/${key.includes('/') ? `music/${key}` : `music/${key}`}`
          : `assets/${key}`;
        
        console.log('🎵 Loading battle music:', { key, musicPath, useS3, s3BasePath });
        this.load.audio(key, musicPath);
        this.load.start();
      }
    };


    if (this.sound.locked) {
      console.log('🎵 Sound is locked, waiting for unlock');
      this.sound.once('unlocked', loadAndPlay);
    } else {
      console.log('🎵 Sound is unlocked, loading and playing');
      loadAndPlay();
    }
  }

  toggleBattleMusicMute() {
    if (!this.battleMusic) return;
    const current = this.battleMusic.volume || 0;
    if (current > 0) {
      // Store user volume and mute
      this.battleMusicUserVolume = current;
      this.battleMusic.setVolume(0);
      audioPrefs.musicMuted = true;
      if (typeof this.battleMusicUserVolume === 'number') audioPrefs.musicUserVol = this.battleMusicUserVolume;
    } else {
      const target = (typeof this.battleMusicUserVolume === 'number' && this.battleMusicUserVolume > 0) ? this.battleMusicUserVolume : 0.315;
      this.battleMusic.setVolume(target);
      audioPrefs.musicMuted = false;
      audioPrefs.musicUserVol = target;
    }
    this.updateMusicButtonsIcon?.();
    this.refreshBattleMusicTitle?.();
  }

  cycleBattleMusicVolume(levels = [0.15, 0.315, 0.5, 0.75]) {
    if (!this.battleMusic || !Array.isArray(levels) || levels.length === 0) return;
    const cur = this.battleMusic.volume || 0;
    // If currently muted, jump to first level
    if (cur === 0) {
      this.battleMusic.setVolume(levels[0]);
      this.battleMusicUserVolume = levels[0];
      return;
    }
    // Find next higher level; wrap to first
    const sorted = [...levels].sort((a, b) => a - b);
    const next = sorted.find(v => v > cur) ?? sorted[0];
    this.battleMusic.setVolume(next);
    this.battleMusicUserVolume = next;
    this.updateMusicButtonsIcon?.();
    this.refreshBattleMusicTitle?.();
  }

  changeBattleMusicVolume(delta = 0.05) {
    if (!this.battleMusic) return;
    const base = (this.battleMusic.volume || this.battleMusicUserVolume || 0.315);
    let next = Math.max(0, Math.min(1, base + delta));
    // Snap tiny values to 0 to make mute reachable
    if (next < 0.02) next = 0;
    this.battleMusicUserVolume = next > 0 ? next : (this.battleMusicUserVolume || 0.315);
    this.battleMusic.setVolume(next);
    audioPrefs.musicMuted = next === 0;
    audioPrefs.musicUserVol = this.battleMusicUserVolume;
    this.updateMusicButtonsIcon?.();
    this.refreshBattleMusicTitle?.();
  }

  updateMusicButtonsIcon() {
    if (!this.battleMusicBtnMute) return;
    const muted = !this.battleMusic || (this.battleMusic.volume || 0) === 0;
    this.battleMusicBtnMute.setText(muted ? '🔇' : '🔊');
  }

  refreshBattleMusicTitle() {
    if (!this.battleMusicTitleText) return;
    const key = this.lastBattleMusicKey;
    if (!key) return;
    const meta = (this.battleMusicManifest || []).find(e => e.key === key);
    const title = meta?.title || (key.split('/').pop() || key).replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ');
    const artist = meta?.artist || 'DaveY';
    const vol = (this.battleMusic && typeof this.battleMusic.volume === 'number') ? this.battleMusic.volume : 0;
    const volLabel = vol > 0 ? ` (vol ${Math.round(vol * 100)}%)` : ' (muted)';
    const textStr = `\u266B ${title} — ${artist}${volLabel}`;
    this.battleMusicTitleText.setText(textStr);
  }

  toggleSfxMute() {
    this.sfxMuted = !this.sfxMuted;
    if (this.sfxManager) {
      if (this.sfxMuted) {
        this.sfxManagerUserBaseVolume = this.sfxManager.baseVolume;
        this.sfxManager.baseVolume = 0;
      } else {
        this.sfxManager.baseVolume = (typeof this.sfxManagerUserBaseVolume === 'number') ? this.sfxManagerUserBaseVolume : 0.3;
      }
    }
    if (this.sfxMuteBtn) this.sfxMuteBtn.setText(this.sfxMuted ? '🔇 SFX' : '🔊 SFX');
    // Persist preference
    audioPrefs.sfxMuted = this.sfxMuted;
    if (typeof this.sfxManagerUserBaseVolume === 'number') audioPrefs.sfxUserVol = this.sfxManagerUserBaseVolume;
  }

  changeSfxBaseVolume(delta = 0.05) {
    // If muted, unmute first to apply change
    if (this.sfxMuted) {
      this.sfxMuted = false;
    }
    const current = (typeof this.sfxManagerUserBaseVolume === 'number') ? this.sfxManagerUserBaseVolume : (this.sfxManager?.baseVolume ?? 0.3);
    let next = Math.max(0, Math.min(1, current + delta));
    if (next < 0.02) next = 0;
    this.sfxManagerUserBaseVolume = next > 0 ? next : (this.sfxManagerUserBaseVolume || 0.3);
    if (this.sfxManager) this.sfxManager.baseVolume = next;
    if (this.sfxMuteBtn) this.sfxMuteBtn.setText(next === 0 ? '🔇 SFX' : '🔊 SFX');
    // Persist preference
    audioPrefs.sfxMuted = next === 0;
    audioPrefs.sfxUserVol = this.sfxManagerUserBaseVolume;
  }


  async startGame() {

    this.lights.enable();
    this.lights.setAmbientColor(0xeeeeee); // optional, global ambient

    // Respect pause: if countdown is paused, do not start
    if (window.__SW_COUNTDOWN_PAUSED__ === true) {
      console.log('[GameScene] Start aborted: countdown paused');
      return;
    }
    // Reset boundary initialization flag for new game
    this.boundaryInitialized = false;

    // Clean up overlay
    if (this.overlayElements) {
      this.overlayElements.forEach(element => {
        if (element && element.destroy) element.destroy();
      });
    }
    if (this.overlayTimerText) {
      this.overlayTimerText.destroy();
    }
    // Clean up join screen leaderboard
    if (this.leaderboardOverlay) {
      this.leaderboardOverlay.hideJoinScreen();
    }
    // Clean up join lobby grid elements if any
    if (this.joinGridObjects && Array.isArray(this.joinGridObjects)) {
      this.joinGridObjects.forEach(o => { try { o.destroy(); } catch (_) {} });
    }
    if (this.joinersText && this.joinersText.destroy) {
      try { this.joinersText.destroy(); } catch (_) {}
    }
    this.joinGridObjects = [];
    this.joinGridArea = null;
    this.overlayElements = [];
    this.joinersText = null;

    // Remove unused variables that conflict with later declarations

    // Use the battlefield that was already selected and loaded in PreBattleScene
    this.currentBattlefield = this.nextBattlefield || BattleStateManager.getBattlefield();
    this.nextBattlefield = null;
    this.nextBattlefieldReady = false;

    if (this.currentBattlefield) {
      // Always create a fresh battlefield background
      if (this.battlefieldBackground) {
        this.battlefieldBackground.destroy();
      }
      let key = this.currentBattlefield.image;
      this.currentBattlefieldKey = key;
      this.loadedBattlefieldKeys.add(key);

      console.log('🎨 GameScene: Using battlefield texture key:', key);
      console.log('🎨 GameScene: Texture exists in cache:', this.textures.exists(key));

      if (!this.textures.exists(key)) {
        key = 'default-battlefield';
      }

      // Position background with origin at top-left to match world coordinates
      // Battlefield image at (0,0) - world coordinates remain (0,0) to (4096,4096)
      // Camera bounds will extend into negative Y space to create visual padding above
      this.battlefieldBackground = this.add.image(0, 0, key);
      this.battlefieldBackground.setOrigin(0, 0); // Top-left origin
      this.battlefieldBackground.setDepth(-1); // Behind everything
      // Battlefield is always 4096x4096
      const worldW = 4096;
      const worldH = 4096;
      //const src = this.textures.get(key)?.getSourceImage?.();
      //const imgW = src?.width || worldW;
      //const imgH = src?.height || worldH;
      //const scaleX = worldW / imgW;
      //const scaleY = worldH / imgH;
      //this.battlefieldBackground.setScale(scaleX, scaleY);
      // Background uses world camera by default
      //console.log('Background: pos(0,0), scaleX:', scaleX, 'scaleY:', scaleY);

      //Set the battlefield background width, height, and scale
      const src = this.textures.get(key)?.getSourceImage?.();
      const imgW = src?.width || worldW;
      const imgH = src?.height || worldH;

      // Preserve aspect ratio: scale to cover, then center (cropping overflow)
      const scale = Math.max(worldW / imgW, worldH / imgH);
      this.battlefieldBackground.setScale(scale);

      // Center the image within the battlefield bounds
      const offsetX = (worldW - imgW * scale) * 0.5;
      const offsetY = (worldH - imgH * scale) * 0.5;
      this.battlefieldBackground.setPosition(offsetX, offsetY);
      
      console.log('[GameScene] Battlefield background positioned:', {
        finalX: offsetX,
        finalY: offsetY,
        scale: scale,
        worldSize: `${worldW}x${worldH}`,
        note: 'Camera bounds extend into negative Y to create padding above'
      });

      console.log('Background: cover scale:', scale, 'offsetX:', offsetX, 'offsetY:', offsetY);

      // Generate and display match title - use custom name if available
      const battlefieldDisplayName = BattleStateManager.getBattlefieldDisplayName?.() || this.currentBattlefield?.name || 'Unknown Battlefield';
      const battlefieldForTitle = { ...this.currentBattlefield, name: battlefieldDisplayName };
      this.matchTitle = generateCampyMatchTitle(battlefieldForTitle);
      if (!this.matchTitleText || !this.matchTitleText.active) {
        // Destroy existing text if it's in a bad state
        if (this.matchTitleText && !this.matchTitleText.active) {
          this.matchTitleText.destroy();
          this.matchTitleText = null;
        }

        this.matchTitleText = this.add.text(this.uiCamera.width / 2, 10, this.matchTitle, {
          fontFamily: 'Arial',
          fontSize: '22px',
          color: '#ffff00',
          stroke: '#000000',
          strokeThickness: 3,
          align: 'center'
        }).setOrigin(0.5).setDepth(1000);
        // Assign to UI camera by making world camera ignore it
        this.worldCamera.ignore(this.matchTitleText);
      } else {
        this.matchTitleText.setText(this.matchTitle);
      }
    }

    //playersLeftText should be at the top right of the screen
    if (!this.playersLeftText) {
      this.playersLeftText = this.add.text(this.uiCamera.width - 45, 11, '', {
        fontFamily: 'Arial',
        fontSize: '22px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(1, 0).setDepth(1003);
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(this.playersLeftText);
    }
    

    // Initialize game elements
    // Create GameManager with battlefield margins and size (small/large)
    const battlefieldMargins = this.currentBattlefield?.margin || null;
    const battlefieldSizeKind = this.currentBattlefield?.size || 'small';
    this.gameManager = new GameManager(this, battlefieldMargins, battlefieldSizeKind);
    
    // Create battle record
    await this.createBattleRecord();
    // Get the active Phaser scene (GameScene) for the IntermissionManager
    const activeScenes = this.phaserGameRef?.current?.scene?.getScenes(true);
    const gameScene = activeScenes?.find(s => s.scene?.key === 'GameScene') || activeScenes?.[0];
    this.intermissionManager = new IntermissionManager(gameScene || this, this.intermissionsData, this.itemsData, this.itemsById);
    // LeaderboardOverlay is already created in create() method, don't recreate it here

    // Initialize SFX manager (after scene is ready and weapons are loaded)
    if (!this.sfxManager && this.weaponsLoaded) {
      this.sfxManager = new SfxManager(this, this.sfxManifest || {});
      this.sfxManager.setZoomBounds(this.initialWorldZoomMin || 0.1, 1.5);
      // Respect SFX mute/base volume state
      const persistedSfxVol = (typeof audioPrefs.sfxUserVol === 'number') ? audioPrefs.sfxUserVol : (this.sfxManager.baseVolume ?? 0.3);
      this.sfxManagerUserBaseVolume = (typeof this.sfxManagerUserBaseVolume === 'number') ? this.sfxManagerUserBaseVolume : persistedSfxVol;
      const startSfxMuted = audioPrefs.sfxMuted === true;
      this.sfxMuted = (typeof this.sfxMuted === 'boolean') ? this.sfxMuted : startSfxMuted;
      this.sfxManager.baseVolume = this.sfxMuted ? 0 : this.sfxManagerUserBaseVolume;
      console.log('SfxManager created with manifest categories:', Object.keys(this.sfxManifest));
    }

    // Initialize Spell manager
    if (!this.spellManager) {
      this.spellManager = new SpellManager(this);
      await this.spellManager.loadSpells();
    }

    if (!this.arrowManager) {
      this.arrowManager = new ArrowManager(this);
    } else if (typeof this.arrowManager.reset === 'function') {
      this.arrowManager.reset();
    }

    // Initialize Taunt manager
    if (!this.tauntManager) {
      this.tauntManager = null;
      console.log('TauntManager initialized');
    }

    this.players = [];
    this.food = [];
    this.deathMarkers = [];
    this.playerGroup = this.add.group(); // Group for z-indexing
    this.gameState = 'playing';
    this.matchStartTime = Date.now(); // Track when the match started
    this.noPlayersTimeoutTriggered = false;
    this.abortedMatchHandled = false;
    this.deathOrderCounter = 0;
    
    // Start fog system when match begins
    if (this.fogManager) {
      console.log('[GameScene] Starting FogManager');
      this.fogManager.start();
    } else {
      console.warn('[GameScene] FogManager not available at match start');
    }

    // Initialize game mode
    console.log('[GameScene] Selecting game mode with selectedGameMode:', this.selectedGameMode);
    const { mode, modeId } = this.gameModeManager.getCurrentMode();
    console.log('[GameScene] getCurrentMode returned:', { modeId, modeName: mode?.name });
    this.gameModeManager.initializeMode(this);
    console.log(`[GameMode] Starting match in ${modeId} mode: ${mode.name}`);
    
    // Ensure game mode display exists (under zoom HUD)
    if (!this.gameModeText || !this.gameModeText.scene) {
      try {
        // Create directly under the zoom HUD (which is at y=8)
        this.gameModeText = this.add.text(8, 24, '', {
          fontFamily: 'Arial',
          fontSize: '12px',
          color: '#00ff88',
          stroke: '#000000',
          strokeThickness: 2
        }).setDepth(10000);
        this.worldCamera.ignore(this.gameModeText);
      } catch (e) {
        console.warn('Failed to create gameModeText, will retry later', e);
      }
    }
    // Update game mode display
    if (this.gameModeText && this.gameModeText.setText) {
      try {
        console.log('Updating game mode display:', mode.name);
        this.gameModeText.setText(`${mode.name}`);
      } catch (e) {
        console.warn('Failed to set game mode text, recreating...', e);
        try {
          if (this.gameModeText && this.gameModeText.destroy) this.gameModeText.destroy();
        } catch (_) {}
        this.gameModeText = this.add.text(8, 24, `Mode: ${mode.name}`, {
          fontFamily: 'Arial',
          fontSize: '12px',
          color: '#00ff88',
          stroke: '#000000',
          strokeThickness: 2
        }).setDepth(10000);
        this.worldCamera.ignore(this.gameModeText);
      }
    }

    // Hide join screen leaderboard when battle starts
    if (this.leaderboardOverlay) {
      this.leaderboardOverlay.hideJoinScreen();
    }

    // Start battle music when the game begins
    this.startBattleMusicIfNeeded();

    console.log('GameState changed to:', this.gameState);


    // Initialize CameraController (battlefield is always 4096x4096)
    // Object scale (from theme_battlefields.object_scale), default 2.0
    // IMPORTANT: Any world-based sizes, offsets, or movement speeds should multiply by this.objectScale
    // so that gameplay and visuals remain proportionally correct across different world sizes
    this.objectScale = Number(this.currentBattlefield?.object_scale);
    if (!this.objectScale || Number.isNaN(this.objectScale)) this.objectScale = 2.0;
    const spawnBounds = this.gameManager?.worldBounds;
    
    // Set initial focus to spawn area center for initial framing
    console.log('[GameScene] Spawn bounds debug:', {
      hasSpawnBounds: !!spawnBounds,
      spawnBoundsType: spawnBounds?.constructor?.name,
      spawnBounds: spawnBounds ? {
        x: spawnBounds.x,
        y: spawnBounds.y,
        width: spawnBounds.width,
        height: spawnBounds.height,
        centerX: spawnBounds.centerX,
        centerY: spawnBounds.centerY
      } : null
    });
    
    if (spawnBounds) {
      this.initialFocusX = spawnBounds.centerX;
      this.initialFocusY = spawnBounds.centerY;
      console.log('[GameScene] Set initialFocusX/Y from spawnBounds:', {
        initialFocusX: this.initialFocusX,
        initialFocusY: this.initialFocusY
      });
    } else {
      // Fallback to battlefield center (2048, 2048)
      this.initialFocusX = 2048;
      this.initialFocusY = 2048;
      console.log('[GameScene] Using fallback battlefield center:', {
        initialFocusX: this.initialFocusX,
        initialFocusY: this.initialFocusY
      });
    }
    
    // Validate camera state before creating CameraController
    if (!this.worldCamera || !this.worldCamera.worldView) {
      console.error('GameScene: worldCamera is in invalid state, cannot create CameraController');
      return;
    }
    
    this.initialWorldZoom = 0.45;
    this.cameraController = new CameraController(this, this.worldCamera);
    this.cameraController.minZoom = this.initialWorldZoomMin || 0.1;
    // init() no longer needs battlefieldSize parameter - always uses 4096x4096
    this.cameraController.init();
    
    // Focus should already be set via initialFocusX/Y in init(), but ensure marker is positioned
    if (spawnBounds && this.cameraController) {
      console.log('[GameScene] Setting focus after init:', {
        spawnCenterX: spawnBounds.centerX,
        spawnCenterY: spawnBounds.centerY,
        currentFocusX: this.cameraController.focusX,
        currentFocusY: this.cameraController.focusY
      });
      this.cameraController.focusX = spawnBounds.centerX;
      this.cameraController.focusY = spawnBounds.centerY;
      console.log('[GameScene] After setting focus:', {
        focusX: this.cameraController.focusX,
        focusY: this.cameraController.focusY,
        markerPosition: this.cameraController.focusMarker ? {
          x: this.cameraController.focusMarker.x,
          y: this.cameraController.focusMarker.y
        } : 'no marker'
      });
      this.cameraController.centerCameraImmediate(); // Immediately position camera and marker
      console.log('[GameScene] After centerCameraImmediate:', {
        focusX: this.cameraController.focusX,
        focusY: this.cameraController.focusY,
        markerPosition: this.cameraController.focusMarker ? {
          x: this.cameraController.focusMarker.x,
          y: this.cameraController.focusMarker.y
        } : 'no marker',
        cameraCenter: this.worldCamera.worldView ? {
          centerX: this.worldCamera.worldView.centerX,
          centerY: this.worldCamera.worldView.centerY
        } : 'no worldView'
      });
    }
    
    // Draw a subtle crosshair at focus to aid early validation (ignored by UI camera)
    if (this.cameraController && this.cameraController.focusMarker) {
      if (this.uiCamera && this.uiCamera.ignore) this.uiCamera.ignore(this.cameraController.focusMarker);
    }
    

    // Create boundary visuals (game world layer)
    this.boundaryGraphics = this.add.graphics();
    this.boundaryGraphics.setDepth(2); // Game world layer: 1-100

    // Create cloud shadow effect using CloudManager
    this.cloudManager = new CloudManager(this, this.currentBattlefield?.size || 'small', this.objectScale || 1.0);
    this.cloudManager.init();

    // Create fog effect manager (starts inactive, will be started when match begins)
    this.fogManager = new FogManager(this, this.currentBattlefield?.size || 'small', this.objectScale || 1.0);
    console.log('[GameScene] FogManager constructed');
    // If the match already transitioned to playing (edge-case due to async), start fog now
    if (this.gameState === 'playing' && !this.fogManager.isActive) {
      console.log('[GameScene] Starting FogManager (constructed late)');
      this.fogManager.start();
    }

    // Mark spawn in progress and create players using game mode system
    this.playersSpawnInProgress = true;
    this.spawnPlayers().catch(error => {
      console.error('❌ Error spawning players:', error);
    });
    
    // Add debug button for quick battle end testing
    //this.createDebugButton();

    // Auto-join credits are consumed in JoinScene when a match starts.
    // Do not decrement here to avoid double consumption.

    // Scale melee range by object scale so engagement distance matches visual size
    if (typeof this.objectScale === 'number' && this.objectScale > 0) {
      this.meleeRange = this.baseMeleeRange * this.objectScale;
      this.rangedRange = this.baseRangedRange * this.objectScale;
    } else {
      this.meleeRange = this.baseMeleeRange;
      this.rangedRange = this.baseRangedRange;
    }

    // Assign mage class to some players
    if (this.spellManager) {
      this.spellManager.assignMages(this.players);
    }

    // Create food
    this.spawnFood();

    // Create loot spawner and spawn initial potions
    this.lootSpawner = new LootSpawner(this);
    const prizePoolConfig = BattleStateManager.getPrizePoolConfig ? BattleStateManager.getPrizePoolConfig() : null;
    this.lootSpawner.setPrizePoolConfig(prizePoolConfig);
    this.lootSpawner.spawnInitial(20);

    // Per-player auto-use timers are scheduled on spawn (staggered)

    // Ensure the UI camera renders ONLY UI by ignoring all world objects
    const worldObjectsForUiIgnore = [];
    if (this.battlefieldBackground) worldObjectsForUiIgnore.push(this.battlefieldBackground);
    if (this.boundaryGraphics) worldObjectsForUiIgnore.push(this.boundaryGraphics);
    if (this.playerGroup) worldObjectsForUiIgnore.push(this.playerGroup);
    if (this.food && this.food.length) worldObjectsForUiIgnore.push(...this.food);
    if (this.deathMarkers && this.deathMarkers.length) worldObjectsForUiIgnore.push(...this.deathMarkers);
    if (worldObjectsForUiIgnore.length) this.uiCamera.ignore(worldObjectsForUiIgnore);

    

    // DEBUG: Draw red border around screen boundaries on UI camera
    /*this.debugGraphics = this.add.graphics();
    this.debugGraphics.lineStyle(3, 0xff0000, 1);
    // Draw border covering the full display area
    const displayWidth = this.uiCamera.width;
    const displayHeight = this.uiCamera.height;
    const offsetX = 0;
    const offsetY = 0;
    this.debugGraphics.strokeRect(offsetX, offsetY, displayWidth, displayHeight);
    this.debugGraphics.setDepth(9999);
    // Assign to UI camera by making world camera ignore it
    this.worldCamera.ignore(this.debugGraphics);*/

    // DEBUG: Add debug text showing camera info on UI camera
    this.debugText = this.add.text(10, 10, '', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#ff0000',
      stroke: '#000000',
      strokeThickness: 2
    }).setDepth(9999);
    // Assign to UI camera by making world camera ignore it
    this.worldCamera.ignore(this.debugText);

    // Game Mode Display
    this.gameModeText = this.add.text(10, 35, '', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#00ff00',
      stroke: '#000000',
      strokeThickness: 2
    }).setDepth(9999);
    // Assign to UI camera by making world camera ignore it
    this.worldCamera.ignore(this.gameModeText);
    
    // Update debug info
    /*this.debugText.setText([
      `World Camera: ${this.worldCamera.width}x${this.worldCamera.height}`,
      `World Zoom: ${this.worldCamera.zoom.toFixed(3)}`,
      `World Center(worldView): ${Math.round(this.worldCamera.worldView.centerX)},${Math.round(this.worldCamera.worldView.centerY)}`,
      `World Scroll: ${Math.round(this.worldCamera.scrollX)},${Math.round(this.worldCamera.scrollY)}`,
      `UI Camera: ${this.uiCamera.width}x${this.uiCamera.height}`,
      `World Bounds: 2048x1536`,
      `Zoom Target: ${this.cameraZoomTarget}`
    ].join('\n'));*/

    // Small zoom HUD in upper left
    if (!this.zoomHud) {
      this.zoomHud = this.add.text(8, 8, '', {
        fontFamily: 'Arial',
        fontSize: '12px',
        color: '#00ff88',
        stroke: '#000000',
        strokeThickness: 2
      }).setDepth(10000);
      this.worldCamera.ignore(this.zoomHud);
    }

    // Listen for resize events to update join screen overlay
    if (!this.resizeListenerAdded) {
      this.scale.on('resize', this.handleSceneResize, this);
      this.resizeListenerAdded = true;
    }

    // Finalize initial world camera framing: ensure bounds expanded and centered
    const isLargeFinal = (this.currentBattlefield?.size === 'large');
    const worldWFinal = isLargeFinal ? 4096 : 2048;
    const worldHFinal = isLargeFinal ? 4096 : 2048;
    const refitZoom = Math.min(this.worldCamera.width / worldWFinal, this.worldCamera.height / worldHFinal);
    this.worldCamera.setZoom(refitZoom);
    const viewWorldW = this.worldCamera.width / refitZoom;
    const viewWorldH = this.worldCamera.height / refitZoom;
    const extraX = Math.max(0, (viewWorldW - worldWFinal) / 2);
    const extraY = Math.max(0, (viewWorldH - worldHFinal) / 2);
    try {
      this.worldCamera.setBounds(-extraX, -extraY, worldWFinal + extraX * 2, worldHFinal + extraY * 2);
    } catch (error) {
      console.warn('GameScene: Error setting final camera bounds:', error);
    }
    this.worldCamera.centerOn(1024, 768);

  }

  abortToJoinScene(reason = 'unknown') {
    if (this.abortedMatchHandled) return;
    this.abortedMatchHandled = true;

    console.warn('[GameScene] Aborting match early', { reason });

    this.gameState = 'aborted';
    this.playersSpawnInProgress = false;

    const cleanupAndReturn = async () => {
      try {
        if (this.gameManager?.currentBattleId) {
          try {
            await this.gameManager.finishBattle('aborted');
          } catch (error) {
            console.error('[GameScene] Failed to mark battle as aborted', error);
          }
          this.gameManager.currentBattleId = null;
        }

        if (this.fogManager?.stop) {
          try { this.fogManager.stop(); } catch (error) {
            console.warn('[GameScene] Failed to stop fog manager during abort', error);
          }
        }

        if (this.battleMusic) {
          try {
            if (this.battleMusic.isPlaying) this.battleMusic.stop();
            this.battleMusic.destroy();
          } catch (error) {
            console.warn('[GameScene] Failed to stop battle music during abort', error);
          }
          this.battleMusic = null;
        }
        if (this.battleMusicTitleText) {
          try { this.battleMusicTitleText.destroy(); } catch (_) {}
          this.battleMusicTitleText = null;
        }
        if (this.battleMusicBg) {
          try { this.battleMusicBg.destroy(); } catch (_) {}
          this.battleMusicBg = null;
        }

        try { BattleStateManager.reset(); } catch (error) {
          console.warn('[GameScene] Failed to reset BattleStateManager during abort', error);
        }
        try { VoteManager.reset(); } catch (_) {}

        const joinPayload = {
          gameMode: this.selectedGameMode || this.gameMode || 'free-for-all',
          teamCount: this.teamCount || 2,
          maxHeroes: this.maxHeroes || undefined
        };

        this.scene.start('JoinScene', joinPayload);
      } finally {
        this.pendingBattleStats = null;
      }
    };

    cleanupAndReturn().catch(error => {
      console.error('[GameScene] Unexpected error while aborting match', error);
      try {
        this.scene.start('JoinScene', {
          gameMode: this.selectedGameMode || this.gameMode || 'free-for-all',
          teamCount: this.teamCount || 2,
          maxHeroes: this.maxHeroes || undefined
        });
      } catch (startError) {
        console.error('[GameScene] Failed to transition to JoinScene after abort error', startError);
      }
    });
  }

  async spawnPlayers() {
    // Guard against spawning while paused (defensive)
    if (window.__SW_COUNTDOWN_PAUSED__ === true) {
      console.log('[GameScene] spawnPlayers aborted: countdown paused');
      this.playersSpawnInProgress = false;
      return;
    }

    // Get player list from BattleStateManager
    const playerList = BattleStateManager.getPlayerList();
    console.log(`🎮 Spawning ${playerList.length} players from BattleStateManager`);
    console.log('[Gamejsx] spawnPlayers playerList:', playerList);

    if (playerList.length === 0) {
      console.error('❌ GameScene: No players available from BattleStateManager! Cannot spawn players.');
      this.playersSpawnInProgress = false;
      return;
    }

    // Initialize PlayerSpawner
    if (!this.playerSpawner) {
      this.playerSpawner = new PlayerSpawner(this);
    }

    // Get available player textures from theme or fallback to defaults
    const playerTextures = this.getAvailablePlayerTextures();
    console.log('🧩 GameScene: available player textures:', playerTextures);

    // For the new battle system, we'll use fallback textures if no theme textures are available
    const fallbackTextures = [
      'easy1','easy2','easy3','easy4','easy5','easy6',
      'elite1','elite2','elite3','elite4','elite5','elite6','elite7','elite8','elite9',
      'expert1','expert2','expert3',
      'hard1','hard2','hard3','hard4',
      'normal1','normal2','normal3','normal4'
    ];

    // Use theme textures if available, otherwise use fallback
    const availableTextures = playerTextures.length > 0 ? playerTextures : fallbackTextures;
    console.log('🧩 GameScene: using textures:', availableTextures);

    // Spawn each player using PlayerSpawner
    for (let i = 0; i < playerList.length; i++) {
      const playerData = playerList[i];
      //const bounds = this.gameManager.worldBounds;
      const bounds = this.gameManager?.spawnBoundsInitial || this.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };

      // Determine spawn position
      let x, y;
      if (playerData.spawnPosition) {
        // Use predefined spawn position if available
        x = playerData.spawnPosition.x;
        y = playerData.spawnPosition.y;
      } else {
        // Random spawn position within bounds
        x = Phaser.Math.Between(bounds.x, bounds.right);
        y = Phaser.Math.Between(bounds.y, bounds.bottom);
      }

      // Determine player texture
      let textureToUse = null;
      let isNFTPlayer = false;

      // Check if player has a hero with image_url
      if (playerData.hero && playerData.hero.image_url) {
        const textureKey = `player_${playerData.hero.id || playerData.wallet || playerData.id}`;
        if (this.textures.exists(textureKey)) {
          textureToUse = textureKey;
          isNFTPlayer = true;
        }
      }

      // Fallback to available textures if no hero sprite found
      if (!textureToUse) {
        textureToUse = availableTextures[i % availableTextures.length];
      }

      // Get player name
      const name = playerData.hero?.name || playerData.name || 'Unknown Hero';
      const displayName = truncateUsername(name, 20);

      // Get wallet address
      //const walletAddress = playerData.wallet || `ai_${playerData.id}`;
      const walletAddress = null;

      // Transform hero data for PlayerSpawner (expects direct properties, not nested in stats)
      let heroData = null;
      if (playerData.hero) {
        //console.log('[Gamejsx] spawnPlayers playerData.hero:', playerData.hero);
        console.log('[Gamejsx] spawnPlayers playerData:', playerData);
        /*
        //Sample playerData.hero object
        {
            "id": "5978939c-e8af-4e3f-858b-06e3098a615b",
            "name": "Shadow",
            "image_url": "https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/elite3.png",
            "level": 32,
            "stats": {
                "strength": 100,
                "endurance": 100,
                "coordination": 100,
                "quickness": 100,
                "focus": 100,
                "self": 100,
                "health": 208,
                "stamina": 242,
                "mana": 193,
                "speed": 100,
                "defense": 66,
                "attack": 100,
                "secondary_spent_health": 58,
                "secondary_spent_stamina": 42,
                "secondary_spent_mana": 43
            },
            "battle_frequency": 11,
            "inventory": [],
            "strategies": {
                "aggression": 50,
                "looting": 50,
                "positioning": 50,
                "melee_ranged": 50,
                "item_use": 50
            }
        }

        */
        const stats = playerData.hero.stats || {};
        heroData = {
          id: playerData.hero.id,
          isJoined: playerData.isJoined,
          isAI: playerData.isAI,
          userId: playerData.userId,
          heroId: playerData.hero.id,
          name: playerData.hero.name,
          image_url: playerData.hero.image_url || null,
          level: playerData.hero.level || 1,
          strength: stats.strength || 10,
          endurance: stats.endurance || 10,
          coordination: stats.coordination || 10,
          quickness: stats.quickness || 10,
          focus: stats.focus || 10,
          self: stats.self || 10,
          health: stats.health || 100,
          stamina: stats.stamina || 100,
          mana: stats.mana || 100,
          speed: stats.speed || 100,
          defense: stats.defense || 100,
          attack: stats.attack || 100,
          secondary_spent_health: stats.secondary_spent_health || 0,
          secondary_spent_stamina: stats.secondary_spent_stamina || 0,
          secondary_spent_mana: stats.secondary_spent_mana || 0,
          stats: stats,
          strategies: playerData.hero.strategies || {},
          inventory: playerData.hero.inventory || [],
          spawnPosition: null // Will be set during spawning
        };
      }

      // Spawn player using PlayerSpawner (handles join battle logic)
      const player = await this.playerSpawner.spawnPlayer(
        x,
        y,
        textureToUse,
        displayName,
        walletAddress,
        heroData,
        playerData.teamId
      );

      player.joinOrder = (typeof playerData.joinOrder === 'number') ? playerData.joinOrder : (typeof playerData.playerNumber === 'number' ? playerData.playerNumber : (this.players.length + 1));
      player.playerNumber = playerData.playerNumber || (this.players.length + 1);
      player.platform = playerData.platform || 'unknown';
      player.isSpecial = !!playerData.isSpecial;

      if(player.sprite) {
        player.sprite.setPipeline('Light2D'); // for images/sprites you want lit
      }

      // Mark as AI if it's an AI player
      if (playerData.isAI) {
        player.isAI = true;
        // AI players could have different behavior/coloring
        if (player.sprite) {
          //player.sprite.setTint(0x888888); // Slightly grayed out for AI
        }
      }

      // Add to game
      this.players.push(player);
      this.playerGroup.add(player.sprite);

      // Make player sprite interactive for info panel
      if (player.sprite) {
        player.sprite.setInteractive({ useHandCursor: true });

        // Add click handler
        player.sprite.on('pointerdown', () => {
          if (this.selectedPlayer === player) {
            // Clicking the same player hides the panel
            this.hidePlayerInfoPanel();
          } else {
            // Clicking a different player shows their info
            this.showPlayerInfoPanel(player);
          }
        });
      }

      // Log player creation
      const playerType = playerData.isAI ? 'AI' : 'Human';
      const spriteType = isNFTPlayer ? 'Hero' : 'Theme';
      //console.log(`👤 Spawned ${playerType} player ${i + 1}/${playerList.length}: ${displayName} - ${spriteType} sprite: ${textureToUse}`);
    }

    console.log(`✅ Spawned ${this.players.length} players total from BattleStateManager`);

    //Make sure all the players have a hero id, or remove them from the game
    this.players = this.players.filter(player => player.hero && player.hero.id);
    console.log(`✅ Removed ${this.players.length} players without a hero id`);
    if (this.players.length === 0) {
      console.error('❌ No players left to join battle');
      this.endMatch();
      return;
    }

    // Join battle batch
    const heroesToJoin = this.players.map(player => ({ heroId: player.hero.id, team: player.teamId, isAI: player.isAI }));
    console.log('✅ Heroes to join:', heroesToJoin);
    const joined = await this.gameManager.joinBattleBatch(heroesToJoin);
    console.log('✅ Joined battle batch:', joined);
    if (!joined) {
      console.error('❌ Failed to join battle batch');
      return;
    }

    // Use game mode system to position players
    this.gameModeManager.spawnPlayers(this, this.players);

    // Mark done spawning
    this.playersSpawnInProgress = false;

    // Populate sidebar now that players are available
    try { this.populatePlayerSidebar(); } catch (_) {}
  }



  spawnFood() {
    // Base scale for world objects (players/food) before applying objectScale
    const baseObjectScale = 0.12;
    if (!this._foodScaleLogged) {
      console.log(`[Food] objectScale=${this.objectScale}, effectiveScale=${(baseObjectScale * (this.objectScale || 1)).toFixed(3)}`);
      this._foodScaleLogged = true;
    }
    if (!this.foodData || this.foodData.length === 0) {
      console.warn('No food data available, using fallback');
      // Fallback to old system if food data not loaded
      for (let i = 0; i < 15; i++) {
        const bounds = this.gameManager.worldBounds;
        const x = Phaser.Math.Between(bounds.x, bounds.right);
        const y = Phaser.Math.Between(bounds.y, bounds.bottom);

        const foodItem = this.add.image(x, y, 'food_melon'); // Default to melon
        foodItem.setScale(baseObjectScale * (this.objectScale || 1));
        foodItem.setDepth(3);
        foodItem.foodData = { healthValue: 25, id: 'food_melon' }; // Default values
        this.food.push(foodItem);
        this.uiCamera.ignore(foodItem);
      }
      return;
    }

    for (let i = 0; i < 30; i++) {
      const bounds = this.gameManager.worldBounds;
      const x = Phaser.Math.Between(bounds.x, bounds.right);
      const y = Phaser.Math.Between(bounds.y, bounds.bottom);

      // Randomly select a food item
      const foodType = Phaser.Utils.Array.GetRandom(this.foodData);
      //console.log('[Gamejsx] spawnFood foodType:', foodType);
      const foodItem = this.add.image(x, y, foodType.spriteKey);
      // Apply food's individual scale multiplier
      const foodScale = foodType.scale || 1.0;
      foodItem.setScale(baseObjectScale * (this.objectScale || 1) * foodScale);
      foodItem.setDepth(3);
      foodItem.foodData = foodType; // Store food data for health value
      this.food.push(foodItem);
      // UI camera should ignore world food items
      this.uiCamera.ignore(foodItem);
    }
  }


  update(time, delta) {
    // Removed showingLeaderboard state handling - now using dedicated VictoryScene flow

    // Old holders overlay logic removed - now using new battle flow with PreBattleScene

    if (this.gameState === 'aborted') {
      return;
    }

    const nowTs = this.time?.now || Date.now();
    if (this.actionFocusActive && this.actionFocusEndAt && nowTs >= this.actionFocusEndAt) {
      this.releaseActionFocus();
    }

    // Update boundary at least once before intermission logic (for initial visual)
    if (this.gameManager && !this.boundaryInitialized) {
      console.log('[Game] updating boundary at least once before intermission logic (for initial visual)');
      this.gameManager.update(0); // Update with 0 delta for initial draw
      this.boundaryInitialized = true;
    }

    // Intermission manager update and pause check
    if (this.zoomHud && this.zoomHud.active && this.worldCamera) {
      try { this.zoomHud.setText(`z ${(this.worldCamera.zoom || 1).toFixed(2)}`); } catch (_) {}
    }

    if (this.intermissionManager) {
    // Guard: if players are still being spawned this tick, skip world updates to avoid zero-alive race
    if (this.playersSpawnInProgress) {
      return;
    }

    /*if (!this.noPlayersTimeoutTriggered && this.gameState === 'playing') {
      const startTs = this.matchStartTime || 0;
      if (startTs > 0 && (Date.now() - startTs) >= 10000) {
        const totalPlayers = Array.isArray(this.players) ? this.players.length : 0;
        const aliveCount = Array.isArray(this.players)
          ? this.players.filter(p => p && p.isAlive).length
          : 0;
        if (totalPlayers === 0 || aliveCount === 0) {
          this.noPlayersTimeoutTriggered = true;
          this.abortToJoinScene('NO_ACTIVE_PLAYERS_AFTER_TIMEOUT');
          return;
        }
      }
    }*/

    const alivePlayers = this.players.filter(p => p.isAlive);
      if (!this.extraLootTriggered && alivePlayers.length > 0 && alivePlayers.length <= 5) {
        this.extraLootTriggered = true;
        if (this.lootSpawner) {
          const lootCount = this.sessionConfig?.lateGameLootCount ?? 20;
          const tokenCount = this.sessionConfig?.lateGameTokenCount ?? 60;
          for (let i = 0; i < lootCount; i++) {
            try { this.lootSpawner.spawnOneRandom(); } catch (_) {}
          }
          try { this.lootSpawner.spawnRandomTokens(tokenCount); } catch (_) {}
        }
      }
      this.intermissionManager.update(delta, alivePlayers.length, this.players);
      // Ensure playersLeftText exists and is active
      if (!this.playersLeftText || !this.playersLeftText.active) {
        // Destroy existing text if it's in a bad state
        if (this.playersLeftText && !this.playersLeftText.active) {
          this.playersLeftText.destroy();
          this.playersLeftText = null;
        }

        // Recreate the players left text
        this.playersLeftText = this.add.text(this.uiCamera.width - 45, -1, '', {
          fontFamily: 'Arial',
          fontSize: '22px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 4
        }).setOrigin(1, 0).setDepth(1003);
        // Assign to UI camera by making world camera ignore it
        this.worldCamera.ignore(this.playersLeftText);
        // Ensure UI camera does not ignore it (no-op unless previously excluded)
        if (this.uiCamera && this.uiCamera.ignore) {
          // uiCamera.ignore only excludes objects; playersLeftText is included by default
        }
      }

      // Update the text content
      this.playersLeftText.setText(`Players Left: ${alivePlayers.length}`);
      if (this.intermissionManager.isPaused()) return; // hard pause world, including boundary shrink
    }

    // Initialize SFX manager if weapons are loaded but manager hasn't been created yet
    if (!this.sfxManager && this.weaponsLoaded && this.sfxManifest) {
      this.sfxManager = new SfxManager(this, this.sfxManifest);
      this.sfxManager.setZoomBounds(this.initialWorldZoomMin || 0.1, 1.5);
      // Respect SFX mute/base volume state
      this.sfxManagerUserBaseVolume = (typeof this.sfxManagerUserBaseVolume === 'number') ? this.sfxManagerUserBaseVolume : (this.sfxManager.baseVolume ?? 0.3);
      if (this.sfxMuted) this.sfxManager.baseVolume = 0;
      else this.sfxManager.baseVolume = this.sfxManagerUserBaseVolume;
      console.log('SfxManager created (late) with manifest categories:', Object.keys(this.sfxManifest));
    }

    // SFX manager cooldowns
    if (this.sfxManager) this.sfxManager.update(delta);

    // Update loot specialization countdown
    if (this.lootSpecializationCountdown && this.lootSpecializationCountdown.active && this.lootSpecializationStartTime) {
      const elapsed = time - this.lootSpecializationStartTime;
      const remaining = Math.max(0, this.lootSpecializationDuration - elapsed);
      const remainingSeconds = Math.ceil(remaining / 1000);
      
      // Only update if the text object is still valid and active
      if (this.lootSpecializationCountdown.active) {
        this.lootSpecializationCountdown.setText(`Loot Specialization: ${remainingSeconds}s remaining`);
        
        // Change color based on remaining time
        if (remainingSeconds <= 3) {
          this.lootSpecializationCountdown.setColor('#EF4444'); // Red
        } else if (remainingSeconds <= 5) {
          this.lootSpecializationCountdown.setColor('#F59E0B'); // Orange
        } else {
          this.lootSpecializationCountdown.setColor('#FFFFFF'); // White
        }
      }
    }

    // Update taunt manager
    if (this.tauntManager) this.tauntManager.update(delta);

    // Update cloud shadows
    if (this.cloudManager) this.cloudManager.update(delta);

    // Update fog system (needs zone boundary from gameManager)
    if (this.fogManager && this.gameManager) {
      this.fogManager.update(delta, this.gameManager.worldBounds);
    }

    // Update boundary (only if GameManager exists)
    if (this.gameManager) {
      this.gameManager.update(delta);
    }

    // Update players (only if GameManager exists)
    if (this.gameManager) {
      this.players.forEach(player => {
        if (player.isAlive) {
          player.update(this.players, this.food, this.gameManager.worldBounds);
        }
      });
    }

    if (this.arrowManager) {
      this.arrowManager.update(delta);
    }

    // Update player info panel if monitoring a player
    this.updatePlayerInfoPanel();

    // Check for combat using game mode system
    this.gameModeManager.checkCombat(this);
    
    // Check for chest combat
    this.checkChestCombat();
    
    // Update chest health bars (follow bobbing animation)
    if (this.lootSpawner && Array.isArray(this.lootSpawner.chests)) {
      this.lootSpawner.chests.forEach(chest => {
        if (chest && typeof chest.update === 'function') {
          chest.update();
        }
      });
    }

    // Check for food collection
    this.checkFoodCollection();
    if (this.lootSpawner) {
      this.lootSpawner.checkLootCollection();
      //this.lootSpawner.checkPoisonCollisions();
    }

    // Update spell system
    if (this.spellManager) {
      this.spellManager.update(delta, this.players);
    }

    // Camera focus + zoom control (guarded if controller not yet ready)
    if (this.cameraController) {
      const alive = this.players.filter(p => p.isAlive);
      if (alive.length === 1) {
        this.cameraController.setPlayerTarget(alive[0]);
      } else if (alive.length > 1 && alive.length <= 5) {
        this.cameraController.setActionTargets(alive);
      } else {
        // Default focus only when not manually overridden or tweening
        if (!this.cameraController.manualFocusActive && !this.cameraController.focusTween) {
          this.cameraController.setFullFocus();
        }
      }
      const boundaryState = this.gameManager?.boundaryState || 'pausing';
      if (this.cameraController && this.cameraController.worldCamera) {
        this.cameraController.update(delta, boundaryState);
      }
    }

    // Check win condition using game mode system (only if game is properly initialized)
    if (this.gameState === 'playing' && this.players && this.players.length > 0 && !this.playersSpawnInProgress) {
      const aliveCount = this.players.filter(p => p.isAlive).length;

      // Voting gates: reopen at ≤20, close at ≤5
      if (!this.votingOpen && !this.votingClosed && aliveCount <= 20) {
        //this.votingOpen = true;
        //try { showMarqueeMessage('🎯 Voting is now open for remaining players!', { color: '#10B981' }, 'high'); } catch (_) {}
      }
      if (!this.votingClosed && aliveCount <= 5) {
        this.votingClosed = true;
        try { VoteManager.closeVoting(); } catch (_) {}
        //try { showMarqueeMessage('🔒 Voting is now closed!', { color: '#FF4444' }, 'high'); } catch (_) {}
      }

      if (!this.finalTenVoteStarted && aliveCount <= 10) {
        this.finalTenVoteStarted = true;
        this.startFinalTenVoting();
      }

      if (this._lastSidebarAliveCount !== aliveCount) {
        this._lastSidebarAliveCount = aliveCount;
        try { this.populatePlayerSidebar(); } catch (_) {}
      }

      const winResult = this.gameModeManager.checkWinCondition(this);
      if (winResult) {
        console.log('🏆 Win condition triggered:', winResult);
        if (winResult.type === 'single') {
          this.endMatch(winResult.winner);
        } else if (winResult.type === 'team') {
          this.endMatch(winResult.winner);
        } else if (winResult.type === 'draw' || winResult.type === 'defeat') {
          this.endMatch(null);
        }
      } else if (aliveCount <= 1) {
        // Log when we have 1 or 0 players but no win result
        console.log('⚠️ No win result despite', aliveCount, 'alive players');
      }

      // Refresh sidebar roster when alive count changes or crosses threshold
      if (this._lastSidebarAliveCount !== aliveCount) {
        this._lastSidebarAliveCount = aliveCount;
        try { this.populatePlayerSidebar(); } catch (_) {}
      }
    }

    // Update sidebar health bars and grey out dead players
    if (Array.isArray(this.sidebarCardObjects) && this.sidebarCardObjects.length) {
      this.sidebarCardObjects.forEach(obj => {
        try {
          if (!obj || !obj.player) return;
          if (obj.resourceBars) {
            const updateBarFill = (key, colorFn) => {
              const entry = obj.resourceBars[key];
              if (!entry?.fill) return;
              const ratio = this.getSidebarResourceRatio(obj.player, key);
              entry.fill.setScale(ratio, 1);
              const color = colorFn ? colorFn(ratio) : entry.fill.fillColor;
              entry.fill.setFillStyle(color);
            };

            updateBarFill('health', (ratio) => this.getSidebarHealthColor(ratio));
            updateBarFill('mana', () => SIDEBAR_MANA_COLOR);
            updateBarFill('stamina', () => SIDEBAR_STAMINA_COLOR);
            updateBarFill('arrows', () => 0xFDE68A);
            
            // Update ultimate background alpha based on charge
            if (obj.resourceBars && obj.resourceBars.ultimate && obj.resourceBars.ultimate.bg) {
              const ultimateRatio = this.getSidebarResourceRatio(obj.player, 'ultimate');
              obj.resourceBars.ultimate.bg.setAlpha(ultimateRatio);
              // Update border alpha when fully charged
              if (ultimateRatio >= 1) {
                obj.resourceBars.ultimate.bg.setStrokeStyle(1, 0xFFD700, 0.8);
              } else {
                obj.resourceBars.ultimate.bg.setStrokeStyle(1, 0xFFD700, 0.3);
              }
            }
            
            // Update main card border to golden when ultimate is fully charged
            if (obj.bg) {
              const ultimateRatio = this.getSidebarResourceRatio(obj.player, 'ultimate');
              if (ultimateRatio >= 1) {
                obj.bg.setStrokeStyle(2, 0xFFD700, 1.0); // Golden outline when fully charged
              } else {
                obj.bg.setStrokeStyle(1, 0x4B5563, 1.0); // Default gray outline
              }
            }
          }
          if (obj.card) {
            obj.card.setAlpha(obj.player.isAlive ? 1 : 0.4);
          }
        } catch (_) {}
      });
    }
  }

  checkCombat() {
    this.players.forEach(p => { p.prevBattling = p.isBattling; p.isBattling = false; });
    for (let i = 0; i < this.players.length; i++) {
      for (let j = i + 1; j < this.players.length; j++) {
        const p1 = this.players[i];
        const p2 = this.players[j];
        if (p1.isAlive && p2.isAlive) {
          const dist = Phaser.Math.Distance.Between(p1.sprite.x, p1.sprite.y, p2.sprite.x, p2.sprite.y);
          if (dist < this.meleeRange) {
            p1.isBattling = true;
            p2.isBattling = true;
            // Combat!
            const now = this.time.now;
            const damage = p1.calculateMeleeDamage();
            // Check if player is stunned - cannot attack while stunned
            const p1Stunned = typeof p1.isStunned === 'function' ? p1.isStunned() : false;
            if (!p1Stunned && now > p1.lastAttackTime + this.getAttackCooldownMs(p1)) {
              p1.lastAttackTime = now;
              if (typeof p1.applyAttackStaminaCost === 'function') {
                p1.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p1.isCharging) p1.isCharging = false;
              const willDie = p2.health <= damage; // Check if this will kill them
              const actualDamage = p2.takeDamage(damage, { critical: !!p1.lastAttackWasCritical });
              p1.damageDealt += actualDamage;
              if (typeof p1.handleDamageDealtForUltimate === 'function') {
                p1.handleDamageDealtForUltimate(actualDamage);
              }

              this.createAttackEffect(p1, p2);
              if (this.sfxManager) {
                this.sfxManager.onAttack(p1);
                this.sfxManager.onHit(p2, actualDamage, willDie);
              }
              if (!p2.isAlive) {
                p1.addElimination(); // Track elimination for p1
                this.createDeathMarker(p2.sprite.x, p2.sprite.y, { popScale: p2.sprite.scale * 10 });
                if (this.sfxManager) this.sfxManager.onDeath(p2);
              }
            }

            // After first attack, re-check alive states to prevent counter-attack on a dead player
            if (!p1.isAlive || !p2.isAlive) {
              continue;
            }
            // Check if player is stunned - cannot attack while stunned
            const p2Stunned = typeof p2.isStunned === 'function' ? p2.isStunned() : false;
            if (!p2Stunned && now > p2.lastAttackTime + this.getAttackCooldownMs(p2)) {
              p2.lastAttackTime = now;
              if (typeof p2.applyAttackStaminaCost === 'function') {
                p2.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p2.isCharging) p2.isCharging = false;
              const damage2 = p2.calculateMeleeDamage();
              const willDie = p1.health <= damage2; // Check if this will kill them
              const actualDamage2 = p1.takeDamage(damage2, { critical: !!p2.lastAttackWasCritical });
              p2.damageDealt += actualDamage2;
              if (typeof p2.handleDamageDealtForUltimate === 'function') {
                p2.handleDamageDealtForUltimate(actualDamage2);
              }

              this.createAttackEffect(p2, p1);
              if (this.sfxManager) {
                this.sfxManager.onAttack(p2);
                this.sfxManager.onHit(p1, actualDamage2, willDie);
              }
              if (!p1.isAlive) {
                p2.addElimination(); // Track elimination for p2
                this.createDeathMarker(p1.sprite.x, p1.sprite.y, { popScale: p1.sprite.scale * 10 });
                if (this.sfxManager) this.sfxManager.onDeath(p1);
              }
            }
          }
        }
      }
    }

    // Post-combat breathing for those who just left battle
    if (this.sfxManager) {
      this.players.forEach(p => {
        if (p.prevBattling && !p.isBattling && p.isAlive) {
          this.sfxManager.onBreath(p);
        }
      });
    }
  }

  checkChestCombat() {
    if (!this.lootSpawner || !Array.isArray(this.lootSpawner.chests)) return;
    
    const players = this.players || [];
    const chests = this.lootSpawner.chests || [];
    
    for (let i = 0; i < players.length; i++) {
      const player = players[i];
      if (!player || !player.isAlive) continue;
      
      for (let j = 0; j < chests.length; j++) {
        const chest = chests[j];
        if (!chest || chest.isDestroyed || !chest.sprite) continue;
        
        const dist = Phaser.Math.Distance.Between(
          player.sprite.x, 
          player.sprite.y, 
          chest.sprite.x, 
          chest.sprite.y
        );
        
        // Allow attacking chests at melee range (same as player combat)
        if (dist < this.meleeRange) {
          player.isBattling = true;
          
          const now = this.time.now;
          const damage = player.calculateMeleeDamage();
          
          // Check attack cooldown
          if (now > player.lastAttackTime + this.getAttackCooldownMs(player)) {
            player.lastAttackTime = now;
            if (typeof player.applyAttackStaminaCost === 'function') {
              player.applyAttackStaminaCost(2);
            }
            // Clear charging flag after first attack
            if (player.isCharging) player.isCharging = false;
            
            // Damage the chest
            const actualDamage = chest.takeDamage(damage);
            player.damageDealt += actualDamage;
            if (typeof player.handleDamageDealtForUltimate === 'function') {
              player.handleDamageDealtForUltimate(actualDamage);
            }
            
            // Create attack effect (player attacking chest)
            this.createAttackEffect(player, { sprite: chest.sprite });
            
            // Play attack sounds
            if (this.sfxManager) {
              this.sfxManager.onAttack(player);
              // Play hit sound for chest
              if (this.sfxManager.onHit) {
                this.sfxManager.onHit({ sprite: chest.sprite }, actualDamage, false);
              }
            }
          }
        }
      }
    }
  }

  checkFoodCollection() {
    const baseObjectScale = 0.1;
    this.players.forEach(player => {
      if (!player.isAlive) return;

      for (let i = this.food.length - 1; i >= 0; i--) {
        const foodItem = this.food[i];
        const dist = Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, foodItem.x, foodItem.y);

        if (dist < 25) {
          // Use the health value from the food data, or default to 15
          const healthValue = foodItem.foodData?.healthValue || 15;
          player.heal(healthValue);
          foodItem.destroy();
          this.food.splice(i, 1);

          // Play random eating sound
          if (this.sfxManager) {
            this.sfxManager.playCategory('food_eating', player, {
              rate: Phaser.Math.FloatBetween(0.9, 1.1),
              volumeMul: 1.2
            });
          }

          // Respawn food elsewhere
          const bounds = this.gameManager.worldBounds;
          const newX = Phaser.Math.Between(bounds.x + 100, bounds.right - 100);
          const newY = Phaser.Math.Between(bounds.y + 100, bounds.bottom - 100);

          // Randomly select a food item for respawn
          const foodType = this.foodData && this.foodData.length > 0
            ? Phaser.Utils.Array.GetRandom(this.foodData)
            : { spriteKey: 'food_melon', healthValue: 25, id: 'food_melon', scale: 1.0 }; // Fallback

          const newFood = this.add.image(newX, newY, foodType.spriteKey);
          // Apply food's individual scale multiplier
          const foodScale = foodType.scale || 1.0;
          newFood.setScale(baseObjectScale * (this.objectScale || 1) * foodScale);
          newFood.setDepth(3);
          newFood.foodData = foodType;
          this.food.push(newFood);
          // UI camera should ignore world food items
          this.uiCamera.ignore(newFood);
          break;
        }
      }
    });
  }

  createDeathMarker(x, y, options = {}) {
    // Array of available bloodpool sprites
    const bloodSprites = ['blood1', 'blood1'];
    
    // Randomly select a blood pool sprite
    const randomBloodSprite = bloodSprites[Math.floor(Math.random() * bloodSprites.length)];
    
    const bloodPoolMarker = this.add.image(x, y, randomBloodSprite);
    bloodPoolMarker.setScale(0.9);
    bloodPoolMarker.setDepth(1);
    
    // Random rotation between -10 and +10 degrees
    const randomBloodRotation = Phaser.Math.Between(-15, 15);
    bloodPoolMarker.setRotation(Phaser.Math.DegToRad(randomBloodRotation));
    
    // Start with alpha 0 and fade in over 5 seconds
    bloodPoolMarker.setAlpha(1);
    this.tweens.add({
      targets: bloodPoolMarker,
      alpha: 0.2,
      duration: 15000,
      ease: 'Power2'
    });
    
    this.deathMarkers.push(bloodPoolMarker);
    // UI camera should ignore world markers
    this.uiCamera.ignore(bloodPoolMarker);

    /*this.time.delayedCall(30000, () => {
      bloodPoolMarker.destroy();
      this.deathMarkers.splice(this.deathMarkers.indexOf(bloodPoolMarker), 1);
    });*/



    //spawn the corpse on the ground
    // Array of available gubbins sprites
    const gubbinsSprites = ['skullbones1', 'skullbones1'];
    
    // Randomly select a gubbins sprite
    const randomBonesSprite = gubbinsSprites[Math.floor(Math.random() * gubbinsSprites.length)];
    
    const bonesMarker = this.add.image(x, y, randomBonesSprite);
    bonesMarker.setScale(0.80);
    bonesMarker.setDepth(2);
    
    // Random rotation between -10 and +10 degrees
    const randomRotation = Phaser.Math.Between(-15, 15);
    bonesMarker.setRotation(Phaser.Math.DegToRad(randomRotation));
    
    // Start with alpha 0 and fade in over 5 seconds
    bonesMarker.setAlpha(1);
    this.tweens.add({
      targets: bonesMarker,
      alpha: 0.3,
      duration: 15000,
      ease: 'Power2'
    });
    
    this.deathMarkers.push(bonesMarker);
    // UI camera should ignore world markers
    this.uiCamera.ignore(bonesMarker);

    // Optional: short skull pop for immediate feedback
    const popScale = options.popScale || 0;
    if (popScale > 0) {
      const finalScale = Math.max(popScale, 0.6);
      //console.log('Creating skull pop with scale: ', finalScale);
      const skullPop = this.add.image(x, y, 'skullbones1');
      skullPop.setDepth(3000);
      skullPop.setScale(finalScale);
      skullPop.setAlpha(1); // force fully visible
      skullPop.setTint(0xFFFFFF);
      skullPop.setBlendMode(Phaser.BlendModes.ADD);
      if (this.uiCamera && this.uiCamera.ignore) {
        this.uiCamera.ignore(skullPop);
      }
      this.tweens.add({
        targets: skullPop,
        scale: finalScale * 1.15,
        duration: 120,
        ease: 'Back.Out',
        onComplete: () => {
          this.deathMarkers.push(skullPop);
          // Fade out over half a second, then destroy
          this.tweens.add({
            targets: skullPop,
            alpha: 0,
            duration: 1000,
            ease: 'Cubic.easeOut',
            onComplete: () => {
              if (skullPop && skullPop.destroy) skullPop.destroy();
            }
          });
        }
      });
    }

    /*this.time.delayedCall(30000, () => {
      bonesMarker.destroy();
      this.deathMarkers.splice(this.deathMarkers.indexOf(bonesMarker), 1);
    });*/


  }

  recordBattleStats(winner) {
    try {
      // Calculate battle duration (from match start to end)
      const nowMs = Date.now();
      const matchStartMs = (typeof this.matchStartTime === 'number' && this.matchStartTime > 0) ? this.matchStartTime : null;
      const battleDurationSeconds = matchStartMs ? Math.max(0, Math.floor((nowMs - matchStartMs) / 1000)) : null;
      const totalPlayers = this.players.length;

      console.log('🔧 DEBUG: Recording battle stats. this.players:', this.players);

      console.log('Recording battle stats:', {
        winner,
        totalPlayers,
        battleDurationSeconds,
        players: this.players.map(p => ({
          heroId: p.heroId,
          userId: p?.userId || null,
          name: p.name,
          isAlive: p.isAlive,
          eliminations: p.eliminations,
          loot: p.loot,
          deathOrder: p.deathOrder,
          deathTimestamp: p.deathTimestamp,
          timeAliveSeconds: p.timeAliveSeconds
        }))
      });

      const winnerPlayers = Array.isArray(winner)
        ? winner.filter(Boolean)
        : (winner ? [winner] : []);

      const isWinnerPlayer = (player) => {
        return winnerPlayers.some(w => {
          if (!w) return false;
          if (w === player) return true;
          if (w.walletAddress && player.walletAddress) {
            return w.walletAddress.trim() === player.walletAddress.trim();
          }
          if (w.userId && player.userId) {
            return w.userId === player.userId;
          }
          return false;
        });
      };

      // Create rankings based on survival and performance
      const playerRankings = this.players.map((player, index) => {
        const isWinnerFlag = isWinnerPlayer(player);
        const survived = player.isAlive || isWinnerFlag;
        const deathOrder = typeof player.deathOrder === 'number' ? player.deathOrder : null;
        const deathTimestamp = typeof player.deathTimestamp === 'number' ? player.deathTimestamp : null;

        let timeAliveSeconds = (typeof player.timeAliveSeconds === 'number' && !Number.isNaN(player.timeAliveSeconds))
          ? player.timeAliveSeconds
          : null;

        if (survived) {
          if (battleDurationSeconds != null) {
            timeAliveSeconds = battleDurationSeconds;
          } else if (matchStartMs) {
            timeAliveSeconds = (nowMs - matchStartMs) / 1000;
          }
        } else if (timeAliveSeconds == null && matchStartMs && deathTimestamp) {
          timeAliveSeconds = (deathTimestamp - matchStartMs) / 1000;
        }

        if (typeof timeAliveSeconds === 'number' && !Number.isNaN(timeAliveSeconds)) {
          timeAliveSeconds = Math.round(Math.max(0, timeAliveSeconds) * 100) / 100;
        } else {
          timeAliveSeconds = null;
        }

        if (typeof timeAliveSeconds === 'number') {
          player.timeAliveSeconds = timeAliveSeconds;
        }

        return {
          player,
          originalIndex: index,
          isWinner: isWinnerFlag,
          eliminations: player.eliminations || 0,
          survived,
          deathOrder,
          timeAlive: timeAliveSeconds,
          damageDealt: player.damageDealt || 0,
          damageTaken: player.damageTaken || 0,
          loot: player.loot || []
        };
      });

      console.log('Player rankings before sort:', playerRankings.map(r => ({
        name: r.player.name,
        heroId: r.player.heroId,
        userId: r.player?.userId || null,
        isWinner: r.isWinner,
        eliminations: r.eliminations,
        survived: r.survived,
        deathOrder: r.deathOrder,
        timeAlive: r.timeAlive
      })));

      // Validation: check if winner is correctly identified
      const winnerRankings = playerRankings.filter(r => r.isWinner);
      const aliveRankings = playerRankings.filter(r => r.survived);
      console.log('Validation:', {
        winnerCount: winnerRankings.length,
        aliveCount: aliveRankings.length,
        winnerRankings: winnerRankings.map(r => r.player.name),
        aliveRankings: aliveRankings.map(r => r.player.name)
      });

      // Sort rankings by winner flag, survival ( death order ), time alive, eliminations
      playerRankings.sort((a, b) => {
        if (a.isWinner && !b.isWinner) return -1;
        if (!a.isWinner && b.isWinner) return 1;

        if (a.survived && !b.survived) return -1;
        if (!a.survived && b.survived) return 1;

        const aOrder = typeof a.deathOrder === 'number' ? a.deathOrder : -Infinity;
        const bOrder = typeof b.deathOrder === 'number' ? b.deathOrder : -Infinity;
        if (aOrder !== bOrder) {
          return bOrder - aOrder; // higher (later) death order first
        }

        const aTime = typeof a.timeAlive === 'number' ? a.timeAlive : (a.survived ? Number.POSITIVE_INFINITY : 0);
        const bTime = typeof b.timeAlive === 'number' ? b.timeAlive : (b.survived ? Number.POSITIVE_INFINITY : 0);
        if (aTime !== bTime) {
          return bTime - aTime;
        }

        const aElims = a.eliminations || 0;
        const bElims = b.eliminations || 0;
        if (aElims !== bElims) {
          return bElims - aElims;
        }

        return 0;
      });

      // Assign final positions
      playerRankings.forEach((ranking, index) => {
        ranking.finalPosition = index + 1;
        if (ranking.player) {
          ranking.player.finalPosition = ranking.finalPosition;
          if (ranking.deathOrder != null) {
            ranking.player.deathOrder = ranking.deathOrder;
          }
          if (typeof ranking.timeAlive === 'number') {
            ranking.player.timeAliveSeconds = ranking.timeAlive;
          }
        }
      });

      /*console.log('Battle rankings after sort:', playerRankings.map(r => ({
        name: r.player.name,
        heroId: r.player.heroId,
        userId: r.player?.userId || null,
        position: r.finalPosition,
        eliminations: r.eliminations,
        winner: r.isWinner,
        survived: r.survived
      })));*/

      // Record battle stats for all players with a heroId  
      const recordablePlayers = playerRankings.filter(ranking => ranking.player.heroId);

      // Don't call recordNewBattleStats immediately - wait for loot delay to complete
      // Store reference for delayed call after loot period
      // Preserve battleId and battleConfig so they're available when recording happens
      this.pendingBattleStats = {
        playerRankings: recordablePlayers,
        battleDurationSeconds,
        totalPlayers,
        battleId: this.gameManager.currentBattleId, // Preserve battleId for later use
        battleConfig: this.gameManager.battleConfig // Preserve battleConfig for later use
      };

      //Grab the top 10 players for the ranking/rewards screen
      const top10Players = playerRankings.slice(0, 10);

      // Store rankings for join screen display
      this.lastBattleRankings = top10Players;

      return playerRankings;
    } catch (error) {
      console.error('Error recording battle stats:', error);
      return [];
    }
  }

  async createBattleRecord() {
    try {
      const theme = getCurrentTheme();
      const { mode } = this.gameModeManager.getCurrentMode();
      const bf = BattleStateManager.getBattlefield();
      
      // Get stat normalization config from BattleStateManager (set by JoinScene)
      const battleStateConfig = BattleStateManager.getBattleConfig();
      const statNormalization = battleStateConfig?.statNormalization || {
        enabled: true,
        strategy: 'hardCap',
        cap: 50, // Default to casual mode
        debug: false
      };

      // Use battlefield from BattleStateManager (which is updated when selected in config modal)
      // Fall back to this.currentBattlefield if BattleStateManager doesn't have one
      const battlefield = bf || this.currentBattlefield;
      

      console.log('🔧 DEBUG: Creating battle record. battlefield:', battlefield);
      const battleConfig = {
        battlefield: battlefield?.name || 'Unknown',
        battlefieldSize: battlefield?.size || 'small',
        battlefieldMargins: battlefield?.margin || null,
        gameMode: mode?.name || 'Unknown',
        teamCount: this.teamCount,
        maxHeroes: this.maxHeroes,
        timestamp: new Date().toISOString(),
        statNormalization
      };

      const prizePoolConfig = BattleStateManager.getPrizePoolConfig ? BattleStateManager.getPrizePoolConfig() : null;
      if (prizePoolConfig && Array.isArray(prizePoolConfig.tokens) && prizePoolConfig.tokens.length) {
        battleConfig.prizePool = prizePoolConfig;
      }

      // Get current session ID from BattleStateManager or window
      const currentSessionId = BattleStateManager.getSessionId() || window.__SW_SESSION_ID__ || null;
      
      // Get battlefield instance ID from BattleStateManager (set during join phase or config modal)
      const battlefieldInstanceId = BattleStateManager.getBattlefieldInstanceId();
      console.log('🔧 DEBUG: Battlefield instance ID from BattleStateManager:', battlefieldInstanceId);
      
      const battleId = await this.gameManager.createBattle(
        theme?.id || 'default',
        mode?.id || 'free-for-all',
        this.maxHeroes || 8,
        battleConfig,
        bf?.id || null,
        currentSessionId,
        battlefieldInstanceId
      );

      if (battleId) {
        console.log('✅ Battle record created:', battleId);
        // Update BattleStateManager with the real battle ID from server
        BattleStateManager.setBattleId(battleId);
        // Store battle config in BattleStateManager for access by PlayerSpawner
        if (BattleStateManager.setBattleConfig) {
          BattleStateManager.setBattleConfig(battleConfig);
        }
      } else {
        console.warn('⚠️ Failed to create battle record');
      }
    } catch (error) {
      console.error('Error creating battle record:', error);
    }
  }

  async recordNewBattleStats(playerRankings, battleDurationSeconds, totalPlayers) {
    try {

      

      console.log('[recordNewBattleStats] Recording stats for each player');
      // Record stats for each player using the new battle tracking system
      /*for (const ranking of playerRankings) {
        if (ranking.player.heroId) {
          const stats = {
            finalPosition: ranking.finalPosition,
            eliminations: ranking.eliminations,
            damageDealt: ranking.player.damageDealt || 0,
            damageTaken: ranking.player.damageTaken || 0,
            survived: ranking.player.survived,
            joinedManually: !!ranking.player.isJoined,
            defeatedOpponentsLevels: Array.isArray(ranking.defeatedOpponentsLevels) ? ranking.defeatedOpponentsLevels : [],
            loot: ranking.player.loot || []
          };

          const xp = await this.gameManager.recordHeroStats(ranking.player.heroId, stats);
          if (typeof xp === 'number') {
            ranking.xpEarned = xp;
          }
        }
      }*/


      //create a minimal playerRankings object for the server to record stats for
      const serverPlayerRankings = playerRankings.map(ranking => {
        const deathOrderValue = (typeof ranking.deathOrder === 'number' && !Number.isNaN(ranking.deathOrder))
          ? ranking.deathOrder
          : null;
        let timeAliveValue = null;
        if (typeof ranking.timeAlive === 'number' && !Number.isNaN(ranking.timeAlive)) {
          timeAliveValue = Number(ranking.timeAlive.toFixed(2));
        }

        return {
          heroId: ranking.player.heroId,
          stats: {
            finalPosition: ranking.finalPosition,
            eliminations: ranking.eliminations,
            damageDealt: ranking.player.damageDealt || 0,
            damageTaken: ranking.player.damageTaken || 0,
            loot: ranking.player.loot || [],
            deathOrder: deathOrderValue,
            timeAlive: timeAliveValue,
            survived: !!ranking.survived,
            joinedManually: !!ranking.player?.hero?.isJoined
          }
        };
      });

      //remove the ranking.player object from the serverPlayerRankings
      serverPlayerRankings.forEach(ranking => {
        delete ranking.player;
      });

      console.log('[recordNewBattleStats] serverPlayerRankings:', serverPlayerRankings);
      console.log('[recordNewBattleStats] ******************** in recordNewBattleStats ********************');

      // Get remaining loot on the battlefield
      const battlefieldRemainingLoot = this.lootSpawner ? this.lootSpawner.getRemainingLoot() : [];
      console.log('[recordNewBattleStats] Remaining battlefield loot:', battlefieldRemainingLoot);

      // Get battlefield instance ID - try from battle config or battle record
      // Note: For now, we'll pass null if not available. The server will handle battlefield instance lookup if needed.
      const battlefieldInstanceId = this.gameManager.battlefieldInstanceId || 
                                    this.gameManager.battleConfig?.battlefieldInstanceId || 
                                    null;

      //Send the battle results to the server, and get back the XP earned for each player
      // Pass original playerRankings (with player objects) for inventory building, and serverPlayerRankings for stats
      const battleResult = await this.gameManager.recordAndFinishBattle(
        serverPlayerRankings,
        battleDurationSeconds,
        totalPlayers,
        'completed',
        battlefieldRemainingLoot,
        battlefieldInstanceId,
        playerRankings // Pass original playerRankings with player objects for inventory
      );
      if (battleResult && battleResult.heroResults) {
        console.log('[recordNewBattleStats]Battle finished');
        console.log('[recordNewBattleStats] XP results:', battleResult);

        const heroXpMap = new Map();
        battleResult.heroResults.forEach(result => {
          if (result && result.heroId) {
            heroXpMap.set(result.heroId, result.xpEarned ?? 0);
          }
        });

        playerRankings.forEach(ranking => {
          const heroId = ranking?.player?.heroId;
          if (!heroId) return;
          const xp = heroXpMap.get(heroId);
          if (typeof xp === 'number') {
            ranking.xpEarned = xp;
            if (ranking.player) {
              ranking.player.xpEarned = xp;
            }
          }
        });

        this.lastBattleResults = battleResult;
        try { BattleStateManager.setBattleRewards(battleResult); } catch (_) {}
        try { BattleStateManager.setRewardsScript(null); } catch (_) {}
      } else {
        console.log('[recordNewBattleStats]Battle finished');
        console.error('[recordNewBattleStats] Failed to get XP earned');
      }


      // Finish the battle
      /*console.log('[recordNewBattleStats] Finishing battle');
      const finished = await this.gameManager.finishBattle('completed');
      if (finished) {
        console.log('recordNewBattleStats]Battle finished');
      } else {
        console.error('[recordNewBattleStats] Failed to finish battle');
      }*/

      
    } catch (error) {
      console.error('Error recording new battle stats:', error);
    }
  }

  calculateXPEarned(finalPosition, eliminations) {
    // Simple XP calculation - can be enhanced later
    let xp = 0;
    
    // Base XP for eliminations
    xp += eliminations * 10;
    
    // Position bonus
    if (finalPosition === 1) {
      xp += 100; // Winner bonus
    } else if (finalPosition <= 3) {
      xp += 50; // Top 3 bonus
    } else if (finalPosition <= 10) {
      xp += 25; // Top 10 bonus
    }
    
    return xp;
  }

  calculateLootDelay(winners) {
    // Check if any winner has a lootDelay property
    let maxLootDelay = 0;
    
    winners.forEach(winner => {
      if (winner && winner.hero && winner.hero.lootDelay) {
        maxLootDelay = Math.max(maxLootDelay, winner.hero.lootDelay);
      }
    });
    
    // Use the greater of GameScene's lootDelay or the highest winner's lootDelay
    const gameSceneLootDelay = this.lootDelay || 0;
    return Math.max(gameSceneLootDelay, maxLootDelay);
  }

  activateLootDelay(winners, delayMs) {
    // Store original loot strategies for restoration
    this.originalLootStrategies = new Map();

    // Stop fog during loot delay period
    if (this.fogManager) {
      this.fogManager.stop();
      console.log('🌫️ Fog disabled during loot delay period');
    }

    // Set winners' loot strategy to 100 for maximum looting
    // AND apply speed boost for post-battle looting
    winners.forEach(winner => {
      if (winner && winner.isAlive) {
        // Store original strategy
        this.originalLootStrategies.set(winner.id, winner.strategyLooting);

        // Set to maximum looting
        winner.strategyLooting = 100;
        
        // Apply speed boost for post-battle loot phase (50% speed increase)
        if (winner.applySpeedModifier && typeof winner.applySpeedModifier === 'function') {
          winner.applySpeedModifier('postBattleLoot', 1.5, delayMs);
        }
        
        console.log(`💰 Set ${winner.name}'s loot strategy to 100 and applied speed boost for ${delayMs}ms`);
      }
    });

    // Create loot specialization UI
    this.createLootSpecializationUI(delayMs);

    // Restore original strategies after delay
    this.time.delayedCall(delayMs, () => {
      this.restoreLootStrategies(winners);
      this.destroyLootSpecializationUI();

      // Restart fog after loot delay ends
      if (this.fogManager) {
        this.fogManager.start();
        console.log('🌫️ Fog restarted after loot delay period');
      }
    });
  }

  restoreLootStrategies(winners) {
    if (!this.originalLootStrategies) return;
    
    winners.forEach(winner => {
      if (winner && this.originalLootStrategies.has(winner.id)) {
        const originalStrategy = this.originalLootStrategies.get(winner.id);
        winner.strategyLooting = originalStrategy;
        console.log(`💰 Restored ${winner.name}'s loot strategy to ${originalStrategy}`);
      }
    });
    
    this.originalLootStrategies.clear();
  }

  createLootSpecializationUI(delayMs) {
    const { width, height } = this.cameras.main;
    
    // Create background panel
    this.lootSpecializationBg = this.add.rectangle(width / 2, height * 0.15, 400, 120, 0x1E293B);
    this.lootSpecializationBg.setStrokeStyle(3, 0xF59E0B);
    this.lootSpecializationBg.setAlpha(0.95);
    this.lootSpecializationBg.setDepth(1000);
    
    // Create title text
    this.lootSpecializationTitle = this.add.text(width / 2, height * 0.12, 'Hero Loot Specialization Activated', {
      fontSize: '24px',
      color: '#F59E0B',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(1001);
    
    // Create countdown text
    this.lootSpecializationCountdown = this.add.text(width / 2, height * 0.18, '', {
      fontSize: '20px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1001);
    
    // Ensure loot specialization UI is visible on UI camera and hidden from world camera
    if (this.worldCamera && this.worldCamera.ignore) {
      this.worldCamera.ignore([this.lootSpecializationBg, this.lootSpecializationTitle, this.lootSpecializationCountdown]);
    }
    
    // Store start time for countdown
    this.lootSpecializationStartTime = this.time.now;
    this.lootSpecializationDuration = delayMs;
    
    // Fade in animation
    this.lootSpecializationBg.setAlpha(0);
    this.lootSpecializationTitle.setAlpha(0);
    this.lootSpecializationCountdown.setAlpha(0);
    
    this.tweens.add({
      targets: [this.lootSpecializationBg, this.lootSpecializationTitle, this.lootSpecializationCountdown],
      alpha: 1,
      duration: 500,
      ease: 'Power2'
    });
  }

  destroyLootSpecializationUI() {
    if (this.lootSpecializationBg) {
      this.tweens.add({
        targets: [this.lootSpecializationBg, this.lootSpecializationTitle, this.lootSpecializationCountdown],
        alpha: 0,
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          if (this.lootSpecializationBg) this.lootSpecializationBg.destroy();
          if (this.lootSpecializationTitle) this.lootSpecializationTitle.destroy();
          if (this.lootSpecializationCountdown) this.lootSpecializationCountdown.destroy();
          
          // Clear references to prevent update loop from accessing destroyed objects
          this.lootSpecializationBg = null;
          this.lootSpecializationTitle = null;
          this.lootSpecializationCountdown = null;
          this.lootSpecializationStartTime = null;
          this.lootSpecializationDuration = null;
        }
      });
    }
  }

  endMatch(winner = null) {
    // Handle both single winner and team winner (array)
    // If winner is an array (team victory), use the first survivor for celebration
    const singleWinner = Array.isArray(winner) ? winner[0] : winner;

    
    

    // Record battle statistics for players and get rankings
    const playerRankings = this.recordBattleStats(singleWinner);

    console.log('Player Rankings:', playerRankings.map(r => ({
      name: r.player.name,
      heroId: r.player.heroId,
      userId: r.player?.userId || null,
      position: r.finalPosition,
      eliminations: r.eliminations,
      winner: r.isWinner,
      survived: r.survived
    })));

    // Store battle results in BattleStateManager for scene access
    BattleStateManager.setWinners(winner ? (Array.isArray(winner) ? winner : [winner]) : []);
    BattleStateManager.setBattleResults({
      playerRankings,
      battleDuration: this.matchStartTime ? Math.floor((Date.now() - this.matchStartTime) / 1000) : 0,
      totalPlayers: this.players.length,
      winner: singleWinner
    });

    // Stop fog system when match ends
    if (this.fogManager) {
      this.fogManager.stop();
    }

    //Expand the world bounds to the full screen
    this.gameManager.worldBounds.width = this.cameras.main.width;
    this.gameManager.worldBounds.height = this.cameras.main.height;
    this.gameManager.worldBounds.x = 0;
    this.gameManager.worldBounds.y = 0;


    // Transition to VictoryScene with battle results
    console.log('🏆 Transitioning to VictoryScene with winner:', singleWinner?.name);
    // Calculate total eliminations
    const totalEliminations = playerRankings.reduce((total, ranking) => total + (ranking.eliminations || 0), 0);
    
    // Create battle results object
    const battleResults = {
      playerRankings,
      duration: this.matchStartTime ? Math.floor((Date.now() - this.matchStartTime) / 1000) : 0,
      battleDuration: this.matchStartTime ? Math.floor((Date.now() - this.matchStartTime) / 1000) : 0, // Keep both for compatibility
      totalEliminations,
      totalPlayers: this.players.length,
      gameMode: this.gameMode || 'Battle Royale'
    };
    
    // Store battle results in BattleStateManager
    BattleStateManager.setBattleResults(battleResults);

    //console.log('[Game.jsx endMatch] ******************** in endMatch ********************');

    // Don't clear currentBattleId yet - we need it for the delayed recordNewBattleStats call
    // It will be cleared after recordNewBattleStats completes
    
    // Check for loot delay system - allow winners to continue looting
    const winners = winner ? (Array.isArray(winner) ? winner : [winner]) : [];
    const calculatedLootDelay = this.calculateLootDelay(winners);
    
    // Calculate total delay: loot delay + victory delay
    const totalDelay = calculatedLootDelay + this.victoryDelay;
    
    if (calculatedLootDelay > 0) {
      console.log(`💰 Loot delay activated: ${calculatedLootDelay}ms for winners to continue looting`);
      this.activateLootDelay(winners, calculatedLootDelay);
      
      // Schedule battle stats recording AFTER loot delay completes
      // This ensures post-battle loot is included
      // Note: ranking.player is a reference, so ranking.player.loot will already have updated values
      this.time.delayedCall(calculatedLootDelay, () => {
        if (this.pendingBattleStats) {
          // Restore battleId and battleConfig before recording (they were preserved in pendingBattleStats)
          if (this.pendingBattleStats.battleId && !this.gameManager.currentBattleId) {
            this.gameManager.currentBattleId = this.pendingBattleStats.battleId;
          }
          if (this.pendingBattleStats.battleConfig && !this.gameManager.battleConfig) {
            this.gameManager.battleConfig = this.pendingBattleStats.battleConfig;
          }
          
          // Log loot counts for debugging
          console.log('[endMatch] Recording battle stats after loot delay period, battleId:', this.gameManager.currentBattleId);
          this.pendingBattleStats.playerRankings.forEach(ranking => {
            if (ranking.player) {
              const lootCount = Array.isArray(ranking.player.loot) ? ranking.player.loot.length : 0;
              console.log(`[endMatch] Player ${ranking.player.name} has ${lootCount} loot items`);
            }
          });
          
          // Store battleId before clearing pendingBattleStats for cleanup check
          const savedBattleId = this.pendingBattleStats.battleId;
          
          this.recordNewBattleStats(
            this.pendingBattleStats.playerRankings,
            this.pendingBattleStats.battleDurationSeconds,
            this.pendingBattleStats.totalPlayers
          );
          
          // Clear the pending stats
          this.pendingBattleStats = null;
          
          // Ensure battleId is cleared after recording (recordAndFinishBattle should clear it, but do it here too for safety)
          // Only clear if it matches what we restored (to avoid clearing a new battle if one started)
          if (this.gameManager.currentBattleId === savedBattleId) {
            this.gameManager.currentBattleId = null;
            this.gameManager.battleConfig = null;
          }
        }
      });
    } else {
      // No loot delay - record stats immediately
      if (this.pendingBattleStats) {
        // Restore battleId and battleConfig if needed (they should already be set, but just in case)
        if (this.pendingBattleStats.battleId && !this.gameManager.currentBattleId) {
          this.gameManager.currentBattleId = this.pendingBattleStats.battleId;
        }
        if (this.pendingBattleStats.battleConfig && !this.gameManager.battleConfig) {
          this.gameManager.battleConfig = this.pendingBattleStats.battleConfig;
        }
        
        this.recordNewBattleStats(
          this.pendingBattleStats.playerRankings,
          this.pendingBattleStats.battleDurationSeconds,
          this.pendingBattleStats.totalPlayers
        );
        this.pendingBattleStats = null;
      }
      
      // Clear battleId and battleConfig after immediate recording
      this.gameManager.currentBattleId = null;
      this.gameManager.battleConfig = null;
    }
    
    // Start VictoryScene but keep GameScene running in background
    // Schedule for loot delay + victory delay to allow winners to loot first
    console.log(`🏆 Victory screen scheduled in ${totalDelay}ms (${calculatedLootDelay}ms loot delay + ${this.victoryDelay}ms victory delay)`);
    this.time.delayedCall(totalDelay, () => {
      // Update state to 'victory' before launching VictoryScene
      sessionStateManager.updateState('victory');
      
      this.scene.launch('VictoryScene', {
        winners: winner ? (Array.isArray(winner) ? winner : [winner]) : [],
        battleResults
      });
    });

    // Stop battle music when match ends
    if (this.battleMusic) {
      if (this.battleMusic.isPlaying) this.battleMusic.stop();
      this.battleMusic.destroy();
      this.battleMusic = null;
    }
    if (this.battleMusicTitleText) {
      this.battleMusicTitleText.destroy();
      this.battleMusicTitleText = null;
    }
    if (this.battleMusicBg) {
      this.battleMusicBg.destroy();
      this.battleMusicBg = null;
    }
    // Ensure all music UI controls are torn down so they reliably rebuild next match
    if (this.battleMusicBtnPlus) {
      this.battleMusicBtnPlus.destroy();
      this.battleMusicBtnPlus = null;
    }
    if (this.battleMusicBtnMinus) {
      this.battleMusicBtnMinus.destroy();
      this.battleMusicBtnMinus = null;
    }
    if (this.battleMusicBtnMute) {
      this.battleMusicBtnMute.destroy();
      this.battleMusicBtnMute = null;
    }
    if (this.battleMusicLabel) {
      this.battleMusicLabel.destroy();
      this.battleMusicLabel = null;
    }
    // SFX controls (left side)
    if (this.sfxMuteBtn) {
      this.sfxMuteBtn.destroy();
      this.sfxMuteBtn = null;
    }
    if (this.sfxBtnMinus) {
      this.sfxBtnMinus.destroy();
      this.sfxBtnMinus = null;
    }
    if (this.sfxBtnPlus) {
      this.sfxBtnPlus.destroy();
      this.sfxBtnPlus = null;
    }
    // Force rebind of the UI control cluster on next music start
    this._musicUiBound = false;

    /*if(doPrizes && singleWinner) {
      // Send reward to winner
      const winnerWallet = singleWinner.walletAddress; // Use the full wallet address
      //const winnerWallet = "Gqok5DgZwZBU9RQ8qPQx7r8zy2aVmco7ifBGTCcRMn9q";
      console.log(`Match ended! Sending ${prizeAmount} SOL reward to winner: ${winnerWallet}`);
      fetch('/api/rewards/send-sol', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          winnerWallet: winnerWallet,
          solAmount: prizeAmount
        })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log('Reward sent successfully:', data);
        } else {
          console.error('Failed to send reward:', data.error);
        }
      })
      .catch(error => {
        console.error('Error sending reward:', error);
      });
    }*/

    // Camera movement disabled - keeping static view
    /*if (singleWinner) {
      // --- Winner Celebration ---
      // 1. Spotlight Effect (overlay layer)
      const spotlight = this.add.graphics({ fillStyle: { color: 0x000000, alpha: 0.8 } });
      spotlight.fillRect(0, 0, 1440, 900);
      spotlight.setDepth(2000); // Overlay layer: 2000+
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(spotlight);
      const maskShape = this.make.graphics();
      const mask = new Phaser.Display.Masks.GeometryMask(this, maskShape);
      spotlight.setMask(mask);
      // Animate the spotlight focusing on the winner
      this.tweens.add({
          targets: maskShape,
          x: singleWinner.sprite.x,
          y: singleWinner.sprite.y,
          onStart: () => {
               maskShape.fillCircle(singleWinner.sprite.x, singleWinner.sprite.y, 300);
          },
          scale: 0.3,
          duration: 800,
          ease: 'Cubic.easeOut'
      });
      // 2. Confetti Shower (overlay layer)
      const particles = this.add.particles(0, 0, 'confetti', {
        x: 0,
        y: 0,
        speed: { min: 100, max: 300 },
        angle: { min: 45, max: 135 },
        lifespan: 4000,
        scale: { min: 0.5, max: 1.5 },
        rotate: { start: 0, end: 360, random: true },
        gravityY: 200,
        emitting: false,
        tint: [0xff4444, 0x44ff44, 0x4444ff, 0xffff44, 0xff44ff, 0x44ffff],
      });
      particles.setDepth(2002); // Overlay layer: 2000+
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(particles);
      particles.emitParticleAt(this.uiCamera.width / 2, -50, 200);
      // Show match ended screen (overlay layer)
      const endScreen = this.add.image(this.uiCamera.width / 2, this.uiCamera.height / 2, 'matchEndedScreen');
      endScreen.setScale(0.6);
      endScreen.setDepth(2000); // Overlay layer: 2000+
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(endScreen);
      endScreen.setVisible(false);
      // Winner text with prize amount (overlay layer)
      const winnerText = this.add.text(this.uiCamera.width / 2, (this.uiCamera.height / 2) - 120, `${singleWinner.name} WINS!`, {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: '36px',
        color: '#FFD700',
        stroke: '#000000',
        strokeThickness: 4,
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2001); // Overlay layer: 2000+
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(winnerText);

      // Prize amount text (overlay layer)
      const prizeText = this.add.text(this.uiCamera.width / 2, (this.uiCamera.height / 2) - 80, `🏆 PRIZE: ${prizeAmount} SOL 🏆`, {
        fontFamily: 'Comic Sans MS, cursive',
        fontSize: '24px',
        color: '#00FF00',
        stroke: '#000000',
        strokeThickness: 3,
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2001); // Overlay layer: 2000+
      // Assign to UI camera by making world camera ignore it
      this.worldCamera.ignore(prizeText);
    } else {
      // No winner - show draw message
      const drawText = this.add.text(this.uiCamera.width / 2, this.uiCamera.height / 2 - 50, 'MATCH DRAW - NO SURVIVORS!', {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: '32px',
        color: '#FF6B6B',
        stroke: '#000000',
        strokeThickness: 4,
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(2001);
      this.worldCamera.ignore(drawText);
    }
    */

    // Countdown text (overlay layer)
    /*
    this.countdownText = this.add.text(this.uiCamera.width / 2, (this.uiCamera.height / 2) + 70, 'Match ends in 10...', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '20px',
      color: '#FFFF00',
      stroke: '#000000',
      strokeThickness: 2,
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(2001); // Overlay layer: 2000+
    // Assign to UI camera by making world camera ignore it
    this.worldCamera.ignore(this.countdownText);

    // Update countdown
    this.time.addEvent({
      delay: 1000,
      repeat: 9,
      callback: () => {
        const remaining = Math.ceil((10000 - this.matchEndTimer) / 1000);
        if (this.countdownText && this.countdownText.active) {
          this.countdownText.setText(`Match ends in ${remaining}...`);
        } else if (this.countdownText && !this.countdownText.active) {
          this.countdownText = null;
        }
      }
    });
    */

    // CRITICAL: Set game state to 'ended' to prevent infinite loop
    this.gameState = 'ended';
    console.log('🏁 Game state set to ended - preventing win condition loop');
  }

  restartMatch() {
    // Hide leaderboard if still visible
    if (this.leaderboardOverlay) {
      this.leaderboardOverlay.hide();
    }

    // Store current camera position to preserve it after restart
    this.preserveCameraPosition = {
      x: this.worldCamera.worldView.centerX,
      y: this.worldCamera.worldView.centerY,
      zoom: this.worldCamera.zoom
    };

    this.scene.restart();
  }

  updateCamera(delta) { /* camera handled by CameraController */ }

  updateCameraZoom() {
    const currentZoom = this.worldCamera?.zoom ?? 1;
    const target = (typeof this.cameraZoomTarget === 'number') ? this.cameraZoomTarget : currentZoom;
    // If we're effectively at the target, stop manual zooming
    if (Math.abs(currentZoom - target) < 0.0005) {
      this.manualZoomActive = false;
      return;
    }

    // Lerp toward the target with a factor (not additive per-frame)
    const lerpFactor = (typeof this.cameraZoomSpeed === 'number' && this.cameraZoomSpeed > 0 && this.cameraZoomSpeed < 1)
      ? this.cameraZoomSpeed
      : 0.05;

    const newZoom = Phaser.Math.Linear(currentZoom, target, lerpFactor);
    this.worldCamera.setZoom(newZoom);
    // Do NOT recentre or preRender here; updateCamera handles bounds and centering.
  }

  updateCameraPan() {
    // Camera following disabled - keep camera centered on battlefield
    // The camera will stay fixed at the center of the world (1024, 768)
    return;
  }

  // One-time cinematic camera tour around the battlefield
  runBattlefieldTourOnce(delayMs = 10000) {
    if (!this.worldCamera) return;
    if (this._tourScheduled) return; // avoid double-schedule
    this._tourScheduled = true;

    this.time.delayedCall(delayMs, () => {
      try {
        // Capture current camera state
        const start = {
          x: this.worldCamera.worldView.centerX,
          y: this.worldCamera.worldView.centerY,
          zoom: this.worldCamera.zoom
        };

        // Compute circle path around battlefield center
        const worldW = (this.currentBattlefield?.size === 'large' || this.currentBattlefield?.size === 'largew') ? 4096 : 2048;
        const worldH = (this.currentBattlefield?.size === 'large' || this.currentBattlefield?.size === 'largew') ? 3072 : 1536;
        const cx = worldW / 2;
        const cy = worldH / 2;
        const radius = Math.min(worldW, worldH) * 0.25; // quarter size radius

        // Build a few keyframes around the circle (0, 90, 180, 270, 360)
        const keyframes = [0, Math.PI/2, Math.PI, 3*Math.PI/2, 2*Math.PI].map(theta => ({
          x: cx + Math.cos(theta) * radius,
          y: cy + Math.sin(theta) * radius
        }));

        // Helper to tween to a point
        const panDuration = 1200;
        const zoomDuringTour = 0.75 * start.zoom; // requested 0.75 relative zoom
        this.worldCamera.zoomTo(zoomDuringTour, 600);

        let i = 0;
        const panNext = () => {
          if (i >= keyframes.length) {
            // Restore original camera
            this.worldCamera.zoomTo(start.zoom, 600);
            this.worldCamera.pan(start.x, start.y, 800, 'Sine.easeInOut');
            return;
          }
          const kf = keyframes[i++];
          this.worldCamera.pan(kf.x, kf.y, panDuration, 'Sine.easeInOut', true, (cam, progress) => {
            // onUpdate noop
          });
          // Schedule next segment slightly after this pan
          this.time.delayedCall(panDuration + 50, panNext);
        };

        // Start panning sequence
        panNext();
      } catch (_) {}
    });
  }

  registerPredictionVote(playerId, weight) {
    if (this.intermissionManager) this.intermissionManager.registerPredictionVote(playerId, weight);
  }

  simulatePredictionStream() {
    if (this.intermissionManager) this.intermissionManager.simulatePredictionStream();
  }

  createUltimateParticles(type, x, y) {
    if (!this.add) return;
    const textureKey = 'star_particle';
    const posX = x ?? 0;
    const posY = y ?? 0;
    const hasTexture = this.textures?.exists?.(textureKey);

    if (hasTexture) {
      try {
        const burstCount = type === 'damage_burst' ? 40 : 80;
        const lifespan = type === 'damage_burst' ? 600 : 900;
        const speedMax = type === 'damage_burst' ? 280 : 220;
        
        // Create particle emitter directly (Phaser 3 API)
        const emitter = this.add.particles(posX, posY, textureKey, {
          speed: { min: 120, max: speedMax },
          angle: { min: 0, max: 360 },
          scale: { start: 0.35, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan,
          gravityY: 0,
          blendMode: 'ADD'
        });
        
        emitter.setDepth(5000);
        emitter.explode(burstCount, posX, posY);
        if (this.uiCamera && this.uiCamera.ignore) {
          this.uiCamera.ignore(emitter);
        }
        this.time?.delayedCall(lifespan + 50, () => {
          try { emitter.destroy(); } catch (_) {}
        });
        return;
      } catch (error) {
        console.warn('[Game] Ultimate particles fallback (manager error):', error);
      }
    }

    // Fallback emoji effect
    const emoji = type === 'damage_burst' ? '⚔️' : '💥';
    const size = type === 'damage_burst' ? 80 : 120;
    const text = this.add.text(posX, posY, emoji, {
      fontFamily: 'Arial',
      fontSize: `${size}px`,
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 6
    }).setOrigin(0.5);
    text.setDepth(5000);
    if (this.uiCamera && this.uiCamera.ignore) {
      this.uiCamera.ignore(text);
    }
    this.tweens.add({
      targets: text,
      scale: { from: 0.8, to: 1.4 },
      alpha: { from: 1, to: 0 },
      duration: type === 'damage_burst' ? 600 : 900,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        try { text.destroy(); } catch (_) {}
      }
    });
  }

  createAttackEffect(attacker, target) {
    // Store original position and scale
    const originalY = attacker.sprite.y;

    // Make the attacking player bounce vertically and scale
    const bounceAmount = Phaser.Math.Between(3, 10);
    const duration = Phaser.Math.Between(75, 100);
    this.tweens.add({
      targets: attacker.sprite,
      y: originalY - bounceAmount, // Bounce up 20 pixels
      //scaleX: originalScaleX * 1.2,
      //scaleY: originalScaleY * 1.2,
      duration: duration,
      ease: 'Power2',
      yoyo: true,
      onComplete: () => {
        // Reset to original position and scale
        attacker.sprite.setPosition(attacker.sprite.x, originalY);
        //attacker.sprite.setScale(originalScaleX, originalScaleY);
      }
    });
  }

  setupFocusHandling() {
    // Prevent music from pausing when window loses focus
    this.focusHandler = () => {
      if (this.battleMusic && this.battleMusic.isPaused) {
        console.log('🎵 Resuming battle music after window focus');
        this.battleMusic.resume();
      }
    };

    this.blurHandler = () => {
      // Don't pause music on blur - let it continue playing
      //console.log('🎵 Window lost focus, but keeping battle music playing');
    };

    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        //console.log('🎵 Tab hidden, but keeping battle music playing');
      } else {
        //console.log('🎵 Tab visible again, ensuring battle music is playing');
        if (this.battleMusic && this.battleMusic.isPaused) {
          this.battleMusic.resume();
        }
      }
    };

    // Add event listeners
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('blur', this.blurHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  createDebugButton() {
    // Create debug button in lower-left corner
    const buttonX = 100;
    const buttonY = this.uiCamera.height - 50;
    
    // Button background
    const buttonBg = this.add.rectangle(buttonX, buttonY, 40, 40, 0xFF4444);
    buttonBg.setStrokeStyle(2, 0xFFFFFF);
    buttonBg.setDepth(10001);
    
    // Button text
    const buttonText = this.add.text(buttonX, buttonY, 'EB', {
      fontSize: '14px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(10002);
    
    // Make button interactive
    buttonBg.setInteractive({ useHandCursor: true });
    buttonBg.on('pointerdown', () => {
      console.log('🔧 DEBUG: Triggering quick battle end for testing');
      this.triggerQuickBattleEnd();
    });
    
    // Hover effects
    buttonBg.on('pointerover', () => {
      buttonBg.setFillStyle(0xFF6666);
    });
    
    buttonBg.on('pointerout', () => {
      buttonBg.setFillStyle(0xFF4444);
    });
    
    // Ensure button is on UI camera (not world camera)
    this.worldCamera.ignore(buttonBg);
    this.worldCamera.ignore(buttonText);
    
    // Store references for cleanup
    this.debugButton = { bg: buttonBg, text: buttonText };
  }

  triggerQuickBattleEnd() {
    console.log('🔧 DEBUG: Simulating battle end with mock data');
    
    // Get actual players from GameScene
    const players = this.players || [];
    if (players.length === 0) {
      console.warn('🔧 DEBUG: No players found in GameScene');
      return;
    }

    console.log(`players: ${players}`);
    console.log(players);
    
    // Select winner (first player) and eliminate all others
    const winner = players[0];
    const eliminatedPlayers = players.slice(1);
    
    console.log('🔧 DEBUG: Winner:', winner.name, 'Eliminated:', eliminatedPlayers.map(p => p.name));
    
    // Actually kill all eliminated players
    eliminatedPlayers.forEach(player => {
      if (player && player.isAlive) {
        console.log('🔧 DEBUG: Killing player:', player.name);
        player.die(); // This will mark them as dead and stop their AI
      }
    });
    
    // Stop all player AI and combat
    players.forEach(player => {
      if (player && player.isAlive) {
        // Stop player movement by clearing target
        player.targetX = player.sprite.x;
        player.targetY = player.sprite.y;
        
        // Stop AI movement
        if (player.aiController) {
          player.aiController.stop();
        }
        
        // Stop any ongoing attacks or abilities
        if (player.attackCooldown) {
          player.attackCooldown = 0;
        }
        
        // Stop any ongoing battles
        if (player.isBattling) {
          player.isBattling = false;
        }
      }
    });
    
    // Stop all game systems
    if (this.fogManager) {
      this.fogManager.stop();
    }
    
    // Stop battle music if playing
    if (this.battleMusic && this.battleMusic.isPlaying) {
      this.battleMusic.stop();
    }
    
    // Stop SFX by setting volume to 0 (SfxManager doesn't have stopAll)
    if (this.sfxManager) {
      this.sfxManager.baseVolume = 0;
    }
    
    // Stop spell manager by clearing active spells (SpellManager doesn't have stopAll)
    if (this.spellManager) {
      this.spellManager.spells = {};
      this.spellManager.spellSounds = [];
    }
    
    // Stop taunt manager by clearing active taunts (TauntManager doesn't have stopAll)
    if (this.tauntManager) {
      // TauntManager doesn't have a stopAll method, but we can clear its state
      console.log('🔧 DEBUG: TauntManager cleared');
    }
    
    // Stop cloud manager (CloudManager has destroy method, not stop)
    if (this.cloudManager) {
      this.cloudManager.destroy();
    }
    
    // GameManager doesn't have a stop method, just log
    if (this.gameManager) {
      console.log('🔧 DEBUG: GameManager cleared');
    }
    
    // Set game state to ended
    this.gameState = 'ended';
    
    // Store mock battle results in BattleStateManager
    BattleStateManager.setWinners([winner]);
    BattleStateManager.setBattleResults({
      playerRankings: players.map((player, index) => ({
        player,
        originalIndex: index,
        isWinner: player === winner,
        eliminations: player === winner ? eliminatedPlayers.length : 0,
        survived: player === winner,
        finalRank: player === winner ? 1 : index + 1
      })),
      battleDuration: 45, // 45 seconds
      totalPlayers: players.length,
      winner: winner
    });
    
    // Calculate total eliminations for debug
    const totalEliminations = eliminatedPlayers.length;
    
    // Create battle results object for debug
    const battleResults = {
      playerRankings: players.map((player, index) => ({
        player,
        originalIndex: index,
        isWinner: player === winner,
        eliminations: player === winner ? eliminatedPlayers.length : 0,
        survived: player === winner,
        finalRank: player === winner ? 1 : index + 1
      })),
      duration: 45,
      battleDuration: 45, // Keep both for compatibility
      totalEliminations,
      totalPlayers: players.length,
      gameMode: 'Battle Royale'
    };
    
    // Store battle results in BattleStateManager
    BattleStateManager.setBattleResults(battleResults);
    
    // Transition directly to VictoryScene
    console.log('🔧 DEBUG: Transitioning to VictoryScene');
    this.scene.launch('VictoryScene', {
      winners: [winner],
      battleResults
    });
  }

  shutdown() {
    try { this.releaseActionFocus(); } catch (_) {}

    // Clean up focus event listeners
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    
    // Keep battlefield background for VictoryScene - don't destroy it here
    // It will be cleaned up when transitioning to RewardsScene
    console.log('🎨 GameScene: Keeping battlefield background for VictoryScene');
    
    // Clean up fog manager
    if (this.fogManager) {
      console.log('🌫️ GameScene: Stopping fog manager on shutdown');
      this.fogManager.stop();
      this.fogManager = null;
    }
    
    // Clean up battle music
    if (this.battleMusic) {
      console.log('🎵 GameScene: Stopping battle music on shutdown');
      if (this.battleMusic.isPlaying) this.battleMusic.stop();
      this.battleMusic.destroy();
      this.battleMusic = null;
    }
    
    // Clean up music UI elements
    if (this.battleMusicTitleText) {
      this.battleMusicTitleText.destroy();
      this.battleMusicTitleText = null;
    }
    if (this.battleMusicBg) {
      this.battleMusicBg.destroy();
      this.battleMusicBg = null;
    }
    
    // Clean up players
    if (this.players && this.players.length > 0) {
      console.log('👥 GameScene: Cleaning up players');
      this.players.forEach(player => {
        if (player && player.destroy) {
          player.destroy();
        }
      });
      this.players = [];
    }
    
    // Clean up overlay elements
    if (this.overlayElements && this.overlayElements.length > 0) {
      console.log('🎨 GameScene: Cleaning up overlay elements');
      this.overlayElements.forEach(element => {
        if (element && element.destroy) {
          element.destroy();
        }
      });
      this.overlayElements = [];
    }
    
    // Clean up leaderboard overlay
    if (this.leaderboardOverlay) {
      console.log('📊 GameScene: Cleaning up leaderboard overlay');
      this.leaderboardOverlay.destroy();
      this.leaderboardOverlay = null;
    }
    
    // Clean up join grid objects
    if (this.joinGridObjects && this.joinGridObjects.length > 0) {
      console.log('🎯 GameScene: Cleaning up join grid objects');
      this.joinGridObjects.forEach(obj => {
        if (obj && obj.destroy) {
          obj.destroy();
        }
      });
      this.joinGridObjects = [];
    }
    
    // Clean up debug button
    if (this.debugButton) {
      console.log('🔧 GameScene: Cleaning up debug button');
      if (this.debugButton.bg && this.debugButton.bg.destroy) {
        this.debugButton.bg.destroy();
      }
      if (this.debugButton.text && this.debugButton.text.destroy) {
        this.debugButton.text.destroy();
      }
      this.debugButton = null;
    }
    
    // Clean up camera controller
    if (this.cameraController) {
      console.log('📷 GameScene: Cleaning up camera controller');
      this.cameraController = null;
    }
    
    // Reset camera references to prevent corruption
    if (this.worldCamera) {
      console.log('📷 GameScene: Resetting world camera reference');
      this.worldCamera = null;
    }
    if (this.uiCamera) {
      console.log('📷 GameScene: Resetting UI camera reference');
      this.uiCamera = null;
    }
    
    console.log('🎮 GameScene shutdown complete');
  }

  setupHeroImageUpdateListener() {
    // Listen for hero image updates via socket (using existing socketService)
    socketService.onHeroImageUpdated((data) => {
      if (!data || !data.heroId || !data.imageUrl) {
        console.warn('[GameScene] Invalid hero-image-updated event data:', data);
        return;
      }

      console.log('[GameScene] Received hero-image-updated event:', data);
      
      // Find the player by heroId
      const player = this.players?.find(p => p && p.hero && p.hero.id === data.heroId);
      if (!player || !player.sprite) {
        console.warn('[GameScene] Player not found for heroId:', data.heroId);
        return;
      }

      // Load the new image and update the player's sprite
      const textureKey = `player_${data.heroId}`;
      
      // Check if texture already exists and remove it to reload
      if (this.textures.exists(textureKey)) {
        this.textures.remove(textureKey);
      }

      // Load the new image
      this.load.image(textureKey, data.imageUrl);
      
      // Wait for the image to load, then update the sprite
      this.load.once(`filecomplete-image-${textureKey}`, () => {
        try {
          if (this.textures.exists(textureKey) && player.sprite) {
            player.sprite.setTexture(textureKey);
            player.imageUrl = data.imageUrl; // Update player's imageUrl reference
            console.log('[GameScene] Updated player sprite texture:', {
              heroId: data.heroId,
              textureKey,
              playerName: player.name
            });
          }
        } catch (error) {
          console.error('[GameScene] Error updating player sprite texture:', error);
        }
      });

      this.load.start();
    });
  }
}

const Game = () => {
  const gameRef = useRef(null);
  const phaserGameRef = useRef(null);
  const [themes, setThemes] = useState([]);
  const [selectedThemeIdState, setSelectedThemeIdState] = useState('');
  const [themeLoaded, setThemeLoaded] = useState(false);
  const { error, warning } = useToast();
  
  // Entitlements check
  const { hasEntitlement, refresh: refreshEntitlements } = useEntitlements();
  const hasGameAccess = hasEntitlement('game_access');
  
  // JWT Auth
  const { token, user, login: jwtLogin } = useJWTAuth();
  
  // Password login state
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Expose game access to Phaser scenes via window
  useEffect(() => {
    try {
      window.__SW_HAS_GAME_ACCESS__ = hasGameAccess;
    } catch (_) {}
  }, [hasGameAccess]);

  // Expose JWT token to Phaser scenes via window
  useEffect(() => {
    try {
      window.__SW_JWT_TOKEN__ = token;
    } catch (_) {}
  }, [token]);

  // Handle password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    
    try {
      const result = await jwtLogin(null, loginUsername, loginPassword);
      if (result.success) {
        setShowPasswordLogin(false);
        setLoginUsername('');
        setLoginPassword('');
        // Entitlements will be fetched automatically via the updated EntitlementsContext
        // Force a refresh of entitlements
        if (refreshEntitlements) {
          await refreshEntitlements();
        }
      } else {
        setLoginError(result.error || 'Login failed');
      }
    } catch (err) {
      setLoginError('An error occurred during login');
      console.error('Login error:', err);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Connect to socket when game page loads (for chat commands and potion consumption)
  useEffect(() => {
    if (token && !socketService.isSocketConnected()) {
      console.log('🔌 [Game] Connecting to socket server for chat commands and real-time updates...');
      // Check if there's an existing session ID
      const existingSessionId = window.__SW_SESSION_ID__ || BattleStateManager.getSessionId();
      socketService.connect(token, existingSessionId || null);
      
      // Initialize SessionStateManager
      sessionStateManager.init(socketService);
      
      // Set up session state change listener
      socketService.onSessionStateChanged((data) => {
        console.log('[Game] Session state changed:', data);
      });
    }
    
    return () => {
      // Don't disconnect socket when leaving game page - let it stay connected
      // Socket will be disconnected when user logs out
    };
  }, [token]);

  const [loadingTheme, setLoadingTheme] = useState(true);
  const [themeSwitchMessage, setThemeSwitchMessage] = useState('');
  const [chatCollapsed, setChatCollapsed] = useState(true);
  const [restreamCollapsed, setRestreamCollapsed] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [roomId, setRoomId] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatStatus, setChatStatus] = useState('disconnected');
  const [selectedGameMode] = useState('free-for-all');
  const [teamCount] = useState(2);
  const chatEsRef = useRef(null);
  const sessionChatEsRef = useRef(null);
  const sessionChatRoomIdRef = useRef(null);
  const [countdownPaused] = useState(false);
  

  
  // Expose countdown pause state to Phaser scenes
  useEffect(() => {
    try {
      window.__SW_COUNTDOWN_PAUSED__ = countdownPaused;
    } catch (_) {}
  }, [countdownPaused]);

 
  
  
  // Account linking moved to home page

  // Listen for wallet changes globally
  useEffect(() => {
    const handleWalletChange = () => {
      console.log('🔄 Global wallet change detected');
      // Force re-render to update account section
      // This will trigger the account modal to refresh when opened
    };

    // Listen for wallet changes across all chains
    if (window.phantom?.solana) {
      window.phantom.solana.on('accountChanged', handleWalletChange);
    }
    if (window.phantom?.ethereum) {
      window.phantom.ethereum.on('accountsChanged', handleWalletChange);
    }
    if (window.phantom?.sui) {
      window.phantom.sui.on('accountChanged', handleWalletChange);
    }

    return () => {
      // Cleanup listeners
      if (window.phantom?.solana) {
        window.phantom.solana.removeListener('accountChanged', handleWalletChange);
      }
      if (window.phantom?.ethereum) {
        window.phantom.ethereum.removeListener('accountsChanged', handleWalletChange);
      }
      if (window.phantom?.sui) {
        window.phantom.sui.removeListener('accountChanged', handleWalletChange);
      }
    };
  }, []);

  // Add CSS animation for pulsing effect
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Load themes on component mount
  useEffect(() => {
    const loadThemes = async () => {
      try {
        const response = await fetch('/api/themes', {
          headers: {
            'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
          },
        });
        if (response.ok) {
          const themeList = await response.json();
          setThemes(themeList);

          // Check localStorage for saved theme selection
          const savedTheme = localStorage.getItem('sw_selected_theme');
          if (savedTheme && themeList.some(t => t.id === savedTheme)) {
            console.log('🎨 Loading saved theme from localStorage:', savedTheme);
            setSelectedThemeIdState(savedTheme);
            setSelectedThemeId(savedTheme);
          } else {
            // Default to SoulWars theme
            console.log('🎨 No saved theme, using default');
            setSelectedThemeIdState('soulwars-default');
            setSelectedThemeId('soulwars-default');
          }
        }
      } catch (error) {
        console.error('Failed to load themes:', error);
        // Still try to load default theme
        setSelectedThemeIdState('soulwars-default');
        setSelectedThemeId('soulwars-default');
      }
    };

    loadThemes();
  }, []);

  // Load the selected theme when it changes
  useEffect(() => {
    const loadSelectedTheme = async () => {
      if (!selectedThemeIdState) return;

      setLoadingTheme(true);
      try {
        console.log(`🎮 Game: Loading theme ${selectedThemeIdState}`);
        await loadTheme(selectedThemeIdState);
        setThemeLoaded(true);
        console.log(`✅ Game: Theme loaded successfully`);
      } catch (error) {
        console.error('❌ Game: Failed to load theme:', error);
        // Try fallback to default theme
        if (selectedThemeIdState !== 'soulwars-default') {
          console.log('🔄 Game: Trying fallback to default theme...');
          try {
            await loadTheme('soulwars-default');
            setThemeLoaded(true);
          } catch (fallbackError) {
            console.error('❌ Game: Fallback theme failed too:', fallbackError);
          }
        }
      } finally {
        setLoadingTheme(false);
      }
    };

    loadSelectedTheme();
  }, [selectedThemeIdState]);

  // Handle theme selection
  const handleThemeChange = async (themeId) => {
    console.log(`🎨 Switching to theme: ${themeId}`);

    // Show user feedback immediately
    const themeName = themes.find(t => t.id === themeId)?.name || themeId;
    setThemeSwitchMessage(`🎨 Switching to ${themeName}... (Restarting game)`);

    // Clean up current theme resources and destroy current game
    try {
      // Clear cached data from old theme
      clearThemeCache();

      // Destroy current Phaser game if it exists
      if (phaserGameRef.current) {
        console.log('💥 Destroying current Phaser game for theme switch');
        phaserGameRef.current.destroy(true);
        phaserGameRef.current = null;
      }

      console.log('🧹 Old theme resources cleaned up');
    } catch (error) {
      console.warn('Error during theme cleanup:', error);
    }

    // Update theme selection
    setSelectedThemeIdState(themeId);
    setSelectedThemeId(themeId);
    setThemeLoaded(false); // Will trigger new game creation

    // Save theme selection to localStorage instead of URL
    if (themeId) {
      localStorage.setItem('sw_selected_theme', themeId);
      console.log('🎨 Saved theme to localStorage:', themeId);
    } else {
      localStorage.removeItem('sw_selected_theme');
      console.log('🎨 Cleared saved theme from localStorage');
    }

    console.log(`✅ Theme switch initiated to: ${themeId}`);

  };

 

  const connectChat = async () => {
    try {
      const roomIdTrimmed = (roomId || '').trim();
      if (!roomIdTrimmed) {
        warning('Please enter a room ID (token mint address)');
        return;
      }

      setChatStatus('connecting');

      const resp = await fetch('/api/chat/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: roomIdTrimmed, username: 'SoulSeedBot', messageHistoryLimit: 0 })
      });
      if (!resp.ok) throw new Error('Failed to configure chat');
      if (chatEsRef.current) {
        try { chatEsRef.current.close(); } catch (e) {}
        chatEsRef.current = null;
      }
      const es = new EventSource(`/api/chat/stream?roomId=${encodeURIComponent(roomIdTrimmed)}`);
      try { window.currentRoomId = roomIdTrimmed; } catch (_) {}
      
      es.addEventListener('open', () => {
        setChatStatus('connected');
      });

      es.addEventListener('error', () => {
        setChatStatus('disconnected');
      });

      es.addEventListener('hello', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.ok) {
            setChatStatus('connected');
          }
        } catch (e) {}
      });

      es.addEventListener('status', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.status === 'connected') {
            setChatStatus('connected');
          } else if (data.status === 'error') {
            setChatStatus('error');
          }
        } catch (e) {}
      });

      // Get userId from JWT auth context (if authenticated)
      let myUserId = user?.id || null;
      let myWalletAddress = null;
      
      // Track userIds that have heroes in the current session
      const sessionUserIds = new Set();
      const updateSessionUserIds = () => {
        try {
          const sessionId = window.__SW_SESSION_ID__ || BattleStateManager?.getSessionId?.();
          if (!sessionId) return;
          
          // Get players from the game scene
          const gameScene = window.phaserGameRef?.current?.scene?.getScene('GameScene');
          if (gameScene && gameScene.players) {
            sessionUserIds.clear();
            gameScene.players.forEach(player => {
              if (player.userId) {
                sessionUserIds.add(player.userId);
              }
            });
          }
        } catch (e) {
          console.warn('[Chat] Error updating session userIds:', e);
        }
      };
      
      // Also track userId/wallet from messages we send (for pumpfun users without JWT)
      es.addEventListener('message', (ev) => {
        try {
          const m = JSON.parse(ev.data);
          
          // Track our own userId/wallet from messages we send
          // (messages from us will have userId/userAddress set)
          if (m.userId && !myUserId) {
            myUserId = m.userId;
          }
          if ((m.userAddress || m.walletAddress) && !myWalletAddress) {
            myWalletAddress = m.userAddress || m.walletAddress;
          }
          
          // Update session userIds periodically
          updateSessionUserIds();
          
          // Filter private messages: only process if targetUserId matches our userId
          // or if targetUserId is in the session, or if targetWallet matches our wallet
          if (m.targetUserId !== undefined) {
            // This is a private message
            const matchesUserId = myUserId && m.targetUserId === myUserId;
            const matchesSession = sessionUserIds.has(m.targetUserId);
            const matchesWallet = myWalletAddress && (m.userAddress || m.walletAddress) && 
              (m.userAddress || m.walletAddress).toLowerCase() === myWalletAddress.toLowerCase();
            
            if (!matchesUserId && !matchesSession && !matchesWallet) {
              // Not for us and not for anyone in the session, skip it
              console.log('[Chat] Filtered private message (not for us or session):', {
                targetUserId: m.targetUserId,
                myUserId,
                inSession: sessionUserIds.has(m.targetUserId),
                targetWallet: (m.userAddress || m.walletAddress)?.slice(0, 8),
                myWallet: myWalletAddress?.slice(0, 8)
              });
              return;
            }
          }
          
          setChatMessages(prev => [...prev, m].slice(-200));
          try { window.dispatchEvent(new CustomEvent('soulwars-chat', { detail: m })); } catch (_) {}
        } catch (e) {}
      });
      es.addEventListener('voteStandings', (ev) => {
        try {
          const data = JSON.parse(ev.data);
          try {
            const sid = (typeof VoteManager?.getSessionId === 'function') ? VoteManager.getSessionId() : null;
            if (!sid || data.sessionId === sid) {
              if (typeof VoteManager?.updateStandingsFromSSE === 'function') {
                VoteManager.updateStandingsFromSSE(data.standings);
              }
              try { window.dispatchEvent(new CustomEvent('vote-standings-update', { detail: data.standings })); } catch (_) {}
            }
          } catch (_) {}
        } catch (e) {
          console.error('Error handling voteStandings:', e);
        }
      });
      //disable the message history as we don't need it right now, but may later
      /*es.addEventListener('messageHistory', (ev) => {
        try {
          const hist = JSON.parse(ev.data);
          setChatMessages(prev => [...prev, ...hist].slice(-200));
          try { hist.forEach(m => window.dispatchEvent(new CustomEvent('soulwars-chat', { detail: m }))); } catch (_) {}
        } catch (e) {}
      });*/
      chatEsRef.current = es;
    } catch (e) {
      console.error(e);
      setChatStatus('error');
      error('Failed to connect PumpFun chat');
    }
  };

  const disconnectChat = async () => {
    try {
      if (chatEsRef.current) { try { chatEsRef.current.close(); } catch (e) {} chatEsRef.current = null; }
      setChatStatus('disconnected');
    } catch (e) {}
  };

  // --- Restream Chat (operator) ---
  const [restreamStatus, setRestreamStatus] = useState('disconnected');
  const [restreamMessages, setRestreamMessages] = useState([]);
  const restreamEsRef = useRef(null);
  const { getAuthHeaders } = useJWTAuth();

  const startRestreamOAuth = async () => {
    try {
      const r = await fetch('/api/restream/auth/start', { 
        credentials: 'include',
        headers: {
          ...((typeof getAuthHeaders === 'function') ? getAuthHeaders() : {}),
          'x-api-key': (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SOULWARS_API_KEY) ? process.env.REACT_APP_SOULWARS_API_KEY : 'dev-key'
        }
      });
      const j = await r.json();
      if (j?.url) window.open(j.url, '_blank');
    } catch (_) {}
  };

  const connectRestream = async () => {
    try {
      setRestreamStatus('connecting');
      await fetch('/api/restream/connect', { 
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json',
          ...((typeof getAuthHeaders === 'function') ? getAuthHeaders() : {}),
          'x-api-key': (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SOULWARS_API_KEY) ? process.env.REACT_APP_SOULWARS_API_KEY : 'dev-key'
        }, 
        credentials: 'include' 
      });
      if (restreamEsRef.current) { try { restreamEsRef.current.close(); } catch (_) {} restreamEsRef.current = null; }
      const qsToken = token ? `?token=${encodeURIComponent(token)}` : '';
      const es = new EventSource(`/api/restream/stream${qsToken}`);
      es.addEventListener('status', (ev) => {
        try { const d = JSON.parse(ev.data); setRestreamStatus(d.status || 'connected'); } catch (_) {}
      });
      es.addEventListener('message', (ev) => {
        try {
          const m = JSON.parse(ev.data);
          setRestreamMessages(prev => [...prev, m].slice(-200));
          try { window.dispatchEvent(new CustomEvent('soulwars-chat', { detail: m })); } catch (_) {}
        } catch (_) {}
      });
      es.onerror = () => setRestreamStatus('error');
      restreamEsRef.current = es;
    } catch (_) { setRestreamStatus('error'); }
  };

  const disconnectRestream = async () => {
    try {
      if (restreamEsRef.current) { try { restreamEsRef.current.close(); } catch (_) {} restreamEsRef.current = null; }
      await fetch('/api/restream/disconnect', { 
        method: 'POST', 
        credentials: 'include',
        headers: {
          ...((typeof getAuthHeaders === 'function') ? getAuthHeaders() : {}),
          'x-api-key': (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SOULWARS_API_KEY) ? process.env.REACT_APP_SOULWARS_API_KEY : 'dev-key'
        }
      });
      setRestreamStatus('disconnected');
    } catch (_) {}
  };

  // Create Phaser game only after theme is loaded
  useEffect(() => {
    if (!gameRef.current || !themeLoaded || loadingTheme || phaserGameRef.current) return;

    console.log('🎯 Game: Creating Phaser game with loaded theme');

    const config = {
      type: Phaser.AUTO,
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      // Configure loader to use crossOrigin for S3 assets
      loader: {
        crossOrigin: 'anonymous'
      },
      // Pass game mode and team count to scene (initial defaults)
      gameMode: selectedGameMode,
      teamCount: teamCount,
      autoFocus: false,
      audio: {
        noAudioFocus: true // Prevents audio from pausing when tab loses focus
      },
      scale: {
        mode: Phaser.Scale.RESIZE,
        parent: gameRef.current,
        width: 1440,
        height: 900,
        autoCenter: Phaser.Scale.CENTER_BOTH
      },
      scene: [InstructionsScene, JoinScene, PreBattleScene, PreloadScene, GameScene, VictoryScene, FirstTimeScene, WrapUpScene, PredictionResultsScene, RewardsScene, BattlefieldLootScene, ItemSpotlightScene],
      backgroundColor: '#000000'
    };

    const game = new Phaser.Game(config);
    phaserGameRef.current = game;
    
    // Expose phaserGameRef to window for global access
    window.phaserGameRef = phaserGameRef;

    // Add fullscreen and resize event listeners for proper resizing
    const handleFullscreenChange = () => {
      if (phaserGameRef.current && phaserGameRef.current.scale) {
        // Small delay to ensure fullscreen transition is complete
        setTimeout(() => {
          phaserGameRef.current.scale.refresh();
          
          // Call resize method on current scene if it exists
          const activeScenes = phaserGameRef.current.scene.getScenes(true);
          if (activeScenes.length > 0) {
            const currentScene = activeScenes[0];
            if (currentScene && typeof currentScene.resize === 'function') {
              currentScene.resize();
            }
          }
        }, 100);
      }
    };

    const handleWindowResize = () => {
      if (phaserGameRef.current && phaserGameRef.current.scale) {
        // Debounce resize events
        clearTimeout(phaserGameRef.current._resizeTimeout);
        phaserGameRef.current._resizeTimeout = setTimeout(() => {
          phaserGameRef.current.scale.refresh();
          
          // Call resize method on current scene if it exists
          const activeScenes = phaserGameRef.current.scene.getScenes(true);
          if (activeScenes.length > 0) {
            const currentScene = activeScenes[0];
            if (currentScene && typeof currentScene.resize === 'function') {
              currentScene.resize();
            }
          }
        }, 150);
      }
    };

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Listen for window resize
    window.addEventListener('resize', handleWindowResize);

    // Store cleanup function
    const cleanupFullscreenListeners = () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener('resize', handleWindowResize);
      
      // Clear any pending resize timeout
      if (phaserGameRef.current && phaserGameRef.current._resizeTimeout) {
        clearTimeout(phaserGameRef.current._resizeTimeout);
      }
    };

    // Clear theme switch message when new game is created
    setThemeSwitchMessage('');

    return () => {
      // Clean up fullscreen event listeners
      cleanupFullscreenListeners();
      
      if (phaserGameRef.current) {
        phaserGameRef.current.destroy(true);
        phaserGameRef.current = null;
      }
      
      // Clean up window reference
      if (window.phaserGameRef === phaserGameRef) {
        window.phaserGameRef = null;
      }
    };
  }, [themeLoaded, loadingTheme]);

  // Keep Phaser config in sync without recreating the game
  useEffect(() => {
    if (phaserGameRef.current) {
      try {
        phaserGameRef.current.config.gameMode = selectedGameMode;
        // Also sync into active scene tree if already created
        const scene = phaserGameRef.current.scene?.getScene('InstructionsScene');
        if (scene) {
          // no-op; instructions scene will read from game.config when starting
        }
      } catch (_) {}
    }
  }, [selectedGameMode]);

  useEffect(() => {
    if (phaserGameRef.current) {
      try {
        phaserGameRef.current.config.teamCount = teamCount;
      } catch (_) {}
    }
  }, [teamCount]);

  // Resize game canvas when sidebar is toggled
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scale) {
      const newWidth = sidebarCollapsed ? 1800 : 1440;
      phaserGameRef.current.scale.resize(newWidth, 900);
      console.log(`🎯 Game: Resized canvas to ${newWidth}x900`);
    }
  }, [sidebarCollapsed]);

  // Handle session creation: connect to local session chat SSE without affecting pumpfun UI
  useEffect(() => {
    const connectSessionChat = async (sessionId) => {
      if (!sessionId) return;

      // Avoid reconnecting if already connected to this session
      if (sessionChatRoomIdRef.current === sessionId && sessionChatEsRef.current) {
        return;
      }

      // Close any existing session chat connection
      if (sessionChatEsRef.current) {
        try { sessionChatEsRef.current.close(); } catch (_) {}
        sessionChatEsRef.current = null;
      }

      sessionChatRoomIdRef.current = sessionId;

      try {
        console.log('[Game] Connecting to session chat stream:', sessionId);

        // For in-game session chat we bind roomId === sessionId; this also
        // sets sessionId explicitly so the relay knows which game session
        // this room belongs to (keeps the API coherent with pumpfun rooms).
        await fetch('/api/chat/config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId: sessionId,
            username: 'SoulSeedBot',
            messageHistoryLimit: 0,
            sessionId
          })
        });

        const es = new EventSource(`/api/chat/stream?roomId=${encodeURIComponent(sessionId)}`);

        // Get userId from JWT auth context (if authenticated)
        let myUserId = user?.id || null;
        let myWalletAddress = null;
        
        // Track userIds that have heroes in the current session
        const sessionUserIds = new Set();
        const updateSessionUserIds = () => {
          try {
            const gameScene = window.phaserGameRef?.current?.scene?.getScene('GameScene');
            if (gameScene && gameScene.players) {
              sessionUserIds.clear();
              gameScene.players.forEach(player => {
                if (player.userId) {
                  sessionUserIds.add(player.userId);
                }
              });
            }
          } catch (e) {
            console.warn('[Game] Error updating session userIds:', e);
          }
        };
        
        // Also track userId/wallet from messages we send (for pumpfun users without JWT)
        es.addEventListener('message', (ev) => {
          try {
            const m = JSON.parse(ev.data);
            
            // Track our own userId/wallet from messages we send
            if (m.userId && !myUserId) {
              myUserId = m.userId;
            }
            if ((m.userAddress || m.walletAddress) && !myWalletAddress) {
              myWalletAddress = m.userAddress || m.walletAddress;
            }
            
            // Update session userIds periodically
            updateSessionUserIds();
            
            // Filter private messages: only process if targetUserId matches our userId
            // or if targetUserId is in the session, or if targetWallet matches our wallet
            if (m.targetUserId !== undefined) {
              // This is a private message
              const matchesUserId = myUserId && m.targetUserId === myUserId;
              const matchesSession = sessionUserIds.has(m.targetUserId);
              const matchesWallet = myWalletAddress && (m.userAddress || m.walletAddress) && 
                (m.userAddress || m.walletAddress).toLowerCase() === myWalletAddress.toLowerCase();
              
              if (!matchesUserId && !matchesSession && !matchesWallet) {
                // Not for us and not for anyone in the session, skip it
                console.log('[Game] Filtered private message (not for us or session):', {
                  targetUserId: m.targetUserId,
                  myUserId,
                  inSession: sessionUserIds.has(m.targetUserId),
                  targetWallet: (m.userAddress || m.walletAddress)?.slice(0, 8),
                  myWallet: myWalletAddress?.slice(0, 8)
                });
                return;
              }
            }
            
            setChatMessages(prev => [...prev, m].slice(-200));
            try { window.dispatchEvent(new CustomEvent('soulwars-chat', { detail: m })); } catch (_) {}
          } catch (e) {
            console.error('[Game] Error handling session chat message:', e);
          }
        });

        es.addEventListener('voteStandings', (ev) => {
          try {
            const data = JSON.parse(ev.data);
            try {
              const sid = (typeof VoteManager?.getSessionId === 'function') ? VoteManager.getSessionId() : null;
              if (!sid || data.sessionId === sid) {
                if (typeof VoteManager?.updateStandingsFromSSE === 'function') {
                  VoteManager.updateStandingsFromSSE(data.standings);
                }
                try { window.dispatchEvent(new CustomEvent('vote-standings-update', { detail: data.standings })); } catch (_) {}
              }
            } catch (_) {}
          } catch (e) {
            console.error('[Game] Error handling session voteStandings:', e);
          }
        });

        es.onerror = (err) => {
          console.warn('[Game] Session chat stream error:', err);
        };

        sessionChatEsRef.current = es;
        console.log('[Game] Session chat stream connected:', sessionId);
      } catch (err) {
        console.error('[Game] Failed to connect session chat stream:', err);
      }
    };

    const handleSessionCreated = (event) => {
      const sessionId = event.detail?.sessionId;
      if (!sessionId) return;
      
      // Store session ID in window for persistence
      window.__SW_SESSION_ID__ = sessionId;
      
      // Reconnect socket with session ID
      if (token) {
        console.log('[Game] Reconnecting socket with session ID:', sessionId);
        socketService.disconnect();
        socketService.connect(token, sessionId);
        
        // Initialize SessionStateManager with session ID
        sessionStateManager.setSessionId(sessionId);
        sessionStateManager.updateState('join');
      }
      
      connectSessionChat(sessionId);
    };

    window.addEventListener('soulwars-session-created', handleSessionCreated);

    // Attempt to connect immediately if a session is already active
    try {
      const existingSessionId = window.__SW_SESSION_ID__ || BattleStateManager.getSessionId();
      if (existingSessionId) {
        connectSessionChat(existingSessionId);
      }
    } catch (_) {}

    return () => {
      window.removeEventListener('soulwars-session-created', handleSessionCreated);
      if (sessionChatEsRef.current) {
        try { sessionChatEsRef.current.close(); } catch (_) {}
        sessionChatEsRef.current = null;
      }
      sessionChatRoomIdRef.current = null;
    };
  }, []);

  // Calculate game width for chatbox
  const gameWidth = sidebarCollapsed ? '1800px' : '1440px';

  // If user doesn't have game access, show "Join the game!" CTA
  if (!hasGameAccess) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh', 
        backgroundImage: `url('/assets/menus/SiteBackground.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem',
        position: 'relative'
      }}>
        {/* Overlay for better text readability */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 0
        }}></div>
        
        <div style={{
          backgroundColor: '#111827',
          border: '2px solid #374151',
          borderRadius: '1rem',
          padding: '2rem',
          maxWidth: '800px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#F3F4F6',
            marginBottom: '1rem'
          }}>
            Join the Game!
          </h1>
          
          <p style={{
            fontSize: '1.25rem',
            color: '#D1D5DB',
            marginBottom: '2rem',
            lineHeight: 1.6
          }}>
            Join the live game on these platforms
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            marginTop: '2rem',
            width: '100%',
            maxWidth: '400px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            <a
              href="https://www.youtube.com/@SoulWarsGG"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#DC2626',
                color: 'white',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                minWidth: 0,
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B91C1C';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#DC2626';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/assets/platform/platform-youtube.png" 
                alt="YouTube" 
                style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>YouTube</span>
            </a>

            <a
              href="https://www.twitch.tv/soulwarsgg"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#9146FF',
                color: 'white',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                minWidth: 0,
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#772CE8';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#9146FF';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/assets/platform/platform-twitch.png" 
                alt="Twitch" 
                style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>Twitch</span>
            </a>

            <a
              href="https://x.com/SoulWarsGG"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                minWidth: 0,
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1F2937';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/assets/platform/platform-x.png" 
                alt="X" 
                style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>X</span>
            </a>

            <a
              href="https://www.tiktok.com/@soulwarsgg?lang=en"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                minWidth: 0,
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1F2937';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/assets/platform/platform-tiktok.png" 
                alt="TikTok" 
                style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>TikTok</span>
            </a>

            <a
              href="https://pump.fun/coin/HXik3MQbaoKdFEdbirN8xS5PmjyaQQz66TgZ9Ygvpump"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#1F2937',
                color: 'white',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                minWidth: 0,
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#374151';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1F2937';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/assets/platform/platform-pumpfun.png" 
                alt="PumpFun" 
                style={{ width: '20px', height: '20px', flexShrink: 0, objectFit: 'contain' }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>PumpFun</span>
            </a>

            {/* Login button for OBS/streaming */}
            {!token && (
              <button
                onClick={() => setShowPasswordLogin(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#10B981',
                  color: 'white',
                  fontWeight: 600,
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)',
                  minWidth: 0,
                  flex: 1,
                  marginTop: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10B981';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span>🔐 Login for Streaming (OBS)</span>
              </button>
            )}
          </div>
        </div>

        {/* Password Login Modal */}
        {showPasswordLogin && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => {
            if (!isLoggingIn) {
              setShowPasswordLogin(false);
              setLoginError('');
            }
          }}>
            <div style={{
              backgroundColor: '#111827',
              border: '2px solid #374151',
              borderRadius: '1rem',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              position: 'relative',
              zIndex: 1001
            }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#F3F4F6',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Login for Streaming
              </h2>
              
              <p style={{
                fontSize: '0.875rem',
                color: '#9CA3AF',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                Use your username and password to access the game for OBS streaming
              </p>

              <form onSubmit={handlePasswordLogin}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    color: '#D1D5DB',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.5rem'
                  }}>
                    Username
                  </label>
                  <input
                    type="text"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    disabled={isLoggingIn}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '0.5rem',
                      color: '#F3F4F6',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                    autoFocus
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    color: '#D1D5DB',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.5rem'
                  }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={isLoggingIn}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '0.5rem',
                      color: '#F3F4F6',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {loginError && (
                  <div style={{
                    backgroundColor: '#7F1D1D',
                    border: '1px solid #991B1B',
                    borderRadius: '0.5rem',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    color: '#FCA5A5',
                    fontSize: '0.875rem'
                  }}>
                    {loginError}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '0.75rem'
                }}>
                  <button
                    type="submit"
                    disabled={isLoggingIn || !loginUsername || !loginPassword}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: isLoggingIn || !loginUsername || !loginPassword ? '#374151' : '#10B981',
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: isLoggingIn || !loginUsername || !loginPassword ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isLoggingIn ? 'Logging in...' : 'Login'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordLogin(false);
                      setLoginError('');
                      setLoginUsername('');
                      setLoginPassword('');
                    }}
                    disabled={isLoggingIn}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#374151',
                      color: '#D1D5DB',
                      fontWeight: 600,
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Handle refresh button click
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh', backgroundColor: '#374151', padding: '0.5rem', position: 'relative' }}>
      
      {/* Refresh Button - Fixed position in top-right */}
      <button
        onClick={handleRefresh}
        title="Refresh page"
        style={{
          position: 'fixed',
          top: '3rem',
          right: '0.5rem',
          width: '36px',
          height: '36px',
          backgroundColor: '#1F2937',
          border: '1px solid #374151',
          borderRadius: '0rem',
          color: '#9CA3AF',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem',
          zIndex: 1000,
          transition: 'background-color 0.2s ease, color 0.2s ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#374151';
          e.currentTarget.style.color = '#F3F4F6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#1F2937';
          e.currentTarget.style.color = '#9CA3AF';
        }}
      >
        🔄
      </button>

      <div style={{ 
        display: 'flex', 
        width: '100%', 
        maxWidth: '1900px', 
        gap: sidebarCollapsed ? '0.5rem' : '1rem',
        alignItems: 'flex-start', 
        justifyContent: 'center',
        transition: 'gap 0.3s ease'
      }}>
        {/* Left sidebar */}
        <div style={{ 
          width: sidebarCollapsed ? '50px' : '350px',
          minWidth: sidebarCollapsed ? '50px' : '350px',
          color: 'white',
          transition: 'width 0.3s ease, min-width 0.3s ease',
          position: 'relative'
        }}>
          {/* Toggle button for sidebar collapse */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '40px',
              height: '40px',
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '0.375rem',
              color: '#9CA3AF',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              zIndex: 100,
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1F2937'}
          >
            {sidebarCollapsed ? '►' : '◄'}
          </button>

          {/* Sidebar content - only show when not collapsed */}
          {!sidebarCollapsed && (
            <>
              {/* Token info */}
              <div style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>SOULWARS</h2>
            {/*<div style={{ fontSize: '0.95rem', color: '#D1D5DB' }}>
              <div>Token CA:</div>
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', wordBreak: 'break-all' }}>
                HXik3MQbaoKdFEdbirN8xS5PmjyaQQz66TgZ9Ygvpump
              </div>
            </div>*/}
          </div>


          {/* Theme Selector */}
          <div style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Game Theme</h2>
            <select
              value={selectedThemeIdState}
              onChange={(e) => handleThemeChange(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: '#374151',
                border: '1px solid #6B7280',
                borderRadius: '0.375rem',
                color: 'white',
                fontSize: '0.875rem'
              }}
            >
              {themes.map(theme => (
                <option key={theme.id} value={theme.id}>
                  {theme.name}
                </option>
              ))}
            </select>
          </div>



          {/* PumpFun Chat - Only show if user has game access */}
          {hasGameAccess && (
            <div style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                   onClick={() => setChatCollapsed(!chatCollapsed)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Pumpfun Connect</h2>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.375rem',
                  backgroundColor: chatStatus === 'connected' ? '#065F46' : 
                                 chatStatus === 'connecting' ? '#92400E' : 
                                 chatStatus === 'error' ? '#7F1D1D' : '#374151',
                  color: chatStatus === 'connected' ? '#10B981' : 
                         chatStatus === 'connecting' ? '#F59E0B' : 
                         chatStatus === 'error' ? '#EF4444' : '#9CA3AF'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: chatStatus === 'connected' ? '#10B981' : 
                                   chatStatus === 'connecting' ? '#F59E0B' : 
                                   chatStatus === 'error' ? '#EF4444' : '#9CA3AF',
                    animation: chatStatus === 'connecting' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                  }}></div>
                  {chatStatus === 'connected' ? 'Connected' : 
                   chatStatus === 'connecting' ? 'Connecting...' : 
                   chatStatus === 'error' ? 'Error' : 'Disconnected'}
                </div>
              </div>
              <span style={{ color: '#9CA3AF' }}>{chatCollapsed ? '▼' : '▲'}</span>
            </div>

            {!chatCollapsed && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#D1D5DB', marginBottom: '0.25rem' }}>Room (token mint)</label>
                <input value={roomId} onChange={e => setRoomId(e.target.value)}
                  placeholder="So1111... token mint address"
                  style={{ width: '100%', padding: '0.4rem', backgroundColor: '#374151', border: '1px solid #6B7280', borderRadius: '0.375rem', color: 'white', marginBottom: '0.5rem' }} />

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button onClick={connectChat} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#10B981', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer' }}>Connect</button>
                  <button onClick={disconnectChat} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#EF4444', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer' }}>Disconnect</button>
                </div>

                <div style={{ fontSize: '0.85rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Incoming messages</div>
                <div style={{ height: '180px', overflowY: 'auto', backgroundColor: '#0B1220', border: '1px solid #374151', borderRadius: '0.375rem', padding: '0.5rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace', fontSize: '0.8rem' }}>
                  {chatMessages.slice(-200).map((m, i) => (
                    <div key={i}>
                      <span style={{ color: '#6EE7B7' }}>[{new Date(m.timestamp).toLocaleTimeString()}]</span>{' '}
                      <span style={{ color: '#93C5FD' }}>{m.username}</span>: <span style={{ color: '#E5E7EB' }}>{m.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Restream Chat (Operator) */}
          {hasGameAccess && (
            <div style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                   onClick={() => setRestreamCollapsed(!restreamCollapsed)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Restream Connect</h2>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    backgroundColor: restreamStatus === 'connected' ? '#065F46' : 
                                   restreamStatus === 'connecting' ? '#92400E' : 
                                   restreamStatus === 'error' ? '#7F1D1D' : '#374151',
                    color: restreamStatus === 'connected' ? '#10B981' : 
                           restreamStatus === 'connecting' ? '#F59E0B' : 
                           restreamStatus === 'error' ? '#EF4444' : '#9CA3AF'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: restreamStatus === 'connected' ? '#10B981' : 
                                     restreamStatus === 'connecting' ? '#F59E0B' : 
                                     restreamStatus === 'error' ? '#EF4444' : '#9CA3AF',
                      animation: restreamStatus === 'connecting' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }}></div>
                    {restreamStatus === 'connected' ? 'Connected' : 
                     restreamStatus === 'connecting' ? 'Connecting...' : 
                     restreamStatus === 'error' ? 'Error' : 'Disconnected'}
                  </div>
                </div>
                <span style={{ color: '#9CA3AF' }}>{restreamCollapsed ? '▼' : '▲'}</span>
              </div>

              {!restreamCollapsed && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button onClick={startRestreamOAuth} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#3B82F6', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer' }}>Authorize</button>
                    <button onClick={connectRestream} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#10B981', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer' }}>Connect</button>
                    <button onClick={disconnectRestream} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#EF4444', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer' }}>Disconnect</button>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>Incoming messages</div>
                  <div style={{ height: '180px', overflowY: 'auto', backgroundColor: '#0B1220', border: '1px solid #374151', borderRadius: '0.375rem', padding: '0.5rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace', fontSize: '0.8rem' }}>
                    {restreamMessages.slice(-200).map((m, i) => (
                      <div key={i}>
                        <span style={{ color: '#6EE7B7' }}>[{new Date(m.timestamp || Date.now()).toLocaleTimeString()}]</span>{' '}
                        <span style={{ color: '#93C5FD' }}>{m.username}</span> <span style={{ color: '#9CA3AF' }}>({m.platform || 'unknown'})</span>: <span style={{ color: '#E5E7EB' }}>{m.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/*<div style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>NEXT LIVE:</h2>
            <div style={{ fontSize: '0.95rem', color: '#D1D5DB' }}>
              <p style={{ lineHeight: 1.6, fontSize: '1.5rem', color: '#FFFF00' }}>
                TBD
              </p>
            </div>
          </div>*/}
            </>
          )}
          
        </div>

        {/* Theme switch notification */}
        {themeSwitchMessage && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            {themeSwitchMessage}
          </div>
        )}

        {/* Game container with conditional content */}
        <div>
          {/* Theme Loading Indicator */}
          {loadingTheme && (
            <div style={{
              width: sidebarCollapsed ? '1800px' : '1440px',
              maxWidth: sidebarCollapsed ? '1800px' : '1440px',
              height: '900px',
              border: '2px solid #6B7280',
              borderRadius: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#111827',
              color: 'white',
              fontSize: '1.25rem',
              fontWeight: '500',
              transition: 'width 0.3s ease, max-width 0.3s ease'
            }}>
              <div style={{ marginBottom: '1rem' }}>🎨</div>
              <div>Loading Theme...</div>
              <div style={{ fontSize: '0.875rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                Preparing game assets
              </div>
            </div>
          )}

          {/* Game canvas - only show when theme is loaded */}
          {themeLoaded && !loadingTheme && (
            <>
              <div 
                ref={gameRef} 
                style={{ 
                  width: sidebarCollapsed ? '1800px' : '1440px',
                  maxWidth: sidebarCollapsed ? '1800px' : '1440px',
                  height: '895px', 
                  border: '2px solid #6B7280', 
                  borderRadius: '0.5rem', 
                  overflow: 'hidden', 
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  transition: 'width 0.3s ease, max-width 0.3s ease'
                }} 
              />
              
              {/* Local Operator Chatbox */}
              <OperatorChatbox roomId={roomId} gameWidth={gameWidth} />
            </>
          )}
        </div>

      </div>
      
      {/* Account linking moved to home page */}
    </div>
  );
};

export default Game;

